import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.NODE_ENV === 'production'
  ? 'https://service.slipms.com/api'
  : 'http://localhost:8080/api';

const HA_BRANDS = ['Signia','Phonak','Widex','Coselgi','ReSound','Starkey','Oticon','Interton','Unitron'];

function buildWATrackMsg(ticket, patientName) {
  const link = `https://service.slipms.com/track/${ticket.repair_no}`;
  const estAmt = parseFloat(ticket.estimate_amount || 0);
  let estLine = '';
  if (ticket.company_report_type === 'FOC') {
    estLine = '\nEstimated Amount: *FOC (Free of Charge)*';
  } else if (ticket.company_report_type === 'Estimate' && estAmt > 0) {
    estLine = `\nEstimated Amount: *₹${estAmt.toLocaleString('en-IN')}*`;
  }
  return `Hi ${patientName}, your hearing aid repair is in progress 🎧\n\nRepair No: *${ticket.repair_no}*\nStatus: ${ticket.status}${estLine}\n\nTrack your repair anytime here:\n${link}`;
}
const COMPLAINTS = ['Dead','Low Output','Intermittent Output','Distortion or Noisy','Connectivity Issue','Bluetooth Streaming Issue','No LED Indication While Charging','Others'];
const STATUS_ORDER = ['Form Submitted','Courier Sent','Received at HO','Sent to Company','Estimate Received','Patient Informed','Patient Approved','Under Repair','Patient Rejected','Dispatched to Branch','Received at Clinic','Delivered to Patient'];
const STATUS_COLOR = {'Form Submitted':'#d97706','Courier Sent':'#2563eb','Received at HO':'#059669','Sent to Company':'#0891b2','Estimate Received':'#f97316','Patient Informed':'#7c3aed','Patient Approved':'#16a34a','Under Repair':'#0891b2','Patient Rejected':'#dc2626','Dispatched to Branch':'#2563eb','Received at Clinic':'#7c3aed','Delivered to Patient':'#16a34a'};
// Approved-path timeline (no Patient Rejected branch)
const TIMELINE_FLOW = ['Form Submitted','Courier Sent','Received at HO','Sent to Company','Estimate Received','Patient Informed','Patient Approved','Under Repair','Dispatched to Branch','Received at Clinic','Delivered to Patient'];

const QUOTES = [
  { q:"I asked my patient if they could hear me. They said 'of course!' Their family disagreed.", a:"— SoundLife" },
  { q:"Warning: After getting hearing aids, you will hear things you were perfectly happy not hearing before.", a:"— SoundLife" },
  { q:"We once had a patient who thought everyone mumbled. Turns out, they didn't.", a:"— SoundLife" },
  { q:"Hearing loss doesn't happen overnight. It happens over years of saying 'huh?' and hoping for the best.", a:"— SoundLife" },
  { q:"The world is full of beautiful sounds. Birds. Laughter. Rain. Your name being called. Don't miss them.", a:"— SoundLife" },
  { q:"They say silence is golden. Our patients disagree — they want every sound back.", a:"— SoundLife" },
  { q:"Ears don't come with a warranty. But we're pretty close.", a:"— SoundLife" },
  { q:"Some people turn up the volume. Some people get it fixed. Guess which ones sleep better.", a:"— SoundLife" },
  { q:"Lip reading is impressive. Hearing aids are easier. Just saying.", a:"— SoundLife" },
  { q:"You can hear a notification from across the room. But not your name being called. Interesting priorities.", a:"— SoundLife" },
  { q:"There are two types of people — those who fixed their hearing early and those who wish they had.", a:"— SoundLife" },
  { q:"Hearing loss is the only condition where everyone around you notices before you do.", a:"— SoundLife" },
  { q:"Beethoven composed his 9th Symphony completely deaf. Imagine what he could've done with a good audiologist.", a:"— SoundLife" },
  { q:"Life is too short to say 'What?' twice. Come on in.", a:"— SoundLife" },
];
const STATUS_ICON  = {'Form Submitted':'📋','Courier Sent':'📦','Received at HO':'🏢','Sent to Company':'🏭','Estimate Received':'💰','Patient Informed':'📞','Patient Approved':'✅','Under Repair':'🔧','Patient Rejected':'❌','Dispatched to Branch':'🚚','Received at Clinic':'📥','Delivered to Patient':'🎉'};
const SLA_DAYS = 15; // target turnaround in days

// ── Font + Global CSS ─────────────────────────────────────────
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Serif+Display&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',system-ui,sans-serif;background:var(--bg);color:var(--text1);-webkit-font-smoothing:antialiased}
:root{
  --bg:#f4f3ff;--bg2:#fff;--bg3:#ede9fe;--border:#ddd6fe;--border2:#c4b5fd;
  --text1:#1e1b4b;--text2:#4c1d95;--text3:#7c3aed;--muted:#8b7ec8;--muted2:#a89fd4;
  --accent:#7c3aed;--accent2:#6d28d9;--accentbg:#ede9fe;--accentbg2:#ddd6fe;
  --danger:#e84c3d;--dangerBg:#fee2e2;--green:#059669;--greenBg:#dcfce7;
  --amber:#d97706;--amberBg:#fef9c3;--blue:#2563eb;--blueBg:#dbeafe;
  --sidebar:rgba(255,255,255,0.82);--sideborder:rgba(196,181,253,0.45);--topbar:#fff;
  --card:#fff;--cardborder:#e9e3ff;--cardshadow:0 2px 16px #7c3aed0d;
  --rowhover:#f5f3ff;--inputbg:#faf9ff;
  --scrolltrack:#ede9fe;--scrollthumb:#c4b5fd;
}
[data-theme="dark"]{
  --bg:#0d0b1a;--bg2:#120f22;--bg3:#1a1530;--border:#2a2250;--border2:#3d3070;
  --text1:#e8e4ff;--text2:#c4b5fd;--text3:#a78bfa;--muted:#6d5fa8;--muted2:#4a3d7a;
  --accent:#8b5cf6;--accent2:#7c3aed;--accentbg:#1e1540;--accentbg2:#2d2060;
  --danger:#f87171;--dangerBg:#3f1515;--green:#34d399;--greenBg:#0d2e22;
  --amber:#fbbf24;--amberBg:#2e1f06;--blue:#60a5fa;--blueBg:#0c1a33;
  --sidebar:rgba(13,11,26,0.86);--sideborder:rgba(42,34,80,0.7);--topbar:#120f22;
  --card:#120f22;--cardborder:#2a2250;--cardshadow:0 2px 20px #00000040;
  --rowhover:#1a1535;--inputbg:#0d0b1a;
  --scrolltrack:#1a1535;--scrollthumb:#3d3070;
}
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:var(--scrolltrack)}
::-webkit-scrollbar-thumb{background:var(--scrollthumb);border-radius:4px}
input,select,textarea,button{font-family:inherit}
input,select,textarea{transition:border-color .15s,box-shadow .18s;}
@keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes pulseBtn{0%,100%{box-shadow:0 4px 14px #7c3aed33}50%{box-shadow:0 4px 28px #7c3aed77,0 0 0 5px #7c3aed18}}
@keyframes spin{to{transform:rotate(360deg)}}
.fadeUp{animation:fadeUp .38s cubic-bezier(.22,1,.36,1) both}
.fadeIn{animation:fadeIn .25s ease both}
.card-hover{transition:transform .18s,box-shadow .18s}
.card-hover:hover{transform:translateY(-2px);box-shadow:0 8px 32px #7c3aed1a}
.btn-pulse{animation:pulseBtn 2s infinite}
.slink{width:100%;display:flex;align-items:center;gap:11px;padding:9px 14px;border:none;background:none;cursor:pointer;border-radius:10px;color:var(--muted);font-size:13.5px;font-weight:600;transition:all .15s;text-align:left;position:relative}
.slink:hover{background:var(--accentbg);color:var(--accent)}
.slink.active{background:linear-gradient(90deg,var(--accentbg),var(--accentbg2)08);color:var(--accent);font-weight:800;border-left:3px solid var(--accent)}
.slink.active .slink-icon{color:var(--accent)}
.slink-icon{font-size:17px;width:22px;text-align:center;flex-shrink:0}
.slink-label{flex:1}
.slink-badge{background:var(--accent);color:#fff;border-radius:20px;font-size:10px;font-weight:800;padding:1px 7px;min-width:18px;text-align:center}
@media(max-width:768px){
  .sidebar-full{width:64px!important}
  .slink-label,.slink-badge,.sidebar-label{display:none!important}
  .sidebar-bottom-name{display:none!important}
  .slink{justify-content:center;padding:10px}
}
input:focus,select:focus,textarea:focus{border-color:var(--accent)!important;box-shadow:0 0 0 3px #7c3aed18}
@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
.drawer-panel{animation:slideInRight .25s cubic-bezier(.22,1,.36,1) both}
@keyframes bellShake{0%,20%,100%{transform:rotate(0)}5%{transform:rotate(-15deg)}10%{transform:rotate(15deg)}15%{transform:rotate(-9deg)}}
@keyframes bellPing{0%{transform:scale(1);opacity:1}70%{transform:scale(2.4);opacity:0}100%{transform:scale(2.4);opacity:0}}
.bell-shake{animation:bellShake 4s ease-in-out 0.5s infinite}
.bell-ping::after{content:'';position:absolute;inset:-3px;border-radius:50%;border:2px solid var(--danger);animation:bellPing 1.8s ease-out 0s infinite}
`;

// ── inject styles ─────────────────────────────────────────────
if (!document.getElementById('svc-styles')) {
  const el = document.createElement('style');
  el.id = 'svc-styles';
  el.textContent = STYLE;
  document.head.appendChild(el);
}

// ── Number formatter — never show raw numbers in UI ───────────
const n = v => (+(v) || 0).toLocaleString('en-IN');

// ── Avatar gradient — unique per user based on username ───────
function avatarGradient(str = '') {
  const gradients = [
    'linear-gradient(135deg,#7c3aed,#4f46e5)',
    'linear-gradient(135deg,#2563eb,#0891b2)',
    'linear-gradient(135deg,#059669,#0d9488)',
    'linear-gradient(135deg,#d97706,#ea580c)',
    'linear-gradient(135deg,#db2777,#9333ea)',
    'linear-gradient(135deg,#0284c7,#7c3aed)',
    'linear-gradient(135deg,#16a34a,#2563eb)',
    'linear-gradient(135deg,#b45309,#d97706)',
  ];
  return gradients[(str.charCodeAt(0) || 0) % gradients.length];
}

// ── Utilities ─────────────────────────────────────────────────
function api(path, opts = {}) {
  return fetch(`${API}/${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  }).then(async r => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
function daysSince(d, stopAt) { if (!d) return 0; const end = stopAt ? new Date(stopAt).getTime() : Date.now(); return Math.floor((end - new Date(d).getTime()) / 86400000); }

// ── Animated counter (same as slipms) ────────────────────────
function useCountUp(target, duration=900, delay=0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const num = typeof target === 'number' ? target : parseInt(target) || 0;
    let raf = null, timer = null;
    const startAnim = () => {
      let t0 = null;
      raf = requestAnimationFrame(function step(ts) {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / duration, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        setVal(Math.round(num * ease));
        if (p < 1) raf = requestAnimationFrame(step);
      });
    };
    if (delay > 0) { timer = setTimeout(startAnim, delay); } else { startAnim(); }
    return () => { if (raf) cancelAnimationFrame(raf); if (timer) clearTimeout(timer); };
  }, [target, duration, delay]);
  return val;
}
function CountUpNum({ value, delay = 0 }) {
  const num = useCountUp(typeof value === 'string' ? parseInt(value) || 0 : value, 900, delay);
  return <>{num.toLocaleString('en-IN')}</>;
}

// ── Design tokens ─────────────────────────────────────────────
const S = {
  card:   { background:'var(--card)', border:'1px solid var(--cardborder)', borderRadius:16, boxShadow:'var(--cardshadow)', padding:24 },
  input:  { width:'100%', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:10, background:'var(--inputbg)', color:'var(--text1)', fontSize:14, outline:'none', transition:'border-color .15s,box-shadow .15s' },
  label:  { display:'block', fontSize:11, fontWeight:700, color:'var(--muted)', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.6px' },
  btnP:   { background:'linear-gradient(135deg,#7c3aed,#6d28d9)', color:'#fff', border:'none', borderRadius:10, padding:'10px 22px', fontWeight:700, fontSize:14, cursor:'pointer', transition:'opacity .15s,box-shadow .15s' },
  btnS:   { background:'var(--accentbg)', color:'var(--accent)', border:'1.5px solid var(--border)', borderRadius:10, padding:'9px 18px', fontWeight:700, fontSize:14, cursor:'pointer' },
  btnD:   { background:'var(--dangerBg)', color:'var(--danger)', border:'1.5px solid #fca5a590', borderRadius:10, padding:'9px 18px', fontWeight:700, fontSize:14, cursor:'pointer' },
  pill:   (c) => ({ display:'inline-flex', alignItems:'center', gap:4, background:c+'18', color:c, borderRadius:20, padding:'3px 11px', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }),
  th:     { padding:'11px 16px', textAlign:'left', fontSize:11, fontWeight:700, letterSpacing:'0.6px', textTransform:'uppercase', color:'#fff', whiteSpace:'nowrap' },
  td:     { padding:'12px 16px', fontSize:13, borderBottom:'1px solid var(--border)', verticalAlign:'middle' },
  pageTitle: { fontSize:22, fontWeight:900, color:'var(--text1)', fontFamily:"'DM Serif Display',serif", letterSpacing:-0.5 },
};

// ── SoundLife Logo ────────────────────────────────────────────
function SLogo({ size = 'md', white = false }) {
  const heights = { sm: 36, md: 54, lg: 80, xl: 100 };
  const h = heights[size] || heights.md;
  return (
    <img
      src="/SL_logo.png"
      alt="SoundLife"
      style={{
        height: h,
        width: 'auto',
        filter: white ? 'brightness(0) invert(1)' : 'none',
        display: 'block',
      }}
    />
  );
}

// ── Toast ─────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  const dur = type === 'notif' ? 6000 : 3800;
  useEffect(() => { const t = setTimeout(onClose, dur); return () => clearTimeout(t); }, [onClose, dur]);
  const isErr   = type === 'error';
  const isNotif = type === 'notif';
  const bg      = isErr ? 'var(--dangerBg)' : isNotif ? 'var(--accentbg)' : 'var(--greenBg)';
  const col     = isErr ? 'var(--danger)'   : isNotif ? 'var(--accent)'   : 'var(--green)';
  const bdr     = isErr ? '#fca5a5'         : isNotif ? '#c4b5fd'         : '#86efac';
  const icon    = isErr ? '⚠️' : isNotif ? '🔔' : '✓';
  return (
    <div className="fadeUp" style={{ position:'fixed', bottom:28, right:28, zIndex:9999, background:bg, color:col, border:`1.5px solid ${bdr}`, borderRadius:16, padding:'14px 18px 14px 16px', fontWeight:600, fontSize:13, boxShadow:'0 8px 32px rgba(0,0,0,.18)', maxWidth:380, display:'flex', alignItems:'flex-start', gap:12 }}>
      <span style={{ fontSize:18, flexShrink:0, marginTop:1 }}>{icon}</span>
      <div style={{ flex:1 }}>
        {isNotif && <div style={{ fontSize:10, fontWeight:800, letterSpacing:1.5, textTransform:'uppercase', marginBottom:4, opacity:.7 }}>New Notification</div>}
        <div>{msg}</div>
      </div>
      <button onClick={onClose} style={{ background:'none', border:'none', color:col, cursor:'pointer', fontSize:16, opacity:.6, padding:0, marginTop:1, flexShrink:0 }}>×</button>
    </div>
  );
}

// ── Public Repair Tracking Page ──────────────────────────────
// Dynamic steps — 'Sent to Company' only shown for non-Direct Company tickets
function getTrackSteps(sendingTo) {
  const base = ['Form Submitted','Courier Sent','Received at HO'];
  if (sendingTo !== 'Direct Company') base.push('Sent to Company');
  base.push('Dispatched Back','Received at Clinic','Delivered to Patient');
  return base;
}
// Map any status to its closest step index (within given steps array)
function stepIndex(status, steps) {
  if (!status || !steps) return 0;
  const exact = steps.findIndex(s => s === status);
  if (exact !== -1) return exact;
  const s = status.toLowerCase();
  if (s.includes('delivered')) return steps.length - 1;
  if (s.includes('received at clinic')) return steps.indexOf('Received at Clinic');
  if (s.includes('dispatched')) return steps.indexOf('Dispatched Back');
  if (s.includes('sent to company') || s.includes('estimate') || s.includes('patient informed') || s.includes('approved') || s.includes('work order') || s.includes('rejected')) {
    const i = steps.indexOf('Sent to Company');
    return i !== -1 ? i : steps.indexOf('Received at HO');
  }
  if (s.includes('received at ho')) return steps.indexOf('Received at HO');
  if (s.includes('courier')) return steps.indexOf('Courier Sent');
  return 0;
}

function TrackingPage({ repairNo }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/track.php?repair_no=${encodeURIComponent(repairNo)}`)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Could not connect. Please try again.'))
      .finally(() => setLoading(false));
  }, [repairNo]);

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
  const fmtDateTime = (v) => v ? new Date(v).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

  const trackSteps = getTrackSteps(data?.sending_to);
  const trackLabels = { 'Sent to Company': 'At Company' };
  const curStep = data ? stepIndex(data.status, trackSteps) : 0;
  const done    = data?.is_archived || data?.status === 'Delivered to Patient';

  return (
    <div style={{ minHeight:'100vh', background:'#f3f4f6', fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>
      {/* Header */}
      <div style={{ background:'#1e1b4b', padding:'16px 24px', display:'flex', alignItems:'center', gap:12 }}>
        <img src="/SL_logo.png" alt="" style={{ height:40, width:40, borderRadius:6, objectFit:'contain', background:'#fff', padding:2 }} onError={e=>e.target.style.display='none'} />
        <div>
          <div style={{ fontSize:17, fontWeight:900, color:'#fff', letterSpacing:-0.5 }}>SoundLife Service</div>
          <div style={{ fontSize:10, color:'#a78bfa', letterSpacing:2, textTransform:'uppercase' }}>Repair Tracking</div>
        </div>
      </div>
      <div style={{ height:4, background:'#7c3aed' }} />

      <div style={{ maxWidth:600, margin:'32px auto', padding:'0 16px' }}>
        {/* Repair number badge */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', marginBottom:6 }}>Repair Number</div>
          <div style={{ fontSize:26, fontWeight:900, color:'#1e1b4b', letterSpacing:-1 }}>{repairNo}</div>
        </div>

        {loading && (
          <div style={{ textAlign:'center', padding:48, color:'#6b7280' }}>
            <div style={{ width:32, height:32, border:'3px solid #ede9fe', borderTopColor:'#7c3aed', borderRadius:'50%', margin:'0 auto 14px', animation:'spin 0.7s linear infinite' }} />
            Looking up your repair…
          </div>
        )}

        {error && (
          <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:12, padding:24, textAlign:'center', color:'#dc2626', fontWeight:600 }}>
            ⚠️ {error}
          </div>
        )}

        {data && (
          <>
            {/* Status card */}
            <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 2px 16px rgba(0,0,0,.08)', padding:24, marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#9ca3af', marginBottom:4 }}>Current Status</div>
                  <div style={{ fontSize:17, fontWeight:800, color: done ? '#059669' : '#7c3aed' }}>{done ? '✅ ' : '🔧 '}{data.status}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:11, color:'#9ca3af', marginBottom:2 }}>Clinic</div>
                  <div style={{ fontSize:13, fontWeight:700, color:'#1e1b4b' }}>{data.clinic}</div>
                </div>
              </div>

              {/* Progress stepper */}
              <div style={{ position:'relative', marginBottom:4 }}>
                {/* Line */}
                <div style={{ position:'absolute', top:13, left:'5%', right:'5%', height:3, background:'#e5e7eb', borderRadius:2 }} />
                <div style={{ position:'absolute', top:13, left:'5%', width:`${Math.min(100, (curStep / (trackSteps.length-1)) * 90)}%`, height:3, background:'#7c3aed', borderRadius:2, transition:'width .4s' }} />
                <div style={{ display:'flex', justifyContent:'space-between', position:'relative' }}>
                  {trackSteps.map((s, i) => {
                    const active = i === curStep;
                    const past   = i < curStep;
                    return (
                      <div key={s} style={{ display:'flex', flexDirection:'column', alignItems:'center', width:`${100/trackSteps.length}%` }}>
                        <div style={{ width:26, height:26, borderRadius:'50%', background: past||active ? '#7c3aed' : '#e5e7eb', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#fff', fontWeight:700, border: active ? '3px solid #c4b5fd' : '2px solid transparent', boxSizing:'border-box', zIndex:1 }}>
                          {past ? '✓' : i+1}
                        </div>
                        <div style={{ fontSize:9, fontWeight:600, textTransform:'uppercase', letterSpacing:0.5, color: active ? '#7c3aed' : past ? '#6b7280' : '#d1d5db', marginTop:5, textAlign:'center', lineHeight:1.3 }}>{trackLabels[s] || s}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Device info */}
            <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 2px 16px rgba(0,0,0,.08)', padding:20, marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', marginBottom:14, borderBottom:'1.5px solid #ede9fe', paddingBottom:8 }}>Device Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 20px' }}>
                {[['Brand', data.ha_brand], ['Model', data.ha_model], ['Type', data.ha_type], ['Repair Date', fmtDate(data.repair_date)]].map(([l,v])=> v ? (
                  <div key={l}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#9ca3af', marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#1e1b4b' }}>{v}</div>
                  </div>
                ) : null)}
              </div>
              {data.estimate && (
                <div style={{ marginTop:12, padding:'12px 14px', background: data.estimate.type==='FOC' ? '#f0fdf4' : '#fffbeb', borderRadius:8, border:`1px solid ${data.estimate.type==='FOC'?'#bbf7d0':'#fde68a'}` }}>
                  {data.estimate.type==='FOC'
                    ? <span style={{ fontSize:13, fontWeight:700, color:'#059669' }}>✅ Repair Cost: FOC (Free of Charge)</span>
                    : <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'#d97706', marginBottom:4 }}>💰 Estimated Repair Cost: ₹{data.estimate.total.toLocaleString('en-IN')}</div>
                        {(data.estimate.amount_left||data.estimate.amount_right||data.estimate.amount_accessory) && (
                          <div style={{ fontSize:11, color:'#92400e', display:'flex', gap:12, flexWrap:'wrap' }}>
                            {data.estimate.amount_left      && <span>Left HA: ₹{data.estimate.amount_left.toLocaleString('en-IN')}</span>}
                            {data.estimate.amount_right     && <span>Right HA: ₹{data.estimate.amount_right.toLocaleString('en-IN')}</span>}
                            {data.estimate.amount_accessory && <span>Accessory: ₹{data.estimate.amount_accessory.toLocaleString('en-IN')}</span>}
                          </div>
                        )}
                      </div>
                  }
                </div>
              )}
              {data.eta_date && !data.delivered_on && (
                <div style={{ marginTop:12, padding:'10px 14px', background:'#eff6ff', borderRadius:8, border:'1px solid #bfdbfe' }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#2563eb' }}>📅 Estimated Arrival: {fmtDate(data.eta_date)}</span>
                </div>
              )}
              {data.delivered_on && (
                <div style={{ marginTop:12, padding:'10px 14px', background:'#f0fdf4', borderRadius:8, border:'1px solid #bbf7d0' }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#059669' }}>✅ Delivered on {fmtDate(data.delivered_on)}</span>
                </div>
              )}
            </div>

            {/* Timeline */}
            <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 2px 16px rgba(0,0,0,.08)', padding:20 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', marginBottom:14, borderBottom:'1.5px solid #ede9fe', paddingBottom:8 }}>Status Timeline</div>
              <div style={{ position:'relative' }}>
                <div style={{ position:'absolute', left:8, top:8, bottom:8, width:2, background:'#ede9fe' }} />
                {data.history.map((h, i) => (
                  <div key={i} style={{ display:'flex', gap:16, marginBottom:14, position:'relative' }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background: i===data.history.length-1 ? '#7c3aed' : '#c4b5fd', flexShrink:0, marginTop:2, zIndex:1 }} />
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:'#1e1b4b' }}>{h.to_status}</div>
                      <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>{fmtDateTime(h.changed_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div style={{ textAlign:'center', marginTop:32, fontSize:11, color:'#9ca3af' }}>
          SoundLife Service Portal · service.slipms.com
        </div>
      </div>
    </div>
  );
}

// ── Print Job Card ────────────────────────────────────────────
function buildPrintHtml(data) {
  const fd = (v) => v ? new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const haType = Array.isArray(data.ha_type) ? data.ha_type.join(', ') : (data.ha_type||'—');
  const complaintArr = Array.isArray(data.complaint) ? data.complaint : (data.complaint ? [data.complaint] : []);
  const warrantyProof = Array.isArray(data.warranty_proof) ? data.warranty_proof.join(', ') : (data.warranty_proof||'');
  const accessories = (() => { try { return typeof data.accessories === 'string' ? JSON.parse(data.accessories || '[]') : (data.accessories || []); } catch { return []; } })();

  const sec = (title) => `<div style="font-size:7pt;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#7c3aed;border-bottom:1.5px solid #ede9fe;padding-bottom:4px;margin:14px 0 10px;">${title}</div>`;
  const field = (label, value, full) => `
    <div style="${full?'':''}margin-bottom:9px;">
      <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#aaa;margin-bottom:2px;">${label}</div>
      <div style="font-size:11pt;font-weight:600;color:#1e1b4b;border-bottom:1px solid #e5e7eb;padding-bottom:4px;">${value||'—'}</div>
    </div>`;

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Job Card — ${data.repair_no}</title>
<style>
  @page{size:A4 portrait;margin:0}
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .body{padding:18px 24px 20px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:0 32px}
  .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0 20px}
</style></head><body>

<!-- Header bar — ink-friendly white background -->
<div style="background:#fff;padding:18px 28px 14px;display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #1e1b4b;">
  <div style="display:flex;align-items:center;gap:14px;">
    <img src="${window.location.origin}/SL_logo.png" alt="" style="height:60px;width:60px;border-radius:8px;object-fit:contain;" onerror="this.style.display='none'"/>
    <div>
      <div style="font-size:20pt;font-weight:900;color:#1e1b4b;letter-spacing:-1px;line-height:1.1;">SoundLife Service</div>
      <div style="font-size:8pt;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#7c3aed;margin-top:4px;">Hearing Aid Repair Job Card</div>
    </div>
  </div>
  <div style="text-align:right;">
    <div style="font-size:7pt;color:#7c3aed;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;margin-bottom:3px;">Repair No.</div>
    <div style="font-size:20pt;font-weight:900;color:#1e1b4b;letter-spacing:-1px;line-height:1.1;">${data.repair_no}</div>
    <div style="font-size:8pt;color:#555;margin-top:4px;">Date: ${fd(data.repair_date)}</div>
  </div>
</div>
<div style="height:3px;background:#7c3aed;-webkit-print-color-adjust:exact;print-color-adjust:exact;"></div>

<div class="body">
  ${sec('Patient &amp; Clinic')}
  <div class="grid2">
    ${field('Clinic / Branch', data.clinic_label)}
    ${field('Patient Name', data.patient_name)}
  </div>

  ${sec('Hearing Aid Details')}
  <div class="grid3">
    ${field('Brand', data.ha_brand)}
    ${field('Model', data.ha_model)}
    ${field('HA Type', haType)}
  </div>
  <div class="grid2">
    ${field('Serial No. (Left)', data.serial_left||'—')}
    ${field('Serial No. (Right)', data.serial_right||'—')}
  </div>

  ${sec('Warranty & Complaint')}
  <div class="grid2">
    ${field('Warranty Type', data.warranty_type)}
    ${field('Complaint', complaintArr.join(', ')||'—')}
  </div>
  ${warrantyProof ? `<div class="grid2">${field('Warranty Proof', warrantyProof)}${data.warranty_ref ? field('Warranty Ref. No.', data.warranty_ref) : '<div></div>'}</div>` : (data.warranty_ref ? field('Warranty Ref. No.', data.warranty_ref) : '')}
  ${data.complaint_other ? `
  <div style="background:#f5f3ff;border-left:3px solid #7c3aed;border-radius:0 4px 4px 0;padding:8px 12px;margin-bottom:9px;">
    <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#7c3aed;margin-bottom:3px;">Complaint Detail</div>
    <div style="font-size:10.5pt;font-weight:600;color:#1e1b4b;">${data.complaint_other}</div>
  </div>` : ''}

  ${data.remarks ? `
  ${sec('Remarks')}
  <div style="background:#faf9ff;border:1px solid #ede9fe;border-radius:5px;padding:9px 13px;margin-bottom:9px;">
    <div style="font-size:10.5pt;font-weight:500;color:#1e1b4b;line-height:1.5;">${data.remarks}</div>
  </div>` : ''}


  ${accessories.length > 0 ? `
  ${sec('Accessories')}
  <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
    <thead>
      <tr style="background:#f5f3ff;">
        <th style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#7c3aed;text-align:left;padding:5px 8px;border:1px solid #ede9fe;">#</th>
        <th style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#7c3aed;text-align:left;padding:5px 8px;border:1px solid #ede9fe;">Accessory</th>
        <th style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#7c3aed;text-align:left;padding:5px 8px;border:1px solid #ede9fe;">Serial No.</th>
      </tr>
    </thead>
    <tbody>
      ${accessories.map((a,i) => `
      <tr>
        <td style="font-size:10pt;font-weight:600;color:#1e1b4b;padding:6px 8px;border:1px solid #e5e7eb;">${i+1}</td>
        <td style="font-size:10pt;font-weight:600;color:#1e1b4b;padding:6px 8px;border:1px solid #e5e7eb;">${a.name||'—'}</td>
        <td style="font-size:10pt;font-weight:700;color:#1e1b4b;padding:6px 8px;border:1px solid #e5e7eb;font-family:'Courier New',monospace;">${a.serial||'—'}</td>
      </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <!-- Signature -->
  <div style="border-top:2px solid #1e1b4b;padding-top:16px;margin-top:16px;display:flex;justify-content:space-between;align-items:flex-end;">
    <div>
      <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#aaa;margin-bottom:18px;">Received By (Staff)</div>
      <div style="border-bottom:1.5px solid #333;width:180px;"></div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#aaa;margin-bottom:18px;">Customer Signature</div>
      <div style="border-bottom:1.5px solid #333;width:180px;"></div>
    </div>
  </div>
  <div style="margin-top:16px;text-align:center;font-size:7pt;color:#bbb;letter-spacing:1px;">
    SoundLife Service Portal · service.slipms.com · Printed ${new Date().toLocaleString('en-IN')}
  </div>
</div>
</body></html>`;
}

function PrintJobCard({ data, onClose }) {
  function doPrint() {
    const html = buildPrintHtml(data);
    // Use hidden iframe so print dialog opens in-page, no new tab
    const existing = document.getElementById('sl-print-iframe');
    if (existing) existing.remove();
    const iframe = document.createElement('iframe');
    iframe.id = 'sl-print-iframe';
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;opacity:0;';
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      iframe.contentWindow.addEventListener('afterprint', () => { iframe.remove(); window.focus(); });
    };
    iframe.srcdoc = html;
  }

  useEffect(() => {
    const t = setTimeout(doPrint, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Screen preview of the card
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,.6)' }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:640, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,.4)' }}>

        {/* Header — always visible, never scrolls */}
        <div style={{ background:'#1e1b4b', borderRadius:'14px 14px 0 0', padding:'18px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <img src="/SL_logo.png" alt="" style={{ height:54, width:54, borderRadius:8, objectFit:'contain', background:'#fff', padding:3 }} />
            <div>
              <div style={{ fontSize:18, fontWeight:900, color:'#fff', letterSpacing:-0.5 }}>SoundLife Service</div>
              <div style={{ fontSize:9, letterSpacing:2.5, textTransform:'uppercase', color:'#a78bfa', marginTop:4 }}>Hearing Aid Repair Job Card</div>
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:9, color:'#a78bfa', letterSpacing:1, textTransform:'uppercase' }}>Repair No.</div>
            <div style={{ fontSize:18, fontWeight:900, color:'#fff' }}>{data.repair_no}</div>
            <div style={{ fontSize:9, color:'#c4b5fd', marginTop:1 }}>{new Date(data.repair_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
          </div>
        </div>
        <div style={{ height:4, background:'#7c3aed', flexShrink:0 }} />

        {/* Body — scrollable */}
        <div style={{ flex:1, overflowY:'auto', padding:'18px 22px', color:'#1e1b4b', fontFamily:'Arial,sans-serif' }}>
          {/* Patient & Clinic */}
          <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', borderBottom:'1.5px solid #ede9fe', paddingBottom:4, marginBottom:10 }}>Patient &amp; Clinic</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 28px', marginBottom:4 }}>
            {[['Clinic', data.clinic_label],['Patient Name', data.patient_name]].map(([l,v])=>(
              <div key={l} style={{ marginBottom:9 }}>
                <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#aaa', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:12, fontWeight:600, borderBottom:'1px solid #e5e7eb', paddingBottom:4 }}>{v||'—'}</div>
              </div>
            ))}
          </div>

          {/* HA Details */}
          <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', borderBottom:'1.5px solid #ede9fe', paddingBottom:4, marginBottom:10 }}>Hearing Aid Details</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'0 20px', marginBottom:4 }}>
            {[['Brand',data.ha_brand],['Model',data.ha_model],['HA Type',Array.isArray(data.ha_type)?data.ha_type.join(', '):(data.ha_type||'—')]].map(([l,v])=>(
              <div key={l} style={{ marginBottom:9 }}>
                <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#aaa', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:12, fontWeight:600, borderBottom:'1px solid #e5e7eb', paddingBottom:4 }}>{v||'—'}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 28px', marginBottom:4 }}>
            {[['Serial No. (Left)',data.serial_left||'—'],['Serial No. (Right)',data.serial_right||'—']].map(([l,v])=>(
              <div key={l} style={{ marginBottom:9 }}>
                <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#aaa', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:12, fontWeight:600, borderBottom:'1px solid #e5e7eb', paddingBottom:4 }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Warranty & Complaint */}
          <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', borderBottom:'1.5px solid #ede9fe', paddingBottom:4, marginBottom:10 }}>Warranty &amp; Complaint</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 28px', marginBottom:6 }}>
            {[['Warranty Type',data.warranty_type],['Complaint',(Array.isArray(data.complaint)?data.complaint:(data.complaint?[data.complaint]:[])).join(', ')||'—']].map(([l,v])=>(
              <div key={l} style={{ marginBottom:9 }}>
                <div style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#aaa', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:12, fontWeight:600, borderBottom:'1px solid #e5e7eb', paddingBottom:4 }}>{v||'—'}</div>
              </div>
            ))}
          </div>
          {data.complaint_other && <div style={{ background:'#f5f3ff', borderLeft:'3px solid #7c3aed', borderRadius:'0 4px 4px 0', padding:'7px 11px', marginBottom:8 }}><div style={{ fontSize:8, fontWeight:700, color:'#7c3aed', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>Complaint Detail</div><div style={{ fontSize:11, fontWeight:600 }}>{data.complaint_other}</div></div>}
          {data.remarks && <div style={{ background:'#faf9ff', border:'1px solid #ede9fe', borderRadius:5, padding:'8px 12px', marginBottom:8 }}><div style={{ fontSize:8, fontWeight:700, color:'#aaa', letterSpacing:1, textTransform:'uppercase', marginBottom:2 }}>Remarks</div><div style={{ fontSize:11, fontWeight:500 }}>{data.remarks}</div></div>}

          {/* Accessories */}
          {(() => { let acc; try { acc = typeof data.accessories==='string' ? JSON.parse(data.accessories||'[]') : (data.accessories||[]); } catch { acc=[]; } return acc.length > 0 ? (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:8, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'#7c3aed', borderBottom:'1.5px solid #ede9fe', paddingBottom:4, marginBottom:8 }}>Accessories</div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#f5f3ff' }}>
                    {['#','Accessory','Serial No.'].map(h=><th key={h} style={{ fontSize:8, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#7c3aed', textAlign:'left', padding:'5px 8px', border:'1px solid #ede9fe' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {acc.map((a,i)=>(
                    <tr key={i}>
                      <td style={{ fontSize:11, fontWeight:600, color:'#1e1b4b', padding:'5px 8px', border:'1px solid #e5e7eb' }}>{i+1}</td>
                      <td style={{ fontSize:11, fontWeight:600, color:'#1e1b4b', padding:'5px 8px', border:'1px solid #e5e7eb' }}>{a.name||'—'}</td>
                      <td style={{ fontSize:11, fontWeight:700, color:'#1e1b4b', padding:'5px 8px', border:'1px solid #e5e7eb', fontFamily:'monospace' }}>{a.serial||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null; })()}

          {/* Signature */}
          <div style={{ borderTop:'2px solid #1e1b4b', paddingTop:14, marginTop:14, display:'flex', justifyContent:'space-between' }}>
            <div><div style={{ fontSize:8, color:'#aaa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Received By (Staff)</div><div style={{ borderBottom:'1.5px solid #333', width:160 }} /></div>
            <div style={{ textAlign:'right' }}><div style={{ fontSize:8, color:'#aaa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Customer Signature</div><div style={{ borderBottom:'1.5px solid #333', width:160 }} /></div>
          </div>
        </div>

        {/* Buttons — always visible, never scrolls */}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', padding:'14px 22px', borderTop:'1px solid #ede9fe', background:'#faf9ff', borderRadius:'0 0 14px 14px', flexShrink:0 }}>
          <button onClick={doPrint} style={{ background:'#1e1b4b', color:'#fff', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>🖨️ Print Again</button>
          <button onClick={onClose} style={{ background:'#dc2626', color:'#fff', border:'none', borderRadius:8, padding:'9px 22px', fontWeight:700, fontSize:13, cursor:'pointer' }}>✕ Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────
function Modal({ title, children, onClose, width = 540 }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(
    <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'rgba(0,0,0,.55)', zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', boxSizing:'border-box' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--card)', borderRadius:20, width:'100%', maxWidth:width, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 32px 80px rgba(0,0,0,.4)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 28px 16px', flexShrink:0, borderBottom:'1px solid var(--border)' }}>
          <span style={{ fontSize:16, fontWeight:800, color:'var(--text1)' }}>{title}</span>
          <button onClick={onClose} style={{ background:'var(--bg3)', border:'none', borderRadius:8, width:32, height:32, fontSize:18, color:'var(--muted)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
        <div style={{ padding:'20px 28px 28px', overflowY:'auto', flex:1 }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Live Clock ────────────────────────────────────────────────
function LiveClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 60000); return () => clearInterval(i); }, []);
  return <span>{t.toLocaleDateString('en-IN',{weekday:'short',day:'2-digit',month:'short'})} · {t.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</span>;
}

// ═════════════════════════════════════════════════════════════
// LOGIN PAGE — Split screen, premium
// ═════════════════════════════════════════════════════════════
function LoginPage({ onOtpStep }) {
  const quote = QUOTES[Math.floor(Date.now()/86400000) % QUOTES.length];
  const [form, setForm] = useState({ username:'', password:'' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e) {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const r = await api('otp.php?action=send', { method:'POST', body:JSON.stringify(form) });
      onOtpStep(form.username, r.maskedEmail, r.cooldown, r.secondsLeft);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex' }}>
      {/* Left panel */}
      <div style={{ width:'46%', background:'linear-gradient(145deg,#1e1b4b 0%,#4c1d95 40%,#7c3aed 100%)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:48, position:'relative', overflow:'hidden' }}>
        {/* Decorative circles */}
        <div style={{ position:'absolute', top:-80, right:-80, width:320, height:320, borderRadius:'50%', background:'rgba(255,255,255,.04)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-60, left:-60, width:240, height:240, borderRadius:'50%', background:'rgba(255,255,255,.06)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', top:'30%', left:-40, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,.03)', pointerEvents:'none' }}/>

        <div className="fadeUp" style={{ zIndex:1, textAlign:'center', maxWidth:360 }}>
          <SLogo size="xl" white />
          <div style={{ display:'inline-block', marginTop:10, marginBottom:4, background:'rgba(255,255,255,.15)', borderRadius:20, padding:'4px 16px', fontSize:11, fontWeight:800, color:'#e0d7ff', letterSpacing:2.5, textTransform:'uppercase' }}>Service Portal</div>

          <div style={{ margin:'28px 0 24px', borderTop:'1px solid rgba(255,255,255,.12)', paddingTop:24 }}>
            <div style={{ fontSize:13, color:'#c4b5fd', fontWeight:600, letterSpacing:1, textTransform:'uppercase', marginBottom:14 }}>Repair Coordination Portal</div>
            {[['🔧','Log & track every repair job'],['📦','Monitor couriers end-to-end'],['📊','Reports & delay analytics'],['🔔','Real-time clinic notifications']].map(([icon,text]) => (
              <div key={text} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:11, textAlign:'left' }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0 }}>{icon}</div>
                <span style={{ fontSize:13, color:'#e0d7ff', fontWeight:500 }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Today's Note */}
          <div style={{ margin:'24px 0', textAlign:'left' }}>
            <div style={{ fontSize:10, color:'#c4b5fd', fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', marginBottom:12 }}>Today's Note</div>
            <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:18, color:'#fff', lineHeight:1.6, fontStyle:'italic', marginBottom:10 }}>"{quote.q}"</div>
            <div style={{ fontSize:12, color:'#a78bfa' }}>{quote.a}</div>
          </div>

          <div style={{ borderTop:'1px solid rgba(255,255,255,.12)', paddingTop:20, display:'flex', justifyContent:'center', gap:24 }}>
            {[['22','Clinics'],['4','States'],['∞','History']].map(([v,l]) => (
              <div key={l} style={{ textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:900, color:'#fff' }}>{v}</div>
                <div style={{ fontSize:10, color:'#c4b5fd', textTransform:'uppercase', letterSpacing:1 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:40 }}>
        <div className="fadeUp" style={{ width:'100%', maxWidth:420 }}>
          <div style={{ marginBottom:36 }}>
            <div style={{ fontSize:28, fontWeight:900, color:'var(--text1)', fontFamily:"'DM Serif Display',serif", marginBottom:6 }}>Welcome back</div>
            <div style={{ fontSize:14, color:'var(--muted)' }}>Sign in to SoundLife Service Portal</div>
          </div>

          <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:18 }}>
            <div>
              <label style={S.label}>Username</label>
              <input style={S.input} value={form.username} onChange={e => setForm(f=>({...f,username:e.target.value}))} placeholder="sl-mis / sl-office / sl-juhu" autoFocus required />
            </div>
            <div>
              <label style={S.label}>Password</label>
              <div style={{ position:'relative' }}>
                <input style={{ ...S.input, paddingRight:44 }} type={showPwd?'text':'password'} value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="••••••••••••" required />
                <button type="button" onClick={() => setShowPwd(p=>!p)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--muted)' }}>{showPwd?'🙈':'👁'}</button>
              </div>
            </div>
            {err && (
              <div style={{ background:'var(--dangerBg)', color:'var(--danger)', borderRadius:10, padding:'11px 15px', fontSize:13, fontWeight:600, display:'flex', gap:8, alignItems:'center' }}>
                ⚠️ {err}
              </div>
            )}
            <button className="btn-pulse" style={{ ...S.btnP, padding:'13px', fontSize:15, marginTop:4, borderRadius:12 }} disabled={loading}>
              {loading ? '⏳ Verifying…' : 'Continue →'}
            </button>
          </form>

          <div style={{ marginTop:32, padding:'16px 20px', background:'var(--bg3)', borderRadius:12, fontSize:12, color:'var(--muted)', textAlign:'center' }}>
            🔐 OTP will be sent to your registered email after password verification
          </div>
        </div>
      </div>
    </div>
  );
}

// ── OTP Page ──────────────────────────────────────────────────
function OtpPage({ username, maskedEmail, initialCooldown, cooldownSecs, onVerified }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [countdown, setCountdown] = useState(initialCooldown ? (cooldownSecs||300) : 0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown(c => c > 0 ? c-1 : 0), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  async function verify(e) {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const r = await api('otp.php?action=verify', { method:'POST', body:JSON.stringify({ username, otp }) });
      onVerified(r.user, r.csrf);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }

  async function resend() {
    setErr('');
    try {
      const r = await api('otp.php?action=resend', { method:'POST', body:JSON.stringify({ username }) });
      setCountdown(r.cooldown ? r.secondsLeft : 300);
    } catch(e) { setErr(e.message); }
  }

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
      <div className="fadeUp" style={{ ...S.card, width:'100%', maxWidth:420, textAlign:'center' }}>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--accentbg)', margin:'0 auto 20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 }}>📧</div>
        <div style={{ fontSize:22, fontWeight:800, color:'var(--text1)', marginBottom:6 }}>Check your email</div>
        <div style={{ fontSize:13, color:'var(--muted)', marginBottom:28 }}>OTP sent to <strong style={{ color:'var(--text1)' }}>{maskedEmail}</strong><br/>Valid for 10 minutes</div>

        <form onSubmit={verify} style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <input style={{ ...S.input, textAlign:'center', fontSize:32, fontWeight:900, letterSpacing:14, fontFamily:'monospace', padding:'16px' }}
            value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
            placeholder="000000" maxLength={6} autoFocus required />
          {err && <div style={{ background:'var(--dangerBg)', color:'var(--danger)', borderRadius:10, padding:'10px 14px', fontSize:13, fontWeight:600 }}>⚠️ {err}</div>}
          <button style={{ ...S.btnP, padding:'13px', fontSize:15 }} disabled={loading||otp.length<6}>
            {loading ? '⏳ Verifying…' : '✓ Verify & Login'}
          </button>
        </form>

        <div style={{ marginTop:20, fontSize:13, color:'var(--muted)' }}>
          {countdown > 0
            ? `Resend available in ${countdown}s`
            : <button style={{ background:'none', border:'none', color:'var(--accent)', fontWeight:700, cursor:'pointer', fontSize:13 }} onClick={resend}>↻ Resend OTP</button>
          }
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// APP ROOT
// ═════════════════════════════════════════════════════════════
export default function App() {
  const [authState, setAuthState] = useState('loading');
  const [user, setUser]   = useState(null);
  const [csrf, setCsrf]   = useState('');
  const [otpData, setOtpData] = useState(null);
  const [dark, setDark]   = useState(localStorage.getItem('svc_theme') === 'dark');
  const [toast, setToast] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    localStorage.setItem('svc_theme', dark ? 'dark' : 'light');
  }, [dark]);

  useEffect(() => {
    api('auth.php?action=check').then(r => {
      setCsrf(r.csrf||'');
      if (r.user) { setUser(r.user); setAuthState('app'); }
      else setAuthState('login');
    }).catch(() => setAuthState('login'));
  }, []);

  const [notifications, setNotifications] = useState([]);
  const refreshNotifs = useCallback(() => api('notifications.php').then(r => {
    setNotifications(r.notifications || []);
    setUnread(r.unread || 0);
    return r;
  }).catch(()=>{}), []);

  const prevUnread = React.useRef(null);
  useEffect(() => {
    if (authState !== 'app') return;
    const poll = () => refreshNotifs().then(r => {
      if (!r) return;
      const count = r.unread || 0;
      // Popup only for clinic + admin — owner sees badge only, no interruptions
      if (prevUnread.current !== null && count > prevUnread.current && r.latest && user?.role !== 'owner') {
        setToast({ msg: r.latest.message, type: 'notif' });
      }
      prevUnread.current = count;
    });
    poll();
    const t = setInterval(poll, 20000);
    return () => clearInterval(t);
  }, [authState, user, refreshNotifs]);

  const showToast = useCallback((msg, type='success') => setToast({ msg, type }), []);
  async function logout() {
    await api('auth.php?action=logout', { method:'POST' }).catch(()=>{});
    setUser(null); setAuthState('login');
  }

  // Public tracking page — no auth needed
  const trackM = window.location.pathname.match(/^\/track\/([A-Z0-9-]+)$/i);
  if (trackM) return <TrackingPage repairNo={trackM[1].toUpperCase()} />;

  if (authState === 'loading') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', flexDirection:'column', gap:20 }}>
      <SLogo size="lg"/>
      <div style={{ display:'flex', alignItems:'center', gap:10, color:'var(--muted)', fontSize:13, marginTop:8 }}>
        <span style={{ width:16, height:16, border:'2px solid #c4b5fd', borderTopColor:'#7c3aed', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}/>
        Connecting…
      </div>
    </div>
  );

  if (authState === 'login') return <LoginPage onOtpStep={(u,m,c,s) => { setOtpData({username:u,maskedEmail:m,cooldown:c,secs:s}); setAuthState('otp'); }} />;
  if (authState === 'otp' && otpData) return <OtpPage {...otpData} onVerified={(u,c) => { setUser(u); setCsrf(c); setAuthState('app'); }} />;

  return (
    <>
      <Shell user={user} csrf={csrf} dark={dark} setDark={setDark} unread={unread} notifications={notifications} refreshNotifs={refreshNotifs} onLogout={logout} showToast={showToast} />
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// SHELL — Sidebar layout
// ═════════════════════════════════════════════════════════════
function Shell({ user, csrf, dark, setDark, unread, notifications, refreshNotifs, onLogout, showToast }) {
  const isClinic = user.role === 'clinic';
  const isOwner  = user.role === 'owner';

  // Shared tickets state — fetched once, reused by ActiveRepairs + AdminDashboard + ClinicHome
  const [sharedTickets, setSharedTickets] = useState(null);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const reloadTickets = useCallback((qs = '') => {
    setTicketsLoading(true);
    api(`tickets.php${qs ? '?' + qs : ''}`)
      .then(d => setSharedTickets(Array.isArray(d) ? d : []))
      .catch(() => setSharedTickets([]))
      .finally(() => setTicketsLoading(false));
  }, []);
  useEffect(() => { reloadTickets(); }, [reloadTickets]);

  // Global drawer for Reports tab — lives at Shell level so it renders above everything
  const [shellDrawerTicket, setShellDrawerTicket] = useState(null);

  // Auto-refresh every 30s — clinic users only. Admin/owner have their own refresh inside AdminDashboard.
  const [liveIndicator, setLiveIndicator] = useState(false);
  useEffect(() => {
    if (!isClinic) return; // admin/owner: no Shell-level refresh, prevents cascading re-renders into AdminDashboard
    const tick = setInterval(() => {
      setLiveIndicator(true);
      api('tickets.php')
        .then(d => { setSharedTickets(Array.isArray(d) ? d : []); })
        .catch(() => {})
        .finally(() => setTimeout(() => setLiveIndicator(false), 800));
    }, 30000);
    return () => clearInterval(tick);
  }, [isClinic]);

  const clinicNav = [
    { id:'home',         icon:'🏠', label:'Home' },
    { id:'new',          icon:'➕', label:'New Repair' },
    { id:'active',       icon:'🔧', label:'Active Repairs' },
    { id:'history',      icon:'📦', label:'Repair History' },
    { id:'search',       icon:'🔍', label:'Patient Search' },
    { id:'notifications',icon:'🔔', label:'Notifications', badge: unread||null },
    { id:'reports',      icon:'🛡️', label:'Warranty' },
  ];
  const adminNav = [
    { id:'dashboard',      icon:'📋', label:'Dashboard' },
    { id:'search',         icon:'🔍', label:'Patient Search' },
    { id:'notifications',  icon:'🔔', label:'Notifications', badge: unread||null },
    { id:'reports',        icon:'📊', label:'Reports' },
    { id:'export',         icon:'📤', label:'Export' },
  ];
  const ownerNav = [
    { id:'dashboard',      icon:'📋', label:'Dashboard' },
    { id:'search',         icon:'🔍', label:'Patient Search' },
    { id:'notifications',  icon:'🔔', label:'Notifications', badge: unread||null },
    { id:'clinics',        icon:'🏥', label:'Clinic Accounts' },
    { id:'reports',        icon:'📊', label:'Reports' },
    { id:'export',         icon:'📤', label:'Export' },
  ];

  const navItems = isClinic ? clinicNav : isOwner ? ownerNav : adminNav;
  const validIds = navItems.map(n => n.id);
  const [tab, setTab] = useState(() => {
    try {
      const saved = localStorage.getItem('sl_tab_' + user.role);
      return saved && validIds.includes(saved) ? saved : navItems[0].id;
    } catch { return navItems[0].id; }
  });
  // persist tab so it survives any re-mount
  useEffect(() => {
    try { localStorage.setItem('sl_tab_' + user.role, tab); } catch {}
  }, [tab, user.role]);

  const roleColor  = user.role === 'owner' ? '#7c3aed' : user.role === 'admin' ? '#2563eb' : '#059669';
  const roleLabel  = user.role === 'owner' ? 'Owner' : user.role === 'admin' ? 'Admin' : user.clinic_label || 'Clinic';
  const initials   = (user.name||user.username).split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase() || (user.username||'').slice(0,2).toUpperCase();

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'var(--bg)' }}>
      {/* ── Sidebar ── */}
      <aside className="sidebar-full" style={{ width:232, background:'var(--sidebar)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)', borderRight:'1px solid var(--sideborder)', display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', flexShrink:0, boxShadow:'2px 0 24px #7c3aed12' }}>
        {/* Logo */}
        <div style={{ padding:'20px 18px 16px', borderBottom:'1px solid var(--sideborder)', display:'flex', flexDirection:'column', alignItems:'center' }}>
          <SLogo size="lg" />
          <div className="sidebar-label" style={{ marginTop:7, fontSize:9, fontWeight:800, color:'var(--accent)', letterSpacing:2.5, textTransform:'uppercase', textAlign:'center' }}>Service Portal</div>
        </div>

        {/* Role badge */}
        <div style={{ margin:'12px 12px 4px', padding:'8px 12px', background:`${roleColor}12`, borderRadius:10, border:`1px solid ${roleColor}22` }}>
          <div style={{ fontSize:10, fontWeight:700, color:roleColor, textTransform:'uppercase', letterSpacing:1 }} className="sidebar-label">Logged in as</div>
          <div style={{ fontSize:12, fontWeight:800, color:'var(--text1)', marginTop:2 }} className="sidebar-label">{roleLabel}</div>
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:'8px 10px', display:'flex', flexDirection:'column', gap:3, overflowY:'auto' }}>
          {navItems.map(item => (
            <button key={item.id} className={`slink${tab===item.id?' active':''}`} onClick={() => { try { localStorage.setItem('sl_tab_' + user.role, item.id); } catch {} setTab(item.id); }}>
              <span className="slink-icon">{item.icon}</span>
              <span className="slink-label">{item.label}</span>
              {item.badge ? <span className="slink-badge">{item.badge}</span> : null}
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div style={{ borderTop:'1px solid var(--sideborder)', padding:'12px 10px' }}>
          <button onClick={() => setDark(d=>!d)} className="slink" style={{ marginBottom:4 }}>
            <span className="slink-icon">{dark?'☀️':'🌙'}</span>
            <span className="slink-label">{dark?'Light Mode':'Dark Mode'}</span>
          </button>
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:10, background:'var(--bg3)' }}>
            <div style={{ width:34, height:34, borderRadius:10, background:avatarGradient(user.username), display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900, color:'#fff', flexShrink:0, letterSpacing:0.5 }}>{initials}</div>
            <div style={{ flex:1, minWidth:0 }} className="sidebar-bottom-name">
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.name||user.username}</div>
              <div style={{ fontSize:10, color:'var(--muted)' }}>{user.username}</div>
            </div>
            <button onClick={onLogout} title="Sign out" style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--muted)', padding:'4px', borderRadius:6, flexShrink:0 }}>⏏</button>
          </div>
        </div>
      </aside>

      {/* ── Content ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflowX:'hidden' }}>
        {/* Topbar */}
        <div style={{ background:'var(--topbar)', borderBottom:'1px solid var(--border)', padding:'0 28px', display:'flex', alignItems:'center', height:58, position:'sticky', top:0, zIndex:20, boxShadow:'0 1px 0 var(--border)' }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:18, fontWeight:800, color:'var(--text1)', fontFamily:"'DM Serif Display',serif" }}>
              {navItems.find(n=>n.id===tab)?.icon} {navItems.find(n=>n.id===tab)?.label}
            </div>
            {isClinic && <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>{user.clinic_label} · {user.clinic_id}</div>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color: liveIndicator?'#059669':'var(--muted)', background:'var(--accentbg)', padding:'4px 10px', borderRadius:20, fontWeight:600, transition:'color 0.3s' }}>
              <span style={{ width:7, height:7, borderRadius:'50%', background: liveIndicator?'#059669':'#22c55e', display:'inline-block', animation:'pulse 2s infinite' }} />
              Live
            </div>
            <div style={{ fontSize:11, color:'var(--muted)', background:'var(--accentbg)', padding:'5px 12px', borderRadius:20, fontWeight:600 }}>
              <LiveClock />
            </div>
            <button onClick={()=>setTab('notifications')} style={{ position:'relative', background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, width:36, height:36, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:17, transformOrigin:'top center', animation: unread > 0 ? 'bellShake 4s ease-in-out 0.5s infinite' : 'none' }}>
              🔔
              {unread > 0 && <span style={{ position:'absolute', top:-4, right:-4, background:'var(--danger)', color:'#fff', borderRadius:'50%', width:17, height:17, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, animation:'bellPing 1.8s ease-out infinite' }}>{unread > 9 ? '9+' : unread}</span>}
            </button>
          </div>
        </div>

        {/* Page content */}
        <div style={{ flex:1, minWidth:0 }}>
          {/* All non-dashboard tabs — animated on each switch */}
          {tab !== 'dashboard' && (
            <div key={tab} className="fadeIn" style={{ padding:'28px 28px 40px', maxWidth:1240, width:'100%', margin:'0 auto', boxSizing:'border-box' }}>
              {tab==='home'         && <ClinicHome      key="home" user={user} onNav={setTab} sharedTickets={sharedTickets} />}
              {tab==='new'          && <NewRepairForm  key="new"  user={user} csrf={csrf} showToast={showToast} onCreated={()=>{ reloadTickets(); setTab('active'); }} />}
              {tab==='active'       && <ActiveRepairs  key="act"  user={user} csrf={csrf} showToast={showToast} sharedTickets={sharedTickets} ticketsLoading={ticketsLoading} onReload={reloadTickets} />}
              {tab==='history'      && <ClinicHistory  key="hist" user={user} />}
              {tab==='search'       && <PatientSearch  key="srch" user={user} csrf={csrf} />}
              {tab==='notifications'&& <NotificationsPanel key="notif" user={user} csrf={csrf} notifications={notifications} onRefresh={refreshNotifs} />}
              {tab==='clinics'      && <ClinicManagement  key="cli"  user={user} csrf={csrf} showToast={showToast} />}
              {tab==='reports'      && <ReportsTab    key="rep"  user={user} csrf={csrf} onOpenTicket={setShellDrawerTicket} />}
              {tab==='export'       && <ExportTab key="exp" user={user} />}
            </div>
          )}
          {/* AdminDashboard is ALWAYS mounted — never destroyed, filter state never resets */}
          {!isClinic && (
            <div style={{ display: tab==='dashboard' ? 'block' : 'none', padding:'28px 28px 40px', maxWidth:1240, width:'100%', margin:'0 auto', boxSizing:'border-box' }}>
              <AdminDashboard user={user} csrf={csrf} showToast={showToast} onReload={reloadTickets} />
            </div>
          )}
          {/* Shell-level drawer — used by Reports tab so it renders above everything */}
          {shellDrawerTicket && <RepairDrawer ticket={shellDrawerTicket} user={user} csrf={csrf} onClose={()=>setShellDrawerTicket(null)} onAction={()=>{}} />}
        </div>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────
function StatCard({ icon, value, label, color }) {
  return (
    <div className="card-hover" style={{ ...S.card, display:'flex', alignItems:'center', gap:16, padding:'18px 22px', borderLeft:`4px solid ${color}`, borderRadius:14 }}>
      <div style={{ width:48, height:48, borderRadius:13, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{icon}</div>
      <div>
        <div style={{ fontSize:32, fontWeight:900, color, lineHeight:1, letterSpacing:-1.5 }}><CountUpNum value={value} /></div>
        <div style={{ fontSize:11, color:'var(--muted)', marginTop:5, fontWeight:700, textTransform:'uppercase', letterSpacing:.6 }}>{label}</div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// NEW REPAIR FORM
// ═════════════════════════════════════════════════════════════
function NewRepairForm({ user, csrf, showToast, onCreated }) {
  const today = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({ repair_date:today, sending_to:'HO', direct_company:'', warranty_type:'Under Warranty', warranty_proof:[], warranty_ref:'', patient_name:'', patient_mobile:'', ha_brand:'', ha_model:'', serial_left:'', serial_right:'', ha_type:[], complaint:[], complaint_other:'', remarks:'', accessories:[] });
  const [serialWarn, setSerialWarn] = useState({ left:null, right:null });
  const [submitting, setSubmitting] = useState(false);
  const [returning, setReturning] = useState(null); // existing patient info
  const [created, setCreated] = useState(null);
  const [printJobCard, setPrintJobCard] = useState(false);

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleArr = (k,v) => setForm(f=>({...f,[k]:f[k].includes(v)?f[k].filter(x=>x!==v):[...f[k],v]}));

  // Auto-fill when mobile reaches 10 digits; clear pre-filled fields when number changes
  useEffect(() => {
    const mob = form.patient_mobile;
    if (mob.length !== 10) {
      // Clear any previously auto-filled patient data when number is being changed
      if (returning) {
        setReturning(null);
        setForm(f => ({
          ...f,
          patient_name:'', ha_brand:'', ha_model:'', ha_type:[],
          serial_left:'', serial_right:'', complaint:[], complaint_other:'', remarks:'',
        }));
        setSerialWarn({ left:null, right:null });
      }
      return;
    }
    api(`patients_repair.php?q=${mob}`).then(results => {
      const match = results?.find?.(p => p.mobile === mob);
      if (!match) { setReturning(null); return; }
      setReturning(match);
      // Always overwrite with the matched patient's latest data
      const t0 = match.tickets?.[0];
      setForm(f => ({
        ...f,
        patient_name:    match.name,
        ha_brand:        t0?.ha_brand    || '',
        ha_model:        t0?.ha_model    || '',
        ha_type:         t0?.ha_type     ? t0.ha_type.split(',').map(s=>s.trim()).filter(Boolean) : [],
        serial_left:     t0?.serial_left  || '',
        serial_right:    t0?.serial_right || '',
        complaint:       typeof t0?.complaint === 'string' ? t0.complaint.split(',').map(s=>s.trim()).filter(Boolean) : [],
        complaint_other: t0?.complaint_other || '',
        remarks:         t0?.remarks     || '',
      }));
    }).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.patient_mobile]);

  async function checkSerial(side, val) {
    if (val.trim().length < 4) { setSerialWarn(w=>({...w,[side]:null})); return; }
    try {
      const r = await api(`tickets.php?action=check_serial&serial=${encodeURIComponent(val.trim())}`);
      setSerialWarn(w=>({...w,[side]:
        r.active_duplicate
          ? { type:'error', msg:`🚨 DUPLICATE — Serial already in active repair ${r.active_repair_no} · ${r.patient_name}` }
          : r.found
          ? { type:'warn',  msg:`⚠️ Re-repair — previously repaired as ${r.repair_no} on ${fmtDate(r.date)} · ${r.patient_name}` }
          : null
      }));
    } catch(_){}
  }

  async function submit(e) {
    e.preventDefault();
    if (form.patient_mobile.replace(/\D/g,'').length !== 10) { showToast('Enter a valid 10-digit mobile number','error'); return; }
    if (!form.serial_left && !form.serial_right) {
      showToast('Please enter at least one serial number — Left or Right', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api('tickets.php', { method:'POST', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({...form, ha_type:form.ha_type.join(', '), complaint:form.complaint.join(', '), csrf_token:csrf}) });
      showToast(`✓ Repair created — ${r.repair_no}`);
      setCreated({
        repair_no: r.repair_no,
        repair_date: form.repair_date,
        clinic_label: user.clinic_label,
        patient_name: form.patient_name,
        patient_mobile: form.patient_mobile,
        ha_brand: form.ha_brand,
        ha_model: form.ha_model,
        ha_type: Array.isArray(form.ha_type) ? form.ha_type.join(', ') : (form.ha_type||''),
        warranty_type: form.warranty_type,
        warranty_proof: Array.isArray(form.warranty_proof) ? form.warranty_proof.join(', ') : '',
        warranty_ref: form.warranty_ref||'',
        serial_left: form.serial_left||'',
        serial_right: form.serial_right||'',
        complaint: Array.isArray(form.complaint) ? form.complaint.join(', ') : (form.complaint||''),
        complaint_other: form.complaint_other||'',
        remarks: form.remarks||'',
        accessories: form.accessories||[]
      });
    } catch(e) { showToast(e.message,'error'); }
    finally { setSubmitting(false); }
  }

  const row2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 };

  if (created) {
    return (
      <div className="fadeUp" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:400 }}>
        {printJobCard && <PrintJobCard data={created} onClose={()=>setPrintJobCard(false)} />}
        <div style={{ ...S.card, maxWidth:480, width:'100%', textAlign:'center', padding:40 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:22, fontWeight:900, color:'var(--green)', marginBottom:8 }}>Repair Created</div>
          <div style={{ fontSize:30, fontWeight:900, color:'var(--accent)', marginBottom:16, letterSpacing:-1 }}>{created.repair_no}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <button onClick={()=>setPrintJobCard(true)} style={{ ...S.btnS, justifyContent:'center', display:'flex', alignItems:'center', gap:8, padding:'13px 24px', fontSize:15 }}>🖨️ Print Job Card</button>
            <button onClick={onCreated} style={{ ...S.btnP, padding:'13px 24px', fontSize:15 }}>→ Go to Active Repairs</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fadeUp">
      <div style={{ marginBottom:24 }}>
        <div style={S.pageTitle}>New Repair Form</div>
        <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>Submit a new device repair request</div>
      </div>

      <form onSubmit={submit}>
        <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

          {/* Section: Job Details */}
          <Section title="Job Details" icon="📋">
            <div style={row2}>
              <div><label style={S.label}>Repair Date</label><input style={{...S.input, background:'var(--bg3)', cursor:'default'}} type="date" value={form.repair_date} readOnly required /></div>
              <div><label style={S.label}>Sending To</label>
                <select style={S.input} value={form.sending_to} onChange={e=>setF('sending_to',e.target.value)}>
                  <option value="HO">HO (Head Office)</option>
                  <option value="Direct Company">Direct Company</option>
                </select>
              </div>
            </div>
            <div>
              <label style={S.label}>Warranty Type</label>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {['Under Warranty','Non Warranty','Re-Repair'].map(wt => (
                  <label key={wt} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', padding:'9px 18px', borderRadius:10, background:form.warranty_type===wt?'var(--accentbg)':'var(--bg)', border:`1.5px solid ${form.warranty_type===wt?'var(--accent)':'var(--border)'}`, fontWeight:600, fontSize:13, color:form.warranty_type===wt?'var(--accent)':'var(--text1)', transition:'all .15s' }}>
                    <input type="radio" name="warranty" value={wt} checked={form.warranty_type===wt} onChange={()=>setF('warranty_type',wt)} style={{ accentColor:'#7c3aed' }}/> {wt}
                  </label>
                ))}
              </div>
            </div>
            {form.warranty_type === 'Under Warranty' && (
              <div style={{ padding:'16px 18px', background:'var(--accentbg)', borderRadius:12, border:'1.5px solid var(--border2)' }}>
                <label style={{ ...S.label, marginBottom:10, display:'block' }}>🛡️ Proof of Warranty</label>
                <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
                  {['Warranty Card','Invoice','Others'].map(opt => (
                    <label key={opt} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', padding:'8px 16px', borderRadius:10, background:form.warranty_proof.includes(opt)?'var(--accent)':'var(--bg)', border:`1.5px solid ${form.warranty_proof.includes(opt)?'var(--accent)':'var(--border)'}`, fontWeight:600, fontSize:13, color:form.warranty_proof.includes(opt)?'#fff':'var(--text1)', transition:'all .15s' }}>
                      <input type="checkbox" checked={form.warranty_proof.includes(opt)} onChange={()=>toggleArr('warranty_proof', opt)} style={{ accentColor:'#7c3aed' }}/> {opt}
                    </label>
                  ))}
                </div>
                <div>
                  <label style={S.label}>Warranty Card No. / Reference Code</label>
                  <input style={S.input} value={form.warranty_ref} onChange={e=>setF('warranty_ref',e.target.value)} placeholder="Enter warranty card number or reference (optional)" />
                </div>
              </div>
            )}
          </Section>

          {/* Section: Patient */}
          <Section title="Patient Details" icon="👤">
            <div style={row2}>
              <div>
                <label style={S.label}>Mobile Number</label>
                <input style={S.input} value={form.patient_mobile} onChange={e=>setF('patient_mobile',e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit mobile" required maxLength={10} autoFocus/>
              </div>
              <div>
                <label style={S.label}>Patient Name</label>
                <input style={S.input} value={form.patient_name} onChange={e=>setF('patient_name',e.target.value)} placeholder="Full name" required />
              </div>
            </div>
            {returning && (
              <div style={{ padding:'12px 16px', background:'linear-gradient(135deg,#059669,#16a34a)', borderRadius:10, color:'#fff', display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:20 }}>🔁</span>
                <div>
                  <div style={{ fontWeight:800, fontSize:13 }}>Returning Patient — {n(returning.total_repairs)} previous repair{returning.total_repairs!==1?'s':''}</div>
                  <div style={{ fontSize:11, opacity:.85, marginTop:2 }}>Details pre-filled from last visit · All fields are editable</div>
                </div>
              </div>
            )}
          </Section>

          {/* Section: Device */}
          <Section title="Device Details" icon="🎧">
            <div style={row2}>
              <div><label style={S.label}>Brand</label>
                <select style={S.input} value={form.ha_brand} onChange={e=>setF('ha_brand',e.target.value)} required>
                  <option value="">— Select brand —</option>
                  {HA_BRANDS.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div><label style={S.label}>Model</label><input style={S.input} value={form.ha_model} onChange={e=>setF('ha_model',e.target.value)} placeholder="Model name / number" /></div>
            </div>
            <div style={row2}>
              <div>
                <label style={S.label}>Serial No. — Left <span style={{color:'var(--danger)'}}>*</span> <span style={{fontWeight:400,fontSize:11,color:'var(--muted)'}}>at least one required</span></label>
                <input style={{ ...S.input, textTransform:'uppercase' }} value={form.serial_left} onChange={e=>{ const v=e.target.value.toUpperCase(); setF('serial_left',v); checkSerial('left',v); }} placeholder="Left ear serial" />
                {serialWarn.left && <div style={{ marginTop:5, fontSize:12, padding:'6px 10px', borderRadius:8, background:serialWarn.left.type==='error'?'var(--dangerBg)':'var(--amberBg)', color:serialWarn.left.type==='error'?'var(--danger)':'var(--amber)', fontWeight:600 }}>{serialWarn.left.msg}</div>}
              </div>
              <div>
                <label style={S.label}>Serial No. — Right</label>
                <input style={{ ...S.input, textTransform:'uppercase' }} value={form.serial_right} onChange={e=>{ const v=e.target.value.toUpperCase(); setF('serial_right',v); checkSerial('right',v); }} placeholder="Right ear serial" />
                {serialWarn.right && <div style={{ marginTop:5, fontSize:12, padding:'6px 10px', borderRadius:8, background:serialWarn.right.type==='error'?'var(--dangerBg)':'var(--amberBg)', color:serialWarn.right.type==='error'?'var(--danger)':'var(--amber)', fontWeight:600 }}>{serialWarn.right.msg}</div>}
              </div>
            </div>
            <div>
              <label style={S.label}>Type</label>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {['RIC','BTE','Pocket'].map(t=>(
                  <label key={t} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', padding:'8px 16px', borderRadius:10, background:form.ha_type.includes(t)?'var(--accentbg)':'var(--bg)', border:`1.5px solid ${form.ha_type.includes(t)?'var(--accent)':'var(--border)'}`, fontWeight:600, fontSize:13, color:form.ha_type.includes(t)?'var(--accent)':'var(--text1)' }}>
                    <input type="checkbox" checked={form.ha_type.includes(t)} onChange={()=>toggleArr('ha_type',t)} style={{ accentColor:'#7c3aed' }}/> {t}
                  </label>
                ))}
              </div>
            </div>
          </Section>

          {/* Section: Accessories */}
          <Section title="Accessories" icon="🔩" action={<button type="button" onClick={()=>setF('accessories',[...form.accessories,{name:'',serial:''}])} style={{ ...S.btnS, padding:'6px 14px', fontSize:12 }}>+ Add Row</button>}>
            {form.accessories.length===0 && <div style={{ color:'var(--muted)', fontSize:13, fontStyle:'italic', padding:'8px 0' }}>No accessories — click "Add Row" to include</div>}
            {form.accessories.map((acc,i)=>(
              <div key={i} style={{ display:'flex', gap:10, marginBottom:8 }}>
                <input style={{ ...S.input, flex:2 }} value={acc.name} onChange={e=>{ const a=[...form.accessories]; a[i]={...a[i],name:e.target.value}; setF('accessories',a); }} placeholder="Accessory name" />
                <input style={{ ...S.input, flex:2, textTransform:'uppercase' }} value={acc.serial} onChange={e=>{ const v=e.target.value.toUpperCase(); const a=[...form.accessories]; a[i]={...a[i],serial:v}; setF('accessories',a); }} placeholder="Serial no." />
                <button type="button" onClick={()=>setF('accessories',form.accessories.filter((_,idx)=>idx!==i))} style={{ ...S.btnD, padding:'9px 14px', flexShrink:0 }}>✕</button>
              </div>
            ))}
          </Section>

          {/* Section: Complaint */}
          <Section title="Complaint" icon="⚠️">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:8 }}>
              {COMPLAINTS.map(c=>(
                <label key={c} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer', padding:'10px 13px', borderRadius:10, background:form.complaint.includes(c)?'var(--accentbg)':'var(--bg)', border:`1.5px solid ${form.complaint.includes(c)?'var(--accent)':'var(--border)'}`, color:form.complaint.includes(c)?'var(--accent)':'var(--text1)', fontWeight:form.complaint.includes(c)?700:400, transition:'all .12s' }}>
                  <input type="checkbox" checked={form.complaint.includes(c)} onChange={()=>toggleArr('complaint',c)} style={{ accentColor:'#7c3aed' }}/> {c}
                </label>
              ))}
            </div>
            {form.complaint.includes('Others') && (
              <textarea style={{ ...S.input, marginTop:12, height:80, resize:'vertical' }} value={form.complaint_other} onChange={e=>setF('complaint_other',e.target.value)} placeholder="Describe the issue in detail…" />
            )}
          </Section>

          {/* Remarks */}
          <Section title="Remarks" icon="📝">
            <textarea style={{ ...S.input, height:90, resize:'vertical' }} value={form.remarks} onChange={e=>setF('remarks',e.target.value)} placeholder="Any additional notes for the repair team…" />
          </Section>

          {(serialWarn.left?.type==='warn' || serialWarn.right?.type==='warn') && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'var(--amberBg)', border:'1px solid var(--amber)', color:'var(--amber)', fontSize:13, fontWeight:700, marginBottom:8 }}>
              ⚠️ Re-repair detected — please verify this is intentional before submitting.
            </div>
          )}
          <div style={{ paddingTop:4 }}>
            <button type="submit" className="card-hover" style={{ ...S.btnP, padding:'14px 36px', fontSize:15, borderRadius:12 }} disabled={submitting}>
              {submitting ? '⏳ Submitting…' : '📤 Submit Repair Request'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({ title, icon, children, action }) {
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, paddingBottom:14, borderBottom:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'var(--accentbg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>{icon}</div>
          <span style={{ fontWeight:800, fontSize:14, color:'var(--text1)' }}>{title}</span>
        </div>
        {action}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// CLINIC HOME DASHBOARD
// ═════════════════════════════════════════════════════════════
function ClinicHome({ user, onNav, sharedTickets }) {
  const [stats, setStats] = useState(null);
  const [expiringWarranties, setExpiringWarranties] = useState([]);
  useEffect(()=>{
    api('reports.php?warranty_only=1')
      .then(d=>setExpiringWarranties((d?.warranty_tracker||[]).filter(r=>+r.days_remaining>=0&&+r.days_remaining<=30)))
      .catch(()=>{});
  },[]);
  const quote = QUOTES[Math.floor(Date.now()/86400000) % QUOTES.length];
  const today = new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  // Recent repairs come from the shared pre-fetched tickets — no extra network call
  const recentTickets = sharedTickets ? sharedTickets.slice(0, 5) : [];
  const awaitingDecision = sharedTickets ? sharedTickets.filter(t => t.status === 'Estimate Received' || t.status === 'Patient Informed') : [];

  useEffect(() => {
    api('tickets.php?action=stats')
      .then(s => setStats(s && typeof s === 'object' ? s : {}))
      .catch(()=> setStats({}));
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const statCards = stats ? [
    { icon:'📅', label:'Today\'s Repairs',    value: stats.today         || 0, color:'#7c3aed' },
    { icon:'📆', label:'This Month',           value: stats.this_month    || 0, color:'#2563eb' },
    { icon:'🔧', label:'Pending',              value: stats.pending       || 0, color:'#d97706' },
    { icon:'📦', label:'In Transit',           value: stats.in_transit    || 0, color:'#0891b2' },
    { icon:'💰', label:'Awaiting Decision',    value: stats.awaiting_decision || 0, color:'#f97316' },
    { icon:'🚚', label:'Returning to Clinic',  value: stats.returning     || 0, color:'#059669' },
    { icon:'✅', label:'Delivered This Month', value: stats.delivered_month || 0, color:'#16a34a' },
    { icon:'📊', label:'All Time',             value: stats.total_all_time || 0, color:'#6d28d9' },
  ] : [];

  return (
    <div className="fadeUp">
      {/* Welcome banner */}
      <div style={{ background:'linear-gradient(135deg,#1e1b4b,#4c1d95,#7c3aed)', borderRadius:20, padding:'28px 32px', marginBottom:28, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,.04)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-30, right:80, width:150, height:150, borderRadius:'50%', background:'rgba(255,255,255,.06)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:11, color:'#a78bfa', fontWeight:700, letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>{today}</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:26, color:'#fff', fontWeight:700, marginBottom:4 }}>{greeting()}, {user.name?.split(' ')[0] || user.username}!</div>
          <div style={{ fontSize:13, color:'#c4b5fd', marginBottom:22 }}>{user.clinic_label} — SoundLife Service Portal</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <button onClick={()=>onNav('new')} style={{ background:'#fff', color:'#7c3aed', border:'none', borderRadius:10, padding:'10px 22px', fontWeight:800, fontSize:14, cursor:'pointer' }}>➕ New Repair</button>
            <button onClick={()=>onNav('active')} style={{ background:'rgba(255,255,255,.15)', color:'#fff', border:'1.5px solid rgba(255,255,255,.3)', borderRadius:10, padding:'10px 22px', fontWeight:700, fontSize:14, cursor:'pointer' }}>🔧 View Active</button>
            <button onClick={()=>onNav('search')} style={{ background:'rgba(255,255,255,.15)', color:'#fff', border:'1.5px solid rgba(255,255,255,.3)', borderRadius:10, padding:'10px 22px', fontWeight:700, fontSize:14, cursor:'pointer' }}>🔍 Search Patient</button>
          </div>
        </div>
      </div>

      {/* Stat grid */}
      {!stats ? (
        <LoadingState />
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14, marginBottom:28 }}>
          {statCards.map(sc => <StatCard key={sc.label} icon={sc.icon} value={sc.value} label={sc.label} color={sc.color} />)}
        </div>
      )}

      {/* Awaiting decision alert */}
      {awaitingDecision.length > 0 && (
        <div style={{ background:'linear-gradient(135deg,#fff7ed,#fef3c7)', border:'1.5px solid #f97316', borderRadius:14, padding:'14px 20px', marginBottom:24, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>⏳</span>
            <div>
              <div style={{ fontWeight:800, fontSize:14, color:'#92400e' }}>Action Required — Estimate Decision</div>
              <div style={{ fontSize:12, color:'#b45309', marginTop:2 }}>
                {awaitingDecision.length} repair{awaitingDecision.length > 1 ? 's' : ''} waiting for patient approval: {awaitingDecision.map(t => t.repair_no).join(', ')}
              </div>
            </div>
          </div>
          <button onClick={() => onNav('active')} style={{ ...S.btnP, background:'#f97316', padding:'8px 18px', fontSize:12, flexShrink:0 }}>View →</button>
        </div>
      )}

      {/* Warranty expiring soon */}
      {expiringWarranties.length > 0 && (
        <div style={{ background:'linear-gradient(135deg,#fff1f2,#fee2e2)', border:'1.5px solid #fca5a5', borderRadius:14, padding:'14px 20px', marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <span style={{ fontSize:20 }}>🛡️</span>
            <div>
              <div style={{ fontWeight:800, fontSize:14, color:'#dc2626' }}>Warranty Expiring Soon</div>
              <div style={{ fontSize:12, color:'#b91c1c', marginTop:1 }}>{expiringWarranties.length} patient{expiringWarranties.length>1?'s':''} · recall them to clinic</div>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {expiringWarranties.map((r,i)=>{
              const d = +r.days_remaining;
              const col = d<=7?'#dc2626':d<=15?'#d97706':'#b45309';
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#fff', borderRadius:10, padding:'10px 14px', gap:10, flexWrap:'wrap', border:'1px solid #fecaca' }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--text1)' }}>{r.patient_name}</div>
                    <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>📱 {r.patient_mobile} · {r.ha_brand} {r.ha_model} · {r.warranty_months}m warranty</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ ...S.pill(col), fontSize:11, fontWeight:800 }}>⚠️ {d}d left</div>
                    <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>Expires {r.expiry_date}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent repairs */}
      {recentTickets.length > 0 && (
        <div style={{ ...S.card, marginBottom:28 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ fontWeight:800, fontSize:15, color:'var(--text1)' }}>🕒 Recent Repairs</div>
            <button onClick={()=>onNav('active')} style={{ ...S.btnS, padding:'5px 14px', fontSize:12 }}>View All →</button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {recentTickets.map(t => (
              <div key={t.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'var(--bg3)', borderRadius:10, gap:12, flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:STATUS_COLOR[t.status]||'#ccc', flexShrink:0 }}/>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:'var(--text1)' }}>{t.repair_no}</div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>{t.patient_name} · {t.ha_brand}</div>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#666'), fontSize:10 }}>{STATUS_ICON[t.status]} {t.status}</span>
                  <span style={{ fontSize:11, color:'var(--muted)' }}>{daysSince(t.repair_date)}d</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Thought footer */}
      <div style={{ padding:'18px 24px', background:'linear-gradient(135deg,#7c3aed10,#7c3aed04)', border:'1px solid #7c3aed18', borderRadius:14, display:'flex', alignItems:'center', gap:18 }}>
        <div style={{ fontSize:22, flexShrink:0 }}>💬</div>
        <div>
          <div style={{ fontSize:10, color:'#7c3aed', fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:6 }}>Today's Thought</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:15, color:'var(--text1)', fontStyle:'italic', lineHeight:1.5 }}>"{quote.q}"</div>
          <div style={{ fontSize:11, color:'var(--muted)', marginTop:5 }}>{quote.a}</div>
        </div>
      </div>
    </div>
  );
}

function HistoryList({ tickets }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {tickets.map(t => {
        const open = expanded === t.id;
        return (
          <div key={t.id} style={{ ...S.card, padding:0, overflow:'hidden' }}>
            {/* Summary row — always visible */}
            <div onClick={() => setExpanded(open ? null : t.id)} style={{ padding:'14px 18px', display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', cursor:'pointer' }}>
              <div style={{ flex:1, minWidth:200 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:'var(--accent)', fontFamily:'monospace' }}>{t.repair_no}</span>
                  <span style={{ ...S.pill('#059669'), fontSize:10 }}>✅ Delivered</span>
                  {t.warranty_type && <span style={{ ...S.pill('#7c3aed'), fontSize:10 }}>{t.warranty_type}</span>}
                </div>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--text1)' }}>{t.patient_name}</div>
                <div style={{ fontSize:11, color:'var(--muted)' }}>{t.patient_mobile} · {t.ha_brand} {t.ha_model}</div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:14, flexShrink:0 }}>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:11, color:'var(--muted)', marginBottom:2 }}>Repair date</div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text1)' }}>{fmtDate(t.repair_date)}</div>
                  {t.serial_left && <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>L: {t.serial_left}</div>}
                  {t.serial_right && <div style={{ fontSize:10, color:'var(--muted)' }}>R: {t.serial_right}</div>}
                </div>
                <div style={{ fontSize:16, color:'var(--muted)', transition:'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</div>
              </div>
            </div>
            {/* Expanded details */}
            {open && (
              <div style={{ borderTop:'1px solid var(--border)', padding:'14px 18px', background:'var(--bg3)', display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
                {t.ha_type && <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>Type</div><div style={{ fontSize:12, color:'var(--text1)' }}>{t.ha_type}</div></div>}
                {t.sending_to && <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>Sent To</div><div style={{ fontSize:12, color:'var(--text1)' }}>{t.sending_to}</div></div>}
                {t.complaint && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>Complaint</div><div style={{ fontSize:12, color:'var(--text1)' }}>{t.complaint}</div></div>}
                {t.complaint_other && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>Details</div><div style={{ fontSize:12, color:'var(--text1)' }}>{t.complaint_other}</div></div>}
                {t.remarks && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', marginBottom:3 }}>Remarks</div><div style={{ fontSize:12, color:'var(--text1)', fontStyle:'italic' }}>{t.remarks}</div></div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// CLINIC REPAIR HISTORY (archived / delivered)
// ═════════════════════════════════════════════════════════════
function ClinicHistory({ user }) {
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter]   = useState('');

  useEffect(() => {
    api('tickets.php?archived=1')
      .then(d => setTickets(Array.isArray(d) ? d : []))
      .catch(() => setTickets([]));
  }, []);

  const filtered = useMemo(() => {
    if (!tickets) return [];
    if (!filter) return tickets;
    const q = filter.toLowerCase();
    return tickets.filter(t =>
      t.repair_no.toLowerCase().includes(q) ||
      t.patient_name.toLowerCase().includes(q) ||
      t.patient_mobile.includes(q) ||
      (t.serial_left||'').toLowerCase().includes(q) ||
      (t.serial_right||'').toLowerCase().includes(q)
    );
  }, [tickets, filter]);

  if (tickets === null) return <LoadingState />;

  return (
    <div className="fadeUp">
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:20, flexWrap:'wrap' }}>
        <input
          style={{ ...S.input, maxWidth:320, flex:1 }}
          placeholder="Search by name, mobile, serial, repair no…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <div style={{ fontSize:12, color:'var(--muted)', whiteSpace:'nowrap' }}>{filtered.length} delivered repairs</div>
      </div>
      {filtered.length === 0 ? (
        <EmptyState icon="📦" title="No delivered repairs" sub="Completed and delivered repairs will appear here." />
      ) : (
        <HistoryList tickets={filtered} />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// ACTIVE REPAIRS
// ═════════════════════════════════════════════════════════════
function ActiveRepairs({ user, csrf, showToast, sharedTickets, ticketsLoading, onReload }) {
  const [actionModal, setActionModal] = useState(null);
  const [filter, setFilter] = useState('');
  const [printTicket, setPrintTicket] = useState(null);
  const quote = QUOTES[Math.floor(Date.now()/86400000) % QUOTES.length];

  const tickets = useMemo(() => sharedTickets ?? [], [sharedTickets]);
  const loading = sharedTickets === null && ticketsLoading;

  const filtered = useMemo(()=>{
    if(!filter) return tickets;
    const q = filter.toLowerCase();
    return tickets.filter(t => t.repair_no.toLowerCase().includes(q) || t.patient_name.toLowerCase().includes(q) || t.patient_mobile.includes(q) || (t.serial_left||'').toLowerCase().includes(q) || (t.serial_right||'').toLowerCase().includes(q));
  },[tickets,filter]);

  async function doAction(ticket, action, extra={}) {
    try {
      if (action === 'delete_ticket') {
        if (!window.confirm(`Delete ${ticket.repair_no}? This cannot be undone.`)) return;
        await api(`tickets.php?id=${ticket.id}`, { method:'DELETE', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ csrf_token:csrf }) });
        showToast(`${ticket.repair_no} deleted`);
        setActionModal(null);
        if (onReload) onReload();
        return;
      }
      await api(`tickets.php?id=${ticket.id}`,{ method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ action, csrf_token:csrf, ...extra }) });
      showToast('Status updated successfully');
      setActionModal(null);
      if (onReload) onReload();
    } catch(e) { showToast(e.message,'error'); }
  }

  return (
    <div className="fadeUp">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={S.pageTitle}>Active Repairs</div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:5, flexWrap:'wrap' }}>
            <span style={{ fontSize:13, color:'var(--muted)', fontWeight:600 }}>{n(tickets.length)} repair{tickets.length!==1?'s':''} in progress</span>
            {tickets.filter(t=>t.delay_alert===2).length > 0 && (
              <span style={{ background:'#fee2e2', color:'#dc2626', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>
                🚨 {tickets.filter(t=>t.delay_alert===2).length} critical
              </span>
            )}
            {tickets.filter(t=>t.delay_alert===1).length > 0 && (
              <span style={{ background:'#fef9c3', color:'#b45309', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>
                ⚠️ {tickets.filter(t=>t.delay_alert===1).length} delayed
              </span>
            )}
            {tickets.filter(t=>t.status==='Estimate Received'||t.status==='Patient Informed').length > 0 && (
              <span style={{ background:'var(--amberBg)', color:'var(--amber)', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>
                ⏳ {tickets.filter(t=>t.status==='Estimate Received'||t.status==='Patient Informed').length} awaiting decision
              </span>
            )}
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <input style={{ ...S.input, width:220 }} value={filter} onChange={e=>setFilter(e.target.value)} placeholder="🔍 Search repairs…" />
          <button onClick={() => onReload()} style={S.btnS} disabled={ticketsLoading}>
            <span style={{ display:'inline-block', animation: ticketsLoading ? 'spin 0.7s linear infinite' : 'none' }}>↻</span>
            {ticketsLoading ? ' Refreshing…' : ' Refresh'}
          </button>
        </div>
      </div>

      {loading ? <LoadingState /> : filtered.length===0 ? (
        <EmptyState icon="🎉" title="All clear!" sub="No active repairs found." />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          {filtered.map(t=><TicketCard key={t.id} ticket={t} user={user} onAction={setActionModal} onPrint={t => setPrintTicket(t)} csrf={csrf} />)}
        </div>
      )}
      {actionModal && <ActionModal ticket={actionModal.ticket} type={actionModal.type} csrf={csrf} onClose={()=>setActionModal(null)} onDone={(a,e)=>doAction(actionModal.ticket,a,{...e,...(actionModal.decision?{decision:actionModal.decision}:{}),...(actionModal.decision_left?{decision_left:actionModal.decision_left}:{}),...(actionModal.decision_right?{decision_right:actionModal.decision_right}:{})})} />}
      {printTicket && <PrintJobCard data={printTicket} onClose={()=>setPrintTicket(null)} />}
      <div style={{ marginTop:32, padding:'18px 24px', background:'linear-gradient(135deg,#7c3aed10,#7c3aed04)', border:'1px solid #7c3aed18', borderRadius:14, display:'flex', alignItems:'center', gap:18 }}>
        <div style={{ fontSize:22, flexShrink:0 }}>💬</div>
        <div>
          <div style={{ fontSize:10, color:'#7c3aed', fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:6 }}>Today's Thought</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:15, color:'var(--text1)', fontStyle:'italic', lineHeight:1.5 }}>"{quote.q}"</div>
          <div style={{ fontSize:11, color:'var(--muted)', marginTop:5 }}>{quote.a}</div>
        </div>
      </div>
    </div>
  );
}

function StatusTimeline({ status, sending_to, days, milestoneDates, repairDate }) {
  const isRejected = status === 'Patient Rejected';
  let flow = isRejected
    ? [...TIMELINE_FLOW.slice(0, TIMELINE_FLOW.indexOf('Patient Approved') + 1)]
    : [...TIMELINE_FLOW];
  if (sending_to === 'Direct Company') flow = flow.filter(s => !['Courier Sent','Received at HO'].includes(s));
  const idx = isRejected ? flow.length : flow.indexOf(status);
  const short = {
    'Form Submitted':    'Submitted',
    'Courier Sent':      'Courier',
    'Received at HO':    sending_to==='Direct Company' ? 'At Co.' : 'At HO',
    'Sent to Company':   'At Co.',
    'Estimate Received': 'Estimate',
    'Patient Informed':  'Informed',
    'Patient Approved':  'Approved',
    'Under Repair':   'Under Repair',
    'Dispatched to Branch':   'Dispatched',
    'Received at Clinic':'At Clinic',
    'Delivered to Patient':'Delivered',
  };
  // Compute days-from-repair-date for a given milestone date
  const stepDays = (s) => {
    if (!milestoneDates || !repairDate || !milestoneDates[s]) return null;
    const diff = Math.round((new Date(milestoneDates[s]) - new Date(repairDate)) / 86400000);
    return diff;
  };
  return (
    <div style={{ marginTop:14, overflowX:'auto', paddingBottom:2 }}>
      <div style={{ display:'flex', alignItems:'center', minWidth:'max-content', gap:0 }}>
        {flow.map((s, i) => {
          const done = i < idx;
          const cur  = i === idx;
          const col  = cur ? (STATUS_COLOR[s]||'#7c3aed') : done ? '#059669' : 'var(--border2)';
          const sd   = done ? stepDays(s) : null;
          return (
            <React.Fragment key={s}>
              {i > 0 && <div style={{ width:18, height:2, background: done?'#059669':'var(--border)', flexShrink:0 }} />}
              <div title={s} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                <div style={{ width:18, height:18, borderRadius:'50%', background: cur?col:done?'#059669':'var(--bg3)', border:`2px solid ${col}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, color:'#fff', fontWeight:900, flexShrink:0 }}>
                  {done?'✓':cur?'●':''}
                </div>
                <div style={{ fontSize:8, color: cur?col:done?'#059669':'var(--muted2)', fontWeight: cur?800:400, whiteSpace:'nowrap', maxWidth:40, textAlign:'center', lineHeight:1.1 }}>{short[s]}</div>
                {cur && days != null && <div style={{ fontSize:8, fontWeight:700, color: days>10?'#dc2626':days>5?'#d97706':col, whiteSpace:'nowrap' }}>{days}d</div>}
                {done && sd != null && <div style={{ fontSize:8, fontWeight:600, color:'#059669', whiteSpace:'nowrap' }}>Day {sd}</div>}
              </div>
            </React.Fragment>
          );
        })}
        {isRejected && (
          <>
            <div style={{ width:18, height:2, background:'#dc2626', flexShrink:0 }} />
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
              <div style={{ width:18, height:18, borderRadius:'50%', background:'#dc2626', border:'2px solid #dc2626', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff', fontWeight:900, flexShrink:0 }}>✗</div>
              <div style={{ fontSize:8, color:'#dc2626', fontWeight:800, whiteSpace:'nowrap' }}>Rejected</div>
              {(() => { const sd = stepDays('Patient Rejected'); return sd != null ? <div style={{ fontSize:8, fontWeight:600, color:'#dc2626', whiteSpace:'nowrap' }}>Day {sd}</div> : days != null ? <div style={{ fontSize:8, fontWeight:700, color:'#dc2626', whiteSpace:'nowrap' }}>{days}d</div> : null; })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TicketCard({ ticket:t, user, onAction, onPrint, csrf }) {
  const days = daysSince(t.repair_date, t.delivered_to_patient || null);
  const slaRemaining = SLA_DAYS - days;
  const slaStatus = t.status === 'Delivered to Patient' ? null : slaRemaining < 0 ? 'over' : slaRemaining <= 3 ? 'warn' : null;
  const isDelay = t.delay_alert > 0;
  const borderCol = t.delay_alert===2 ? 'var(--danger)' : t.delay_alert===1 ? 'var(--amber)' : (STATUS_COLOR[t.status]||'var(--border)');
  const canAct = user?.role !== 'owner';
  const _ms = t.milestone_dates ? (() => { try { return JSON.parse(t.milestone_dates); } catch { return {}; } })() : {};
  const _statusDate = _ms[t.status];
  const daysAtStatus = _statusDate ? Math.floor((Date.now() - new Date(_statusDate)) / 86400000) : null;
  const isOverdue = daysAtStatus !== null && daysAtStatus >= 7 && t.status !== 'Delivered to Patient';

  return (
    <div className="card-hover" style={{ ...S.card, borderLeft:`4px solid ${borderCol}`, padding:'18px 22px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{ fontWeight:900, fontSize:15, color:'var(--text1)' }}>{t.repair_no}</span>
            {t.is_re_repair ? <span style={S.pill('#dc2626')}>RE-REPAIR</span> : null}
            {isDelay && <span style={S.pill(t.delay_alert===2?'#dc2626':'#d97706')}>{t.delay_alert===2?'🔴 Critical Delay':'🟡 Delayed'}</span>}
            {isOverdue && <span style={{ ...S.pill('#b45309'), fontSize:10 }}>⏰ {daysAtStatus}d at {t.status}</span>}
          </div>
          <div style={{ fontSize:13, color:'var(--muted)', marginTop:5, display:'flex', gap:16, flexWrap:'wrap' }}>
            <span>👤 {t.patient_name}</span>
            <span>📱 {t.patient_mobile}</span>
            <span>🎧 {t.ha_brand} {t.ha_model}</span>
            <span>📅 {fmtDate(t.repair_date)} · <strong>{days}d</strong> elapsed{slaStatus==='over'&&<span style={{ marginLeft:6, background:'#fee2e2', color:'#dc2626', borderRadius:6, padding:'1px 7px', fontSize:11, fontWeight:700 }}>⚠️ {Math.abs(slaRemaining)}d over SLA</span>}{slaStatus==='warn'&&<span style={{ marginLeft:6, background:'#fef9c3', color:'#d97706', borderRadius:6, padding:'1px 7px', fontSize:11, fontWeight:700 }}>⏱ {slaRemaining}d left</span>}</span>
          </div>
          <div style={{ fontSize:12, color:'var(--muted2)', marginTop:4 }}>
            {t.warranty_type}
            {t.sending_to==='Direct Company' && t.direct_company && <span style={{ marginLeft:8, ...S.pill('#7c3aed'), fontSize:10 }}>🏭 Direct → {t.direct_company}</span>}
            {t.warranty_proof && <span style={{ marginLeft:8, ...S.pill('#059669'), fontSize:10 }}>🛡️ {t.warranty_proof}</span>}
            {t.warranty_ref && <span style={{ marginLeft:6, fontSize:11, color:'var(--muted)' }}>· Ref: <code style={{ fontSize:11 }}>{t.warranty_ref}</code></span>}
          </div>
        </div>
        <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#666'), fontSize:12, padding:'5px 14px' }}>{STATUS_ICON[t.status]} {t.status}</span>
      </div>

      {(t.out_company||t.ret_company) && (
        <div style={{ marginTop:12, display:'flex', gap:16, flexWrap:'wrap', padding:'10px 14px', background:'var(--bg3)', borderRadius:10, fontSize:12, color:'var(--muted)' }}>
          {t.out_company && <span>📦 Out: <strong>{t.out_company}</strong> / {t.out_tracking}</span>}
          {t.ret_company && <span>🚚 Return: <strong>{t.ret_company}</strong> / {t.ret_tracking}</span>}
        </div>
      )}
      {t.company_report && (
        <div style={{ marginTop:10, padding:'12px 14px', background: t.company_report_type==='FOC' ? 'var(--greenBg)' : 'var(--amberBg)', borderRadius:10, fontSize:13 }}>
          <div style={{ fontWeight:700, color: t.company_report_type==='FOC' ? 'var(--green)' : 'var(--amber)', marginBottom:4, display:'flex', flexWrap:'wrap', alignItems:'center', gap:6 }}>
            {t.company_report_type==='FOC' ? '✅ FOC — Free of Charge' : (() => {
              const hasLR = t.amount_left > 0 || t.amount_right > 0;
              const decCol = d => d==='Approved'?'#059669':d==='Rejected'?'#dc2626':'#d97706';
              return hasLR ? (
                <span style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:6 }}>
                  💰 Estimate:
                  {t.amount_left > 0 && (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      <span style={{ background:'var(--blueBg)', color:'var(--blue)', borderRadius:6, padding:'2px 8px', fontSize:12 }}>L ₹{Number(t.amount_left).toLocaleString('en-IN')}</span>
                      {t.decision_left && <span style={{ ...S.pill(decCol(t.decision_left)), fontSize:10 }}>{t.decision_left}</span>}
                    </span>
                  )}
                  {t.amount_left > 0 && t.amount_right > 0 && <span style={{ color:'var(--muted)' }}>+</span>}
                  {t.amount_right > 0 && (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      <span style={{ background:'var(--blueBg)', color:'var(--blue)', borderRadius:6, padding:'2px 8px', fontSize:12 }}>R ₹{Number(t.amount_right).toLocaleString('en-IN')}</span>
                      {t.decision_right && <span style={{ ...S.pill(decCol(t.decision_right)), fontSize:10 }}>{t.decision_right}</span>}
                    </span>
                  )}
                  {t.amount_left > 0 && t.amount_right > 0 && (
                    <span style={{ fontSize:11, fontWeight:600, color:'var(--muted)' }}>= ₹{Number((parseFloat(t.amount_left)||0)+(parseFloat(t.amount_right)||0)).toLocaleString('en-IN')}</span>
                  )}
                </span>
              ) : <span>💰 Estimate: ₹{Number(t.estimate_amount).toLocaleString('en-IN')}</span>;
            })()}
            {t.warranty_months && <span style={{ fontWeight:600, fontSize:11 }}>· {t.warranty_months}m warranty</span>}
            {!t.amount_left && !t.amount_right && t.estimate_amount && t.patient_decision && <span style={{ fontWeight:400 }}>· Patient: <strong>{t.patient_decision}</strong></span>}
          </div>
          <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.5 }}>{t.company_report}</div>
        </div>
      )}
      {t.outcome_note && (
        <div style={{ marginTop:10, padding:'10px 14px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, fontSize:13 }}>
          <div style={{ fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:.5, marginBottom:4, color:'#16a34a' }}>📋 Work Done</div>
          <div style={{ color:'#15803d', lineHeight:1.5 }}>{t.outcome_note}</div>
        </div>
      )}

      <StatusTimeline status={t.status} sending_to={t.sending_to} days={days} milestoneDates={t.milestone_dates ? (() => { try { return JSON.parse(t.milestone_dates); } catch { return null; } })() : null} repairDate={t.repair_date} />
      {/* ── Action buttons: ONE primary CTA + secondary row ── */}
      <div style={{ marginTop:14 }}>
        {/* Primary CTA — one big obvious next step */}
        {canAct && (() => {
          const big = { ...S.btnP, width:'100%', padding:'11px 18px', fontSize:14, fontWeight:800, justifyContent:'center', marginBottom:8 };
          if (t.status==='Form Submitted' && user?.role!=='clinic')
            return <button style={{...big,background:'linear-gradient(135deg,#059669,#16a34a)'}} onClick={()=>onAction({ticket:t,type:'confirm_received_ho'})}>{t.sending_to==='Direct Company' ? '🏭 Mark Sent to Company' : '🏢 Mark Received at HO'}</button>;
          if (t.status==='Form Submitted')
            return <button style={{...big}} onClick={()=>onAction({ticket:t,type:'send_courier'})}>📦 Send Courier</button>;
          if (t.status==='Courier Sent' && user?.role!=='clinic')
            return <button style={{...big,background:'linear-gradient(135deg,#059669,#16a34a)'}} onClick={()=>onAction({ticket:t,type:'confirm_received_ho'})}>{t.sending_to==='Direct Company' ? '🏭 Company Received' : '🏢 Mark Received at HO'}</button>;
          if (t.status==='Received at HO' && t.sending_to!=='Direct Company' && user?.role!=='clinic')
            return <button style={{...big,background:'linear-gradient(135deg,#0891b2,#06b6d4)'}} onClick={()=>onAction({ticket:t,type:'sent_to_company'})}>🏭 Sent to Company</button>;
          if ((t.status==='Sent to Company'||(t.status==='Received at HO'&&t.sending_to==='Direct Company')) && user?.role!=='clinic')
            return <div style={{ display:'flex', gap:8 }}>
              <button style={{...big}} onClick={()=>onAction({ticket:t,type:'submit_estimate_report'})}>💰 Estimate Report</button>
              <button style={{...big, background:'linear-gradient(135deg,#059669,#16a34a)'}} onClick={()=>onAction({ticket:t,type:'submit_foc'})}>✅ FOC Report</button>
            </div>;
          if (t.status==='Estimate Received' && user?.role==='clinic')
            return <button style={{...big,background:'linear-gradient(135deg,#7c3aed,#6d28d9)'}} onClick={()=>onAction({ticket:t,type:'mark_patient_informed'})}>📞 Mark Patient Informed</button>;
          if (t.status==='Patient Informed') {
            const hasL = +t.amount_left > 0, hasR = +t.amount_right > 0;
            if (hasL && hasR) return (
              <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#059669,#16a34a)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Approved',decision_right:'Approved'})}>✅ Approve Both</button>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#2563eb,#3b82f6)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Approved',decision_right:'Rejected'})}>✅ Left Only</button>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#7c3aed,#8b5cf6)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Rejected',decision_right:'Approved'})}>✅ Right Only</button>
                <button style={{...S.btnD,flex:1,justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Rejected',decision_right:'Rejected'})}>❌ Reject All</button>
              </div>
            );
            if (hasL) return (
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#059669,#16a34a)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Approved'})}>✅ Approve Left HA</button>
                <button style={{...S.btnD,justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_left:'Rejected'})}>❌ Reject</button>
              </div>
            );
            if (hasR) return (
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#059669,#16a34a)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_right:'Approved'})}>✅ Approve Right HA</button>
                <button style={{...S.btnD,justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision_right:'Rejected'})}>❌ Reject</button>
              </div>
            );
            return (
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <button style={{...S.btnP,flex:1,background:'linear-gradient(135deg,#059669,#16a34a)',justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision:'Approved'})}>✅ Patient Approved</button>
                <button style={{...S.btnD,flex:1,justifyContent:'center'}} onClick={()=>doDirectAction(t,'patient_decision',{decision:'Rejected'})}>❌ Rejected</button>
              </div>
            );
          }
          if (t.status==='Dispatched to Branch')
            return <button style={{...big}} onClick={()=>onAction({ticket:t,type:'confirm_received_at_clinic'})}>📥 Confirm Received at Clinic</button>;
          if (t.status==='Received at Clinic')
            return <button style={{...big,background:'linear-gradient(135deg,#059669,#16a34a)'}} onClick={()=>onAction({ticket:t,type:'delivered_to_patient'})}>🎉 Delivered to Patient</button>;
          return null;
        })()}
        {t.status==='Received at Clinic' && (
          <HoldingNoteInline ticketId={t.id} initialNote={t.holding_note||''} csrf={csrf} />
        )}
        {/* Secondary row — print, whatsapp, delete */}
        <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
          {onPrint && <button style={{ ...S.btnS, padding:'7px 13px', fontSize:12 }} onClick={()=>onPrint(t)}>🖨️ Print</button>}
          <button title="Share tracking link on WhatsApp" style={{ background:'#25D366', color:'#fff', border:'none', borderRadius:8, padding:'7px 13px', fontWeight:700, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}
            onClick={()=>{
              const msg = buildWATrackMsg(t, t.patient_name);
              window.open(`https://wa.me/91${t.patient_mobile}?text=${encodeURIComponent(msg)}`, '_blank');
            }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Share
          </button>
          {t.status==='Form Submitted' && canAct && <button style={{ ...S.btnD, padding:'7px 13px', fontSize:12 }} onClick={()=>onAction({ticket:t,type:'delete_ticket'})}>🗑️ Delete</button>}
        </div>
      </div>
    </div>
  );
  function doDirectAction(ticket,action,extra){ onAction({ticket,type:action,...extra}); }
}

function ActionModal({ ticket, type, csrf, onClose, onDone }) {
  const defaultReportType = type === 'submit_estimate_report' ? 'Estimate'
    : type === 'submit_foc' ? 'FOC'
    : ticket?.company_report_type || 'FOC';
  const accessories = (() => { try { return typeof ticket?.accessories === 'string' ? JSON.parse(ticket.accessories || '[]') : (ticket?.accessories || []); } catch { return []; } })();
  const hasAccessories = accessories.length > 0;
  const [form, setForm] = useState({
    courier_company:'', tracking_no:'',
    amount_left: ticket?.amount_left ?? '', amount_right: ticket?.amount_right ?? '',
    amount_accessory: ticket?.amount_accessory ?? '',
    company_report: ticket?.company_report ?? '', report_type: defaultReportType,
    warranty_months: ticket?.warranty_months ?? '', eta_date:'',
    reason:'',
  });
  const needsCourier  = ['send_courier','dispatch_back'].includes(type);
  const needsReport   = ['submit_foc','submit_estimate_report','edit_estimate'].includes(type);
  const needsRevert   = type === 'revert_status';
  const showTypePicker = type === 'edit_estimate';
  const titles = {
    send_courier:'Enter Outward Courier Details',
    mark_patient_informed:'Confirm Patient Informed',
    confirm_received_at_clinic:'Confirm Received at Clinic',
    delivered_to_patient:'Confirm Delivered to Patient',
    confirm_received_ho:'Confirm Received at HO',
    sent_to_company:'Confirm Sent to Company',
    dispatch_back:'Dispatch to Branch — Enter Courier Details',
    submit_foc:'✅ FOC Report — No Estimate',
    submit_estimate_report:'💰 Estimate Report',
    edit_estimate:'✏️ Edit Estimate / Report',
    revert_status:'↩ Revert to Previous Status',
  };

  return (
    <Modal title={titles[type]||'Action'} onClose={onClose}>
      <div style={{ padding:'10px 14px', background:'var(--bg3)', borderRadius:10, marginBottom:18, fontSize:13, color:'var(--muted)' }}>
        Ticket: <strong style={{ color:'var(--text1)' }}>{ticket.repair_no}</strong> &nbsp;·&nbsp; {ticket.patient_name} &nbsp;·&nbsp; {ticket.ha_brand}
        {needsRevert && <span style={{ marginLeft:10, color:'#dc2626', fontWeight:700 }}>Current: {ticket.status}</span>}
      </div>
      <form onSubmit={e=>{e.preventDefault();
        if(needsCourier) onDone(type,{courier_company:form.courier_company,tracking_no:form.tracking_no});
        else if(needsReport) onDone(type==='edit_estimate'?'edit_estimate':'submit_company_report',{report_type:form.report_type,company_report:form.company_report,amount_left:form.amount_left,amount_right:form.amount_right,amount_accessory:form.amount_accessory,warranty_months:form.warranty_months,eta_date:form.eta_date});
        else if(needsRevert) onDone('revert_status',{reason:form.reason});
        else onDone(type,{});
      }} style={{ display:'flex', flexDirection:'column', gap:14 }}>
        {needsCourier && <>
          <div><label style={S.label}>Courier Company Name</label><input style={S.input} value={form.courier_company} onChange={e=>setForm(f=>({...f,courier_company:e.target.value}))} placeholder="e.g. Blue Dart, DTDC, FedEx" required /></div>
          <div><label style={S.label}>Tracking Number</label><input style={S.input} value={form.tracking_no} onChange={e=>setForm(f=>({...f,tracking_no:e.target.value}))} placeholder="AWB / tracking number" required /></div>
        </>}
        {needsReport && <>
          {showTypePicker && (
            <div>
              <label style={S.label}>Company Report Type</label>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                {['FOC','Estimate'].map(opt=>(
                  <label key={opt} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'10px 18px', borderRadius:10, border:`2px solid ${form.report_type===opt?'#7c3aed':'var(--border)'}`, background:form.report_type===opt?'var(--accentbg)':'var(--card)', flex:1, justifyContent:'center', fontWeight:700, fontSize:14, color:form.report_type===opt?'var(--accent)':'var(--muted)' }}>
                    <input type="radio" name="report_type" value={opt} checked={form.report_type===opt} onChange={()=>setForm(f=>({...f,report_type:opt}))} style={{ display:'none' }}/>
                    {opt === 'FOC' ? '✅ FOC (Free of Charge)' : '💰 Estimate (Chargeable)'}
                  </label>
                ))}
              </div>
            </div>
          )}
          {form.report_type==='Estimate' && type !== 'submit_foc' && (() => {
            const total = (parseFloat(form.amount_left)||0) + (parseFloat(form.amount_right)||0) + (parseFloat(form.amount_accessory)||0);
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={S.label}>Left HA Amount (₹) <span style={{ fontWeight:400, color:'var(--muted)' }}>— leave blank if N/A</span></label>
                    <input style={S.input} type="number" min="0" step="0.01" value={form.amount_left} onChange={e=>setForm(f=>({...f,amount_left:e.target.value}))} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={S.label}>Right HA Amount (₹) <span style={{ fontWeight:400, color:'var(--muted)' }}>— leave blank if N/A</span></label>
                    <input style={S.input} type="number" min="0" step="0.01" value={form.amount_right} onChange={e=>setForm(f=>({...f,amount_right:e.target.value}))} placeholder="0.00" />
                  </div>
                </div>
                {hasAccessories && (
                  <div>
                    <label style={S.label}>Accessory Amount (₹) <span style={{ fontWeight:400, color:'var(--muted)' }}>— {accessories.map(a=>a.name).filter(Boolean).join(', ')}</span></label>
                    <input style={S.input} type="number" min="0" step="0.01" value={form.amount_accessory} onChange={e=>setForm(f=>({...f,amount_accessory:e.target.value}))} placeholder="0.00" />
                  </div>
                )}
                {total > 0 && (
                  <div style={{ padding:'8px 14px', background:'var(--accentbg)', borderRadius:8, fontSize:13, fontWeight:700, color:'var(--accent)' }}>
                    Total Estimate: ₹{total.toLocaleString('en-IN', {minimumFractionDigits:2})}
                  </div>
                )}
                <div>
                  <label style={S.label}>Warranty After Repair</label>
                  <select style={S.input} value={form.warranty_months} onChange={e=>setForm(f=>({...f,warranty_months:e.target.value}))}>
                    <option value="">No Warranty</option>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="9">9 Months</option>
                    <option value="12">12 Months</option>
                  </select>
                </div>
              </div>
            );
          })()}
          <div>
            <label style={S.label}>Company Report / Remarks</label>
            <textarea style={{ ...S.input, height:100, resize:'vertical' }} value={form.company_report} onChange={e=>setForm(f=>({...f,company_report:e.target.value}))} placeholder={form.report_type==='FOC' ? 'Optional — leave blank for standard FOC remark' : 'Enter company diagnosis, damage notes, parts to be replaced, or any remarks from the lab…'} required={form.report_type!=='FOC'} />
          </div>
          <div>
            <label style={S.label}>Estimated Arrival Date <span style={{ fontWeight:400, color:'var(--muted)' }}>— optional, shown to patient on tracking page</span></label>
            <input style={S.input} type="date" value={form.eta_date} onChange={e=>setForm(f=>({...f,eta_date:e.target.value}))} />
          </div>
        </>}
        {needsRevert && (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ padding:'12px 14px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, fontSize:13, color:'#991b1b' }}>
              ⚠️ This will move the ticket back to its previous status. Any associated data for the current status will be cleared.
            </div>
            <div>
              <label style={S.label}>Reason for reverting <span style={{ fontWeight:400, color:'var(--muted)' }}>— optional</span></label>
              <input style={S.input} value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value}))} placeholder="e.g. Wrong ticket updated, incorrect estimate entered…" />
            </div>
          </div>
        )}
        {!needsCourier && !needsReport && !needsRevert && type !== 'delivered_to_patient' && (
          <div style={{ padding:'14px', background:'var(--bg3)', borderRadius:10, fontSize:14, color:'var(--text1)', textAlign:'center' }}>
            Confirm this action?
          </div>
        )}
        {type === 'delivered_to_patient' && (
          <div style={{ border:'2px solid #16a34a', borderRadius:12, padding:'16px', background:'#f0fdf4' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#166534', letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>🎉 Delivering to Patient</div>
            <div style={{ fontSize:18, fontWeight:800, color:'#15803d', marginBottom:4 }}>{ticket.patient_name}</div>
            <div style={{ fontSize:12, color:'#166534' }}>{ticket.repair_no} · {ticket.ha_brand} {ticket.ha_model}</div>
            <div style={{ marginTop:10, padding:'8px 12px', background:'#dcfce7', borderRadius:8, fontSize:12, color:'#166534', fontWeight:600 }}>
              ⚠️ This will archive the repair permanently. Make sure this is the correct patient.
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:10, paddingTop:4 }}>
          <button type="submit" style={needsRevert ? {...S.btnP, background:'#dc2626', borderColor:'#dc2626'} : type==='delivered_to_patient' ? {...S.btnP, background:'linear-gradient(135deg,#059669,#16a34a)', fontSize:15, padding:'12px 24px', fontWeight:700} : S.btnP}>
            {needsRevert ? '↩ Revert Status' : type==='delivered_to_patient' ? `✅ Yes, Deliver to ${ticket.patient_name.split(' ')[0]}` : 'Confirm'}
          </button>
          <button type="button" onClick={onClose} style={S.btnS}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ═════════════════════════════════════════════════════════════
// PATIENT SEARCH
// ═════════════════════════════════════════════════════════════
function PatientSearch({ user, csrf }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.length < 6) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(()=>{
      api(`patients_repair.php?q=${encodeURIComponent(q)}`).then(setResults).catch(()=>{}).finally(()=>setLoading(false));
    },300);
    return ()=>clearTimeout(t);
  },[q]);

  if (selected) return <PatientProfile patient={selected} csrf={csrf} onBack={()=>{ setSelected(null); }} />;

  return (
    <div className="fadeUp">
      {/* Hero search header */}
      <div style={{ background:'linear-gradient(135deg,#1e1b4b,#4c1d95,#7c3aed)', borderRadius:20, padding:'32px 36px', marginBottom:28, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,.04)', pointerEvents:'none' }}/>
        <div style={{ position:'absolute', bottom:-30, left:-30, width:150, height:150, borderRadius:'50%', background:'rgba(255,255,255,.06)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:11, color:'#a78bfa', fontWeight:700, letterSpacing:2, textTransform:'uppercase', marginBottom:8 }}>Repair History Lookup</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:26, color:'#fff', fontWeight:700, marginBottom:4 }}>Patient Search</div>
          <div style={{ fontSize:13, color:'#c4b5fd', marginBottom:24 }}>Search by mobile number or serial number to view complete repair history</div>
          {/* Search input */}
          <div style={{ position:'relative', maxWidth:480 }}>
            <span style={{ position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', fontSize:18, pointerEvents:'none' }}>🔍</span>
            <input
              style={{ width:'100%', padding:'14px 16px 14px 48px', fontSize:16, fontWeight:700, letterSpacing:1, background:'rgba(255,255,255,.12)', border:'1.5px solid rgba(255,255,255,.2)', borderRadius:14, color:'#fff', outline:'none', boxSizing:'border-box', backdropFilter:'blur(8px)', textTransform:'uppercase' }}
              value={q}
              onChange={e=>setQ(e.target.value.toUpperCase().slice(0,20))}
              placeholder="Mobile number or serial no…"
              maxLength={20}
              autoFocus
            />
            {q.length>0 && (
              <button onClick={()=>setQ('')} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,.2)', border:'none', borderRadius:8, width:28, height:28, cursor:'pointer', color:'#fff', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
            )}
          </div>
          {q.length>0 && q.length<6 && <div style={{ marginTop:10, fontSize:12, color:'#c4b5fd' }}>⏳ {6-q.length} more character{6-q.length!==1?'s':''} to start search…</div>}
          {loading && <div style={{ marginTop:10, fontSize:12, color:'#c4b5fd', display:'flex', alignItems:'center', gap:8 }}><span style={{ width:12, height:12, border:'2px solid #a78bfa', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite'}}/>Searching…</div>}
          {q.length>=6 && !loading && results.length>0 && <div style={{ marginTop:10, fontSize:12, color:'#a78bfa' }}>✓ {results.length} patient{results.length!==1?'s':''} found</div>}
        </div>
      </div>

      {/* Results */}
      {q.length>=6 && results.length===0 && !loading && (
        <EmptyState icon="🔍" title="No patient found" sub={`No patient found for "${q}" — try mobile number or serial no.`} />
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {results.map(p=>(
          <div key={p.id} className="card-hover" style={{ ...S.card, cursor:'pointer', display:'flex', alignItems:'center', gap:18, padding:'18px 24px' }} onClick={()=>setSelected(p)}>
            <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#7c3aed,#6d28d9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:900, color:'#fff', flexShrink:0 }}>
              {(p.name||'?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:800, fontSize:15, color:'var(--text1)' }}>{p.name}</div>
              <div style={{ fontSize:13, color:'var(--muted)', marginTop:3, display:'flex', gap:16, flexWrap:'wrap' }}>
                <span>📱 {p.mobile}</span>
                <span>📅 Since {fmtDate(p.created_at)}</span>
              </div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:24, fontWeight:900, color:'#7c3aed', lineHeight:1 }}>{n(p.total_repairs)}</div>
              <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5 }}>Repair{p.total_repairs!==1?'s':''}</div>
            </div>
            <span style={{ color:'var(--muted)', fontSize:18, marginLeft:4 }}>›</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PatientProfile({ patient:p, onBack, backLabel, csrf='' }) {
  const [expanded, setExpanded] = useState(null);
  const [printTicket, setPrintTicket] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [live, setLive] = useState(p); // local copy so edits reflect immediately
  const [liveTickets, setLiveTickets] = useState(p.tickets || []);
  const [editingTicket, setEditingTicket] = useState(null); // ticket id being edited
  const [tEdit, setTEdit] = useState({}); // fields for ticket edit form
  const [pmsHistory, setPmsHistory] = useState(null);
  const [pmsLoading, setPmsLoading] = useState(false);

  useEffect(()=>{
    if (!live.mobile) return;
    setPmsLoading(true);
    api(`patients_repair.php?pms_history=1&mobile=${encodeURIComponent(live.mobile)}`)
      .then(d=>{ setPmsHistory(d?.found ? d : null); setPmsLoading(false); })
      .catch(()=>{ setPmsLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[live.mobile]);
  const [tEditBusy, setTEditBusy] = useState(false);
  const [tEditErr, setTEditErr] = useState('');

  const startEdit = () => {
    setEditName(live.name); setEditMobile(live.mobile);
    setEditErr(''); setEditing(true);
  };
  const saveEdit = async () => {
    const name   = editName.trim();
    const mobile = editMobile.replace(/\D/g,'');
    if (!name)              { setEditErr('Name cannot be empty'); return; }
    if (mobile.length !== 10) { setEditErr('Mobile must be 10 digits'); return; }
    setEditBusy(true); setEditErr('');
    try {
      const res = await api(`patients_repair.php?id=${live.id}`, { method:'PUT', body: JSON.stringify({ name, mobile }) });
      if (res.error) { setEditErr(res.error); setEditBusy(false); return; }
      setLive(prev => ({ ...prev, name, mobile }));
      setEditing(false);
    } catch(e) { setEditErr('Save failed. Please try again.'); }
    setEditBusy(false);
  };

  const startTicketEdit = (t) => {
    const proof = t.warranty_proof ? t.warranty_proof.split(',').map(s=>s.trim()).filter(Boolean) : [];
    setTEdit({ serial_left: t.serial_left||'', serial_right: t.serial_right||'', warranty_type: t.warranty_type||'', ha_brand: t.ha_brand||'', ha_model: t.ha_model||'', warranty_proof: proof, warranty_ref: t.warranty_ref||'' });
    setTEditErr(''); setEditingTicket(t.id);
  };
  const saveTicketEdit = async (ticketId) => {
    setTEditBusy(true); setTEditErr('');
    try {
      const res = await api(`tickets.php?id=${ticketId}`, { method:'PUT', headers:{'X-CSRF-Token':csrf}, body: JSON.stringify({ action:'edit_details', csrf_token:csrf, ...tEdit }) });
      if (res.error) { setTEditErr(res.error); setTEditBusy(false); return; }
      const proofStr = (tEdit.warranty_proof||[]).join(', ') || null;
      setLiveTickets(prev => prev.map(t => t.id===ticketId ? { ...t, ...tEdit, warranty_proof: proofStr, warranty_ref: tEdit.warranty_type==='Under Warranty' ? tEdit.warranty_ref||null : null } : t));
      setEditingTicket(null);
    } catch(e) { setTEditErr('Save failed. Please try again.'); }
    setTEditBusy(false);
  };

  return (
    <>
    <div className="fadeUp">
      <button onClick={onBack} style={{ ...S.btnS, marginBottom:20 }}>{backLabel || '← Back to Search'}</button>
      <div style={{ ...S.card, marginBottom:18, borderLeft:'4px solid var(--accent)' }}>
        {editing ? (
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--text1)', marginBottom:12 }}>Edit Patient Details</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10, maxWidth:360 }}>
              <div>
                <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Patient Name</div>
                <input style={{ ...S.input, width:'100%' }} value={editName} onChange={e=>setEditName(e.target.value)} placeholder="Full name" autoFocus/>
              </div>
              <div>
                <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4 }}>Mobile Number</div>
                <input style={{ ...S.input, width:'100%' }} value={editMobile} onChange={e=>setEditMobile(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="10-digit mobile" maxLength={10}/>
              </div>
              {editErr && <div style={{ fontSize:12, color:'var(--danger)', fontWeight:600 }}>⚠ {editErr}</div>}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={saveEdit} disabled={editBusy} style={{ ...S.btnP, opacity:editBusy?0.6:1 }}>{editBusy?'Saving…':'Save'}</button>
                <button onClick={()=>setEditing(false)} style={S.btnS}>Cancel</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ fontSize:22, fontWeight:900, color:'var(--text1)' }}>{live.name}</div>
                <button onClick={startEdit} style={{ background:'none', border:'1px solid var(--border)', borderRadius:6, padding:'3px 10px', fontSize:11, color:'var(--muted)', cursor:'pointer' }}>✏️ Edit</button>
              </div>
              <div style={{ fontSize:14, color:'var(--muted)', marginTop:6, display:'flex', gap:20, flexWrap:'wrap' }}>
                <span>📱 {live.mobile}</span>
                <span>🔧 {n(live.total_repairs)} total repairs</span>
                <span>📅 Since {fmtDate(live.created_at)}</span>
              </div>
            </div>
            {live.frequent_serials?.length>0 && (
              <div style={{ padding:'10px 16px', background:'var(--dangerBg)', borderRadius:10, fontSize:13, color:'var(--danger)', fontWeight:700 }}>
                ⚠️ Frequent Repair<br/><span style={{ fontWeight:400, fontSize:12 }}>Serial(s): {live.frequent_serials.join(', ')} · 3+ times</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ fontSize:16, fontWeight:800, color:'var(--text1)', marginBottom:14 }}>Repair History <span style={{ fontSize:12, fontWeight:400, color:'var(--muted)' }}>· tap a card to see full details</span></div>
      {liveTickets.length===0 ? <EmptyState icon="📋" title="No repairs" sub="No repair history found for this patient." /> : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {liveTickets.map(t=>(
            <div key={t.id}>
              <div className="card-hover" style={{ ...S.card, borderLeft:`4px solid ${STATUS_COLOR[t.status]||'#ccc'}`, padding:'16px 22px', cursor:'pointer' }} onClick={()=>setExpanded(expanded===t.id?null:t.id)}>
                <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontWeight:800 }}>{t.repair_no}</span>
                      {t.is_re_repair && <span style={S.pill('#dc2626')}>RE-REPAIR</span>}
                      {t.clinic_label && <span style={{ fontSize:12, color:'var(--muted)' }}>@ {t.clinic_label}</span>}
                    </div>
                    <div style={{ fontSize:13, color:'var(--muted)', marginTop:5, display:'flex', gap:14, flexWrap:'wrap' }}>
                      <span>{t.ha_brand} {t.ha_model}</span>
                      <span>{t.warranty_type}</span>
                      <span>{fmtDate(t.repair_date)}</span>
                      {t.delivered_to_patient && <span>Delivered {fmtDate(t.delivered_to_patient)}{t.delay_days>0?` · ${t.delay_days}d delay`:''}</span>}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3 }}>
                      <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#666'), fontSize:12, padding:'5px 14px' }}>{STATUS_ICON[t.status]} {t.status}</span>
                      {(() => {
                        const ms = t.milestone_dates ? (() => { try { return JSON.parse(t.milestone_dates); } catch { return {}; } })() : {};
                        const statusTs = t.status === 'Form Submitted' ? t.repair_date : ms[t.status];
                        if (!statusTs) return null;
                        const daysAt = Math.floor((Date.now() - new Date(statusTs).getTime()) / 86400000);
                        const col = daysAt >= 7 ? '#dc2626' : daysAt >= 4 ? '#d97706' : 'var(--muted2)';
                        return (
                          <div style={{ fontSize:10, color:col, fontWeight:600, textAlign:'right' }}>
                            {fmtDate(statusTs)} · <span style={{ fontWeight:700 }}>{daysAt === 0 ? 'today' : `${daysAt}d here`}</span>
                          </div>
                        );
                      })()}
                    </div>
                    <span style={{ fontSize:16, color:'var(--muted)' }}>{expanded===t.id?'▲':'▼'}</span>
                  </div>
                </div>
              </div>
              {expanded===t.id && (
                <div className="fadeUp" style={{ ...S.card, borderLeft:`4px solid ${STATUS_COLOR[t.status]||'#ccc'}`, borderTop:'none', borderTopLeftRadius:0, borderTopRightRadius:0, padding:'18px 22px', background:'var(--bg3)' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16 }}>
                    <Field label="Repair No." value={t.repair_no} />
                    <Field label="Repair Date" value={fmtDate(t.repair_date)} />
                    <Field label="Brand / Model" value={`${t.ha_brand||''} ${t.ha_model||''}`.trim()||'—'} />
                    <Field label="HA Type" value={t.ha_type||'—'} />
                    <Field label="Warranty Type" value={t.warranty_type} />
                    <Field label="Serial (L)" value={t.serial_left||'—'} />
                    <Field label="Serial (R)" value={t.serial_right||'—'} />
                    {(() => { try { const acc = typeof t.accessories==='string' ? JSON.parse(t.accessories||'[]') : (t.accessories||[]); return acc.length>0 ? acc.map((a,i)=><Field key={i} label={`Accessory ${acc.length>1?i+1:''}`} value={`${a.name||'—'}${a.serial?` · ${a.serial}`:''}`} />) : null; } catch { return null; } })()}
                    <Field label="Clinic" value={t.clinic_label||'—'} />
                  </div>
                  {t.complaint && (
                    <div style={{ marginTop:14 }}>
                      <div style={S.label}>Complaint(s)</div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:6 }}>
                        {(t.complaint||'').split(',').map(c=><span key={c} style={{ ...S.pill('#7c3aed'), fontSize:11 }}>{c.trim()}</span>)}
                      </div>
                      {t.complaint_other && <div style={{ marginTop:6, fontSize:13, color:'var(--text2)' }}>{t.complaint_other}</div>}
                    </div>
                  )}
                  {t.remarks && (
                    <div style={{ marginTop:14 }}>
                      <div style={S.label}>Clinic Remarks</div>
                      <div style={{ marginTop:6, fontSize:13, color:'var(--text2)', lineHeight:1.6 }}>{t.remarks}</div>
                    </div>
                  )}
                  {t.company_report && (
                    <div style={{ marginTop:14, padding:'12px 16px', background: t.company_report_type==='FOC'?'var(--greenBg)':'var(--amberBg)', borderRadius:10 }}>
                      <div style={{ fontWeight:700, fontSize:13, color: t.company_report_type==='FOC'?'var(--green)':'var(--amber)', marginBottom:6 }}>
                        🏭 Company Lab Report — {t.company_report_type==='FOC' ? 'FOC (Free of Charge)' : (() => {
                          const hasLR = +t.amount_left > 0 || +t.amount_right > 0;
                          if (hasLR) {
                            const parts = [];
                            if (+t.amount_left  > 0) parts.push(`L ₹${Number(t.amount_left).toLocaleString('en-IN')}`);
                            if (+t.amount_right > 0) parts.push(`R ₹${Number(t.amount_right).toLocaleString('en-IN')}`);
                            return `Estimate ${parts.join(' + ')}`;
                          }
                          return `Estimate ₹${t.estimate_amount}`;
                        })()}
                      </div>
                      <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6 }}>{t.company_report}</div>
                    </div>
                  )}
                  {(() => {
                    const hasLR = +t.amount_left > 0 || +t.amount_right > 0;
                    const decCol = d => d==='Approved'?'#059669':d==='Rejected'?'#dc2626':'#d97706';
                    if (hasLR && (t.decision_left || t.decision_right)) return (
                      <div style={{ marginTop:10, fontSize:13, color:'var(--muted)', display:'flex', flexWrap:'wrap', alignItems:'center', gap:6 }}>
                        Patient decision:
                        {+t.amount_left  > 0 && t.decision_left  && <span style={{ fontWeight:700, color: decCol(t.decision_left)  }}>L: {t.decision_left}</span>}
                        {+t.amount_right > 0 && t.decision_right && <span style={{ fontWeight:700, color: decCol(t.decision_right) }}>R: {t.decision_right}</span>}
                      </div>
                    );
                    if (!hasLR && t.estimate_amount && t.patient_decision) return (
                      <div style={{ marginTop:10, fontSize:13, color:'var(--muted)' }}>Patient decision: <strong style={{ color:'var(--text1)' }}>{t.patient_decision}</strong></div>
                    );
                    return null;
                  })()}
                  {t.outcome_note && (
                    <div style={{ marginTop:10, padding:'10px 14px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, fontSize:13 }}>
                      <div style={{ fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:.5, marginBottom:4, color:'#16a34a' }}>📋 Work Done</div>
                      <div style={{ color:'#15803d', lineHeight:1.5 }}>{t.outcome_note}</div>
                    </div>
                  )}
                  {/* ── Inline edit form for ticket details ── */}
                  {editingTicket===t.id ? (
                    <div style={{ marginTop:14, padding:'14px 16px', background:'var(--accentbg)', borderRadius:10, border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--text1)', marginBottom:12 }}>Edit Ticket Details</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10, marginBottom:10 }}>
                        {[['serial_left','Serial (Left)'],['serial_right','Serial (Right)'],['ha_brand','HA Brand'],['ha_model','HA Model']].map(([k,lbl])=>(
                          <div key={k}>
                            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:3 }}>{lbl}</div>
                            <input style={{ ...S.input, width:'100%', fontSize:13 }} value={tEdit[k]||''} onChange={e=>setTEdit(x=>({...x,[k]:e.target.value}))} placeholder={lbl}/>
                          </div>
                        ))}
                        <div>
                          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:3 }}>Warranty Type</div>
                          <select style={{ ...S.input, width:'100%', fontSize:13 }} value={tEdit.warranty_type||''} onChange={e=>{
                            const wt=e.target.value;
                            // auto-clear proof when switching away from Under Warranty
                            setTEdit(x=>({...x, warranty_type:wt, ...(wt!=='Under Warranty'?{warranty_proof:[],warranty_ref:''}:{})}));
                          }}>
                            {['Under Warranty','Non Warranty','Re-Repair'].map(v=><option key={v}>{v}</option>)}
                          </select>
                        </div>
                      </div>
                      {tEdit.warranty_type==='Under Warranty' && (
                        <div style={{ borderTop:'1px solid var(--border)', paddingTop:10, marginTop:4 }}>
                          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:6 }}>Warranty Proof</div>
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
                            {['Warranty Card','Invoice','Others'].map(opt=>(
                              <label key={opt} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'6px 14px', borderRadius:8, background:(tEdit.warranty_proof||[]).includes(opt)?'var(--accent)':'var(--bg)', border:`1.5px solid ${(tEdit.warranty_proof||[]).includes(opt)?'var(--accent)':'var(--border)'}`, fontWeight:600, fontSize:12, color:(tEdit.warranty_proof||[]).includes(opt)?'#fff':'var(--text1)', transition:'all .15s' }}>
                                <input type="checkbox" checked={(tEdit.warranty_proof||[]).includes(opt)} onChange={()=>setTEdit(x=>({ ...x, warranty_proof: (x.warranty_proof||[]).includes(opt) ? (x.warranty_proof||[]).filter(v=>v!==opt) : [...(x.warranty_proof||[]),opt] }))} style={{ accentColor:'#7c3aed' }}/> {opt}
                              </label>
                            ))}
                          </div>
                          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:3 }}>Warranty Card No. / Reference</div>
                          <input style={{ ...S.input, width:'100%', fontSize:13 }} value={tEdit.warranty_ref||''} onChange={e=>setTEdit(x=>({...x,warranty_ref:e.target.value}))} placeholder="Warranty card number or reference (optional)"/>
                        </div>
                      )}
                      {tEditErr && <div style={{ fontSize:12, color:'var(--danger)', fontWeight:600, marginBottom:8 }}>⚠ {tEditErr}</div>}
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={()=>saveTicketEdit(t.id)} disabled={tEditBusy} style={{ ...S.btnP, padding:'7px 18px', fontSize:12, opacity:tEditBusy?0.6:1 }}>{tEditBusy?'Saving…':'Save'}</button>
                        <button onClick={()=>setEditingTicket(null)} style={{ ...S.btnS, padding:'7px 18px', fontSize:12 }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop:10, display:'flex', justifyContent:'flex-start' }}>
                      <button onClick={()=>startTicketEdit(t)} style={{ background:'none', border:'1px solid var(--border)', borderRadius:6, padding:'4px 12px', fontSize:11, color:'var(--muted)', cursor:'pointer' }}>✏️ Edit Details</button>
                    </div>
                  )}

                  <div style={{ marginTop:14, display:'flex', justifyContent:'flex-end', gap:8, flexWrap:'wrap' }}>
                    <button style={{ background:'#25D366', color:'#fff', border:'none', borderRadius:8, padding:'6px 16px', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}
                      onClick={()=>{
                        const msg = buildWATrackMsg(t, live.name);
                        window.open(`https://wa.me/91${live.mobile}?text=${encodeURIComponent(msg)}`, '_blank');
                      }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Share Tracking
                    </button>
                    <button style={{ ...S.btnS, padding:'6px 16px', fontSize:12 }} onClick={()=>setPrintTicket({
                      repair_no: t.repair_no,
                      repair_date: t.repair_date,
                      clinic_label: t.clinic_label||live.clinic_label||'',
                      patient_name: live.name,
                      patient_mobile: live.mobile,
                      ha_brand: t.ha_brand,
                      ha_model: t.ha_model||'',
                      ha_type: t.ha_type||'',
                      warranty_type: t.warranty_type,
                      serial_left: t.serial_left||'',
                      serial_right: t.serial_right||'',
                      complaint: t.complaint||'',
                      complaint_other: t.complaint_other||'',
                      remarks: t.remarks||'',
                      accessories: (() => { try { return typeof t.accessories==='string' ? JSON.parse(t.accessories||'[]') : (t.accessories||[]); } catch { return []; } })(),
                    })}>🖨️ Print Job Card</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    {/* ── PMS Consultation History (360 View) ── */}
    {(pmsLoading || pmsHistory) && (
      <div style={{ marginTop:28 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
          <div style={{ flex:1, height:1, background:'var(--border)' }}/>
          <div style={{ display:'flex', alignItems:'center', gap:7, background:'linear-gradient(135deg,#1e1b4b,#4c1d95)', borderRadius:20, padding:'5px 14px' }}>
            <span style={{ fontSize:12 }}>📋</span>
            <span style={{ fontSize:11, fontWeight:700, color:'#c4b5fd', letterSpacing:.5 }}>PMS CONSULTATION HISTORY</span>
          </div>
          <div style={{ flex:1, height:1, background:'var(--border)' }}/>
        </div>

        {pmsLoading && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'20px 0', color:'var(--muted)', fontSize:13 }}>
            <span style={{ width:14, height:14, border:'2px solid #c4b5fd', borderTopColor:'#7c3aed', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }}/>
            Loading clinic consultation history…
          </div>
        )}

        {pmsHistory && (
          <div>
            {/* Patient status + source pills */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
              {pmsHistory.patient_status && (
                <span style={{ background:'#ede9fe', color:'#7c3aed', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                  🩺 {pmsHistory.patient_status}
                </span>
              )}
              {pmsHistory.source && (
                <span style={{ background:'#f0fdf4', color:'#15803d', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                  📣 {pmsHistory.source}
                </span>
              )}
              {pmsHistory.registered && (
                <span style={{ background:'var(--bg3)', color:'var(--muted)', padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
                  📅 Registered {fmtDate(pmsHistory.registered)}
                </span>
              )}
            </div>

            {(!pmsHistory.visits || pmsHistory.visits.length === 0) ? (
              <div style={{ padding:'20px 0', color:'var(--muted)', fontSize:13 }}>No consultation visits recorded in PMS yet.</div>
            ) : (
              <div style={{ position:'relative', paddingLeft:20 }}>
                <div style={{ position:'absolute', left:7, top:6, bottom:6, width:2, background:'linear-gradient(to bottom,#7c3aed,#c4b5fd44)', borderRadius:2 }}/>
                {pmsHistory.visits.map((v,i)=>(
                  <div key={v.id} style={{ position:'relative', marginBottom:12 }}>
                    <div style={{ position:'absolute', left:-13, top:6, width:10, height:10, borderRadius:'50%', background:'#7c3aed', border:'2px solid var(--card)', boxShadow:'0 0 0 2px #7c3aed40' }}/>
                    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'11px 14px', marginLeft:8 }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:6, marginBottom:6 }}>
                        <div style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>
                          {new Date(v.ts).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                          {' · '}
                          {new Date(v.ts).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
                        </div>
                        {v.clinic_id && <span style={{ background:'#ede9fe', color:'#7c3aed', padding:'1px 8px', borderRadius:5, fontSize:9, fontWeight:700 }}>{v.clinic_id}</span>}
                      </div>
                      {v.attended_by && <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', marginBottom:4 }}>👨‍⚕️ {v.attended_by}</div>}
                      {v.note && <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.55 }}>{v.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {printTicket && <PrintJobCard data={printTicket} onClose={()=>setPrintTicket(null)} />}
    </>
  );
}

// ── RepairFunnel ──────────────────────────────────────────────
function RepairFunnel({ ticket: t, ms }) {
  const isRejected = t.status === 'Patient Rejected';
  // Was the rejection path taken at any point? (ticket may now be Dispatched after rejection)
  const wentRejected = isRejected || !!ms['Patient Rejected'];
  // Stages skipped due to rejection
  const REJECTION_SKIPPED = new Set(wentRejected ? ['Patient Approved','Under Repair'] : []);

  const isFOC = t.company_report_type === 'FOC';
  let flow = isRejected
    ? [...TIMELINE_FLOW.slice(0, TIMELINE_FLOW.indexOf('Patient Approved') + 1), 'Patient Rejected']
    : [...TIMELINE_FLOW];
  if (t.sending_to === 'Direct Company') flow = flow.filter(s => !['Courier Sent','Received at HO'].includes(s));
  // FOC repairs are free — skip the estimate/approval stages entirely
  if (isFOC) flow = flow.filter(s => !['Estimate Received','Patient Informed','Patient Approved'].includes(s));

  const curIdx = flow.indexOf(t.status);

  // Timestamp for a stage (Form Submitted uses repair_date as anchor)
  const getTs = s => {
    if (s === 'Form Submitted') return t.repair_date ? new Date(t.repair_date).getTime() : null;
    return ms[s] ? new Date(ms[s]).getTime() : null;
  };

  return (
    <div style={{ position:'relative', paddingLeft:26 }}>
      <div style={{ position:'absolute', left:8, top:6, bottom:6, width:2, background:'var(--border)', borderRadius:2 }}/>
      {flow.map((s, i) => {
        const skipped = REJECTION_SKIPPED.has(s);
        const done    = !skipped && (isRejected ? i < flow.length - 1 : i < curIdx);
        const cur     = !skipped && i === curIdx;
        const pending = !done && !cur && !skipped;
        const color   = skipped ? '#dc2626' : cur ? (STATUS_COLOR[s]||'#7c3aed') : done ? '#059669' : 'var(--muted2)';

        const ts     = getTs(s);
        const nextTs = i + 1 < flow.length ? getTs(flow[i + 1]) : null;

        let daysInStage = null;
        if (!skipped && ts) {
          if (cur)               daysInStage = Math.floor((Date.now() - ts) / 86400000);
          else if (done && nextTs) daysInStage = Math.floor((nextTs - ts) / 86400000);
        }

        return (
          <div key={s} style={{ position:'relative', marginBottom:12, display:'flex', alignItems:'flex-start', gap:10, opacity: skipped ? 0.45 : 1 }}>
            <div style={{ position:'absolute', left:-18, top:3, width:14, height:14, borderRadius:'50%', background:skipped?'#fee2e2':cur?color:done?'#059669':'var(--bg3)', border:`2px solid ${color}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:7, color: skipped?'#dc2626':'#fff', fontWeight:900, flexShrink:0 }}>
              {skipped?'✕':done?'✓':cur?'●':''}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6, flexWrap:'wrap' }}>
                <div style={{ fontSize:12, fontWeight:cur?800:done?600:400, color:skipped?'#dc2626':pending?'var(--muted2)':cur?color:'var(--text1)', textDecoration: skipped?'line-through':'none' }}>
                  {STATUS_ICON[s]||'○'} {s}
                  {cur && <span style={{ marginLeft:6, fontSize:9, background:color+'22', color, padding:'1px 6px', borderRadius:10, fontWeight:700 }}>● Active</span>}
                  {skipped && <span style={{ marginLeft:6, fontSize:9, background:'#fee2e2', color:'#dc2626', padding:'1px 6px', borderRadius:10, fontWeight:700 }}>Skipped</span>}
                </div>
                {daysInStage !== null && (
                  <span style={{ fontSize:11, fontWeight:700, flexShrink:0, color: cur && daysInStage>=7?'#dc2626': cur && daysInStage>=4?'#d97706': cur?color:'#059669' }}>
                    {daysInStage === 0 ? 'Today' : `${daysInStage}d${cur?' here':''}`}
                  </span>
                )}
              </div>
              {ts && !skipped && (
                <div style={{ fontSize:10, color:'var(--muted2)', marginTop:2 }}>
                  {new Date(ts).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── HoldingNoteInline ─────────────────────────────────────────────
function HoldingNoteInline({ ticketId, initialNote, csrf }) {
  const [note, setNote] = React.useState(initialNote);
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const save = async () => {
    if (note === initialNote) return;
    setSaving(true);
    try {
      await api(`tickets.php?id=${ticketId}`, {
        method: 'PUT',
        headers: { 'X-CSRF-Token': csrf },
        body: JSON.stringify({ action: 'save_holding_note', holding_note: note, csrf_token: csrf })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) {}
    setSaving(false);
  };

  return (
    <div style={{ margin:'12px 0', padding:'12px 16px', background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:10 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'#92400e', letterSpacing:1, textTransform:'uppercase', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
        🗒 Holding Reason
        {saving && <span style={{ fontWeight:400, color:'#d97706' }}>saving…</span>}
        {saved && <span style={{ fontWeight:400, color:'#059669' }}>✓ saved</span>}
      </div>
      <textarea
        style={{ width:'100%', border:'1px solid #fcd34d', borderRadius:8, background:'#fff', padding:'8px 10px', fontSize:12, color:'#78350f', resize:'vertical', minHeight:52, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
        placeholder="Why is delivery delayed? e.g. Patient out of town till 25 Jun, will call on return…"
        value={note}
        onChange={e => setNote(e.target.value)}
        onBlur={save}
      />
      {note && <div style={{ fontSize:10, color:'#92400e', marginTop:4 }}>This note is visible to HO admins on the dashboard.</div>}
    </div>
  );
}

// ── DrawerSection ─────────────────────────────────────────────
function DrawerSection({ title, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:'var(--accent)', marginBottom:10, paddingBottom:6, borderBottom:'1px solid var(--border)' }}>{title}</div>
      {children}
    </div>
  );
}

// ── RepairDrawer ─────────────────────────────────────────────
function RepairDrawer({ ticket:t, user, csrf, onClose, onAction }) {
  const canEditOutcome = ['admin','owner','office','mis'].includes(user?.role);
  const [outcomeNote, setOutcomeNote] = React.useState(t?.outcome_note || '');
  const [outcomeSavedVal, setOutcomeSavedVal] = React.useState(t?.outcome_note || '');
  const outcomeClean = outcomeNote === outcomeSavedVal;
  const saveOutcome = async () => {
    try {
      await api(`tickets.php?id=${t.id}`, { method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ action:'save_outcome_note', outcome_note:outcomeNote, csrf_token:csrf }) });
      setOutcomeSavedVal(outcomeNote);
    } catch(e) { /* silent */ }
  };
  if (!t) return null;
  const days = daysSince(t.repair_date, t.delivered_to_patient||null);
  const canAct = user?.role !== 'owner';
  const ms = t.milestone_dates ? (() => { try { return JSON.parse(t.milestone_dates); } catch { return {}; } })() : {};
  const overSLA = days >= SLA_DAYS && t.status !== 'Delivered to Patient';
  return (
    <div style={{ position:'fixed', inset:0, zIndex:800 }}>
      <div className="fadeIn" style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.35)', cursor:'pointer' }} onClick={onClose} />
      <div className="drawer-panel" style={{ position:'absolute', top:0, right:0, bottom:0, width:Math.min(520, window.innerWidth - 32), background:'var(--card)', boxShadow:'-8px 0 40px rgba(0,0,0,.2)', display:'flex', flexDirection:'column', overflowY:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'var(--bg3)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:900, fontSize:16, color:'var(--accent)', fontFamily:'monospace', letterSpacing:-0.5 }}>{t.repair_no}</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{t.patient_name} · {t.clinic_label}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#666'), fontSize:11 }}>{STATUS_ICON[t.status]} {t.status}</span>
            <button onClick={onClose} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, width:30, height:30, cursor:'pointer', fontSize:17, color:'var(--muted)', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          </div>
        </div>
        <div style={{ flex:1, padding:'18px 20px', overflowY:'auto' }}>
          {overSLA && (
            <div style={{ padding:'8px 14px', background:'var(--dangerBg)', borderRadius:10, border:'1px solid #fca5a5', marginBottom:16, fontSize:12, fontWeight:700, color:'var(--danger)' }}>
              ⚠️ SLA exceeded — {days} days elapsed (target {SLA_DAYS} days)
            </div>
          )}
          <DrawerSection title="Patient & Device">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
              {[['Patient', t.patient_name],['Mobile', t.patient_mobile],['Brand', t.ha_brand],['Model', t.ha_model||'—'],['Type', t.ha_type||'—'],['Warranty', t.warranty_type],['Serial (L)', t.serial_left||'—'],['Serial (R)', t.serial_right||'—']].map(([l,v])=>(
                <div key={l}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>{l}</div><div style={{ fontSize:13, fontWeight:600, color:'var(--text1)' }}>{v}</div></div>
              ))}
            </div>
          </DrawerSection>
          {(t.complaint || t.remarks) && (
            <DrawerSection title="Complaint & Notes">
              {t.complaint && <div style={{ marginBottom:8 }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Complaint</div><div style={{ fontSize:13, color:'var(--text1)' }}>{t.complaint}</div></div>}
              {t.complaint_other && <div style={{ marginBottom:8 }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Details</div><div style={{ fontSize:13, color:'var(--text1)' }}>{t.complaint_other}</div></div>}
              {t.remarks && <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Remarks</div><div style={{ fontSize:13, color:'var(--text1)' }}>{t.remarks}</div></div>}
            </DrawerSection>
          )}
          {(t.out_company || t.ret_company) && (
            <DrawerSection title="Courier Details">
              {t.out_company && <div style={{ marginBottom:8 }}><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Outward Courier</div><div style={{ fontSize:13, fontWeight:600, color:'var(--text1)' }}>{t.out_company} <span style={{ color:'var(--muted)', fontWeight:400 }}>· {t.out_tracking||'—'}</span></div></div>}
              {t.ret_company && <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Return Courier</div><div style={{ fontSize:13, fontWeight:600, color:'var(--text1)' }}>{t.ret_company} <span style={{ color:'var(--muted)', fontWeight:400 }}>· {t.ret_tracking||'—'}</span></div></div>}
            </DrawerSection>
          )}
          {t.company_report && (
            <DrawerSection title="Company Report">
              <div style={{ padding:'10px 14px', background:t.company_report_type==='FOC'?'var(--greenBg)':'var(--amberBg)', borderRadius:8, fontSize:13 }}>
                <div style={{ fontWeight:700, color:t.company_report_type==='FOC'?'var(--green)':'var(--amber)', marginBottom:4 }}>
                  {t.company_report_type==='FOC' ? '✅ FOC (Free of Charge)' : `💰 Estimate: ₹${Number(t.estimate_amount||0).toLocaleString('en-IN')}`}
                </div>
                <div style={{ color:'var(--text2)', lineHeight:1.5 }}>{t.company_report}</div>
              </div>
            </DrawerSection>
          )}
          {/* Work Done — admin writes, clinic reads */}
          {canEditOutcome ? (
            <DrawerSection title="📋 Work Done">
              <textarea
                style={{ ...S.input, width:'100%', minHeight:80, resize:'vertical', fontSize:13, lineHeight:1.5 }}
                value={outcomeNote}
                onChange={e=>setOutcomeNote(e.target.value)}
                placeholder="e.g. HA serviced FOC, receiver cleaned. Charger rejected by patient."
              />
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                <button
                  disabled={outcomeClean}
                  style={{ ...S.btnP, padding:'5px 16px', fontSize:12, opacity: outcomeClean ? 1 : 1, background: outcomeClean ? '#16a34a' : undefined, borderColor: outcomeClean ? '#16a34a' : undefined, cursor: outcomeClean ? 'default' : 'pointer' }}
                  onClick={saveOutcome}
                >
                  {outcomeClean ? '✅ Saved' : 'Save'}
                </button>
              </div>
            </DrawerSection>
          ) : outcomeNote ? (
            <DrawerSection title="📋 Work Done">
              <div style={{ padding:'10px 14px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:8, fontSize:13, color:'#15803d', lineHeight:1.5 }}>{outcomeNote}</div>
            </DrawerSection>
          ) : null}
          <DrawerSection title="🔄 Repair Pipeline">
            <RepairFunnel ticket={t} ms={ms} />
          </DrawerSection>
          <DrawerSection title="Dates">
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
              <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Repair Date</div><div style={{ fontSize:13, fontWeight:600 }}>{fmtDate(t.repair_date)}</div></div>
              <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Days Elapsed</div><div style={{ fontSize:13, fontWeight:700, color:overSLA?'var(--danger)':days>=SLA_DAYS*0.7?'var(--amber)':'var(--text1)' }}>{days}d {overSLA?'⚠️ SLA exceeded':days>=SLA_DAYS*0.7?`(${SLA_DAYS-days}d left)`:''}</div></div>
              {t.delivered_to_patient && <div><div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:2 }}>Delivered</div><div style={{ fontSize:13, fontWeight:600 }}>{fmtDate(t.delivered_to_patient)}</div></div>}
            </div>
          </DrawerSection>
          {t.admin_note && (
            <DrawerSection title="Internal Note">
              <div style={{ padding:'10px 14px', background:'var(--accentbg)', borderRadius:8, fontSize:13, color:'var(--text2)', lineHeight:1.5, fontStyle:'italic' }}>{t.admin_note}</div>
            </DrawerSection>
          )}
          {t.holding_note && t.status==='Received at Clinic' && (
            <DrawerSection title="📥 Holding Note">
              <div style={{ padding:'10px 14px', background:'#fef9c3', borderRadius:8, fontSize:13, color:'#92400e', lineHeight:1.5, border:'1px solid #fcd34d' }}>{t.holding_note}</div>
            </DrawerSection>
          )}
        </div>
        {canAct && onAction && (
          <div style={{ padding:'12px 20px', borderTop:'1px solid var(--border)', background:'var(--bg3)', flexShrink:0 }}>
            <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.5, marginBottom:8 }}>Quick Actions</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {t.status==='Form Submitted' && <button style={{ ...S.btnP, padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'send_courier'}); }}>📦 Send Courier</button>}
              {t.status==='Form Submitted' && user?.role!=='clinic' && <button style={{ ...S.btnP, background:'linear-gradient(135deg,#059669,#16a34a)', padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'confirm_received_ho'}); }}>{t.sending_to==='Direct Company' ? '🏭 Sent to Co.' : '🏢 At HO'}</button>}
              {t.status==='Courier Sent' && user?.role!=='clinic' && <button style={{ ...S.btnP, padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'confirm_received_ho'}); }}>{t.sending_to==='Direct Company' ? '🏭 Co. Received' : '✓ Received at HO'}</button>}
              {t.status==='Received at HO' && t.sending_to!=='Direct Company' && user?.role!=='clinic' && <button style={{ ...S.btnP, background:'linear-gradient(135deg,#0891b2,#06b6d4)', padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'sent_to_company'}); }}>🏭 To Company</button>}
              {(t.status==='Sent to Company' || (t.status==='Received at HO' && t.sending_to==='Direct Company')) && user?.role!=='clinic' && <>
                <button style={{ ...S.btnP, padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'submit_estimate_report'}); }}>💰 Estimate</button>
                <button style={{ ...S.btnP, padding:'6px 12px', fontSize:12, background:'linear-gradient(135deg,#059669,#16a34a)' }} onClick={()=>{ onClose(); onAction({ticket:t,type:'submit_foc'}); }}>✅ FOC</button>
              </>}
              {t.status==='Under Repair' && user?.role!=='clinic' && <button style={{ ...S.btnS, padding:'6px 12px', fontSize:12 }} onClick={()=>{ onClose(); onAction({ticket:t,type:'dispatch_back'}); }}>🚚 Dispatch</button>}
              {['Estimate Received','Patient Informed'].includes(t.status) && user?.role!=='clinic' && (
                <button style={{ ...S.btnS, padding:'6px 12px', fontSize:12, borderColor:'#d97706', color:'#d97706' }} onClick={()=>{ onClose(); onAction({ticket:t,type:'edit_estimate'}); }}>✏️ Edit Estimate</button>
              )}
              {t.status!=='Form Submitted' && t.status!=='Delivered to Patient' && user?.role!=='clinic' && (
                <button style={{ ...S.btnS, padding:'6px 12px', fontSize:12, borderColor:'#dc2626', color:'#dc2626' }} onClick={()=>{ onClose(); onAction({ticket:t,type:'revert_status'}); }}>↩ Revert</button>
              )}
              <button style={{ background:'#25D366', color:'#fff', border:'none', borderRadius:8, padding:'6px 12px', fontWeight:700, fontSize:12, cursor:'pointer' }}
                onClick={()=>{ const msg=buildWATrackMsg(t,t.patient_name); window.open(`https://wa.me/91${t.patient_mobile}?text=${encodeURIComponent(msg)}`,'_blank'); }}>WA</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── BatchCourierModal ─────────────────────────────────────────
function BatchCourierModal({ count, onClose, onDone }) {
  const [form, setForm] = useState({ courier_company:'', tracking_no:'' });
  return (
    <Modal title={`📦 Batch Courier — ${count} Ticket${count>1?'s':''}`} onClose={onClose}>
      <div style={{ padding:'10px 14px', background:'var(--amberBg)', borderRadius:10, marginBottom:16, fontSize:13, color:'var(--amber)', fontWeight:600 }}>
        ⚠️ Same courier details will be applied to all {count} selected ticket{count>1?'s':''}.
      </div>
      <form onSubmit={e=>{ e.preventDefault(); onDone(form); }} style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div><label style={S.label}>Courier Company</label><input style={S.input} value={form.courier_company} onChange={e=>setForm(f=>({...f,courier_company:e.target.value}))} placeholder="e.g. Blue Dart, DTDC, FedEx" required /></div>
        <div><label style={S.label}>AWB / Tracking Number</label><input style={S.input} value={form.tracking_no} onChange={e=>setForm(f=>({...f,tracking_no:e.target.value}))} placeholder="Tracking / AWB number" required /></div>
        <div style={{ display:'flex', gap:10 }}>
          <button type="submit" style={S.btnP}>📦 Apply to All {count}</button>
          <button type="button" onClick={onClose} style={S.btnS}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// ── KanbanView ────────────────────────────────────────────────
const KANBAN_STAGES = [
  { status:'Form Submitted',    color:'#d97706' },
  { status:'Courier Sent',      color:'#2563eb' },
  { status:'Received at HO',    color:'#059669' },
  { status:'Sent to Company',   color:'#0891b2' },
  { status:'Estimate Received', color:'#f97316' },
  { status:'Patient Informed',  color:'#7c3aed' },
  { status:'Patient Approved',  color:'#16a34a' },
  { status:'Under Repair',   color:'#0891b2' },
  { status:'Dispatched to Branch', color:'#2563eb' },
  { status:'Received at Clinic', color:'#7c3aed' },
  { status:'Patient Rejected',  color:'#dc2626' },
];
function getKanbanAction(t, isClinic) {
  switch (t.status) {
    case 'Form Submitted':    return !isClinic ? { label: t.sending_to==='Direct Company' ? '🏭 Sent to Company' : '✅ Received at HO', type:'confirm_received_ho', direct:false } : null;
    case 'Courier Sent':      return !isClinic ? { label: t.sending_to==='Direct Company' ? '✅ Received at Co.' : '✅ Received at HO', type:'confirm_received_ho', direct:false } : null;
    case 'Received at HO':    return !isClinic ? (t.sending_to==='Direct Company'
      ? { label:'💰 Estimate Report', type:'submit_estimate_report', direct:false }
      : { label:'🏭 Sent to Company', type:'sent_to_company', direct:false }) : null;
    case 'Sent to Company':   return !isClinic ? { label:'💰 Estimate Report', type:'submit_estimate_report', direct:false } : null;
    case 'Estimate Received': return isClinic ? { label:'📞 Patient Informed', type:'mark_patient_informed', direct:false } : null;
    case 'Patient Approved':  return !isClinic ? { label:'🔧 Mark Under Repair', type:'confirm_work_order', direct:true } : null;
    case 'Under Repair':        return !isClinic ? { label:'✅ Dispatch to Clinic', type:'dispatch_back', direct:false } : null;
    case 'Patient Rejected':    return !isClinic ? { label:'📦 Return to Clinic', type:'dispatch_back', direct:false } : null;
    case 'Dispatched to Branch':return isClinic ? { label:'📥 Confirm Received', type:'confirm_received_at_clinic', direct:false } : null;
    case 'Received at Clinic':  return isClinic ? { label:'🎉 Delivered to Patient', type:'delivered_to_patient', direct:false } : null;
    default: return null;
  }
}

function KanbanView({ tickets, onDrawer, onAction, onDirectAction, user }) {
  const active = useMemo(() => tickets.filter(t => !t.is_archived), [tickets]);
  const isClinic = user?.role === 'clinic';
  return (
    <div style={{ overflowX:'auto', paddingBottom:16 }}>
      <div style={{ display:'flex', gap:12, minWidth:'max-content', alignItems:'flex-start', paddingBottom:4 }}>
        {KANBAN_STAGES.map(stage => {
          const cols = active.filter(t => t.status === stage.status);
          if (cols.length === 0) return null;
          return (
            <div key={stage.status} style={{ width:230, flexShrink:0 }}>
              <div style={{ padding:'7px 12px', borderRadius:'10px 10px 0 0', background:`${stage.color}14`, borderBottom:`2px solid ${stage.color}`, display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:800, color:stage.color, textTransform:'uppercase', letterSpacing:.5, maxWidth:170, lineHeight:1.2 }}>{stage.status}</div>
                <span style={{ background:stage.color, color:'#fff', borderRadius:20, fontSize:10, fontWeight:800, padding:'1px 7px', flexShrink:0 }}>{cols.length}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {cols.map(t => {
                  const days = daysSince(t.repair_date, t.delivered_to_patient||null);
                  const overSLA = days >= SLA_DAYS;
                  const action = getKanbanAction(t, isClinic);
                  return (
                    <div key={t.id} style={{ background:'var(--card)', border:'1px solid var(--cardborder)', borderLeft:`3px solid ${stage.color}`, borderRadius:10, padding:'10px 12px' }}>
                      {/* Card header — click opens drawer */}
                      <div style={{ cursor:'pointer' }} onClick={() => onDrawer && onDrawer(t)}>
                        <div style={{ fontWeight:800, fontSize:12, color:'var(--accent)', marginBottom:3 }}>{t.repair_no}</div>
                        <div style={{ fontSize:11, fontWeight:600, color:'var(--text1)' }}>{t.patient_name}</div>
                        <div style={{ fontSize:10, color:'var(--muted)', marginTop:1 }}>{t.ha_brand}{t.ha_model ? ` · ${t.ha_model}` : ''}</div>
                        <div style={{ fontSize:10, color:'var(--muted)' }}>{t.clinic_label}</div>
                        <div style={{ marginTop:6, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <span style={{ fontSize:10, fontWeight:700, color:overSLA?'var(--danger)':days>=SLA_DAYS*0.7?'var(--amber)':'var(--green)' }}>
                            {overSLA ? `⚠️ ${days}d / SLA!` : `${days}d / ${SLA_DAYS}d`}
                          </span>
                          {t.warranty_type==='Under Warranty' && <span style={{ fontSize:9, background:'#05966918', color:'var(--green)', borderRadius:4, padding:'1px 5px', fontWeight:700 }}>W</span>}
                        </div>
                      </div>
                      {/* Action button */}
                      {action && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            if (action.direct) onDirectAction && onDirectAction(t, action.type);
                            else onAction && onAction(t, action.type);
                          }}
                          style={{ marginTop:8, width:'100%', padding:'5px 0', fontSize:10, fontWeight:700, background:`${stage.color}18`, border:`1.5px solid ${stage.color}55`, color:stage.color, borderRadius:6, cursor:'pointer', transition:'background .12s' }}
                          onMouseEnter={e=>e.currentTarget.style.background=`${stage.color}30`}
                          onMouseLeave={e=>e.currentTarget.style.background=`${stage.color}18`}
                        >
                          {action.label}
                        </button>
                      )}
                      {/* FOC report for no-estimate repairs */}
                      {t.status==='Sent to Company' && (
                        <button
                          onClick={e => { e.stopPropagation(); onAction && onAction(t, 'submit_foc'); }}
                          style={{ marginTop:4, width:'100%', padding:'5px 0', fontSize:10, fontWeight:700, background:'#05966918', border:'1.5px solid #05966955', color:'#059669', borderRadius:6, cursor:'pointer' }}
                        >
                          ✅ FOC — No Estimate
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={S.label}>{label}</div>
      <div style={{ fontSize:13, color:'var(--text1)', fontWeight:600, marginTop:4 }}>{value}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═════════════════════════════════════════════════════════════
function CourierBadge({ out_company, out_tracking, ret_company, ret_tracking }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position:'relative', display:'inline-block', marginTop:4 }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:6, padding:'2px 8px', fontSize:10, color:'var(--muted)', cursor:'pointer', fontWeight:600 }}>
        📦 Courier {open?'▲':'▼'}
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, zIndex:50, background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', minWidth:220, boxShadow:'0 8px 24px rgba(0,0,0,.15)', marginTop:4 }}>
          {out_company && <div style={{ marginBottom:6 }}><div style={{ fontSize:9, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1 }}>Outward Courier</div><div style={{ fontSize:12, fontWeight:600 }}>{out_company}</div><div style={{ fontSize:11, color:'var(--muted)', fontFamily:'monospace' }}>{out_tracking}</div></div>}
          {ret_company && <div><div style={{ fontSize:9, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1 }}>Return Courier</div><div style={{ fontSize:12, fontWeight:600 }}>{ret_company}</div><div style={{ fontSize:11, color:'var(--muted)', fontFamily:'monospace' }}>{ret_tracking}</div></div>}
        </div>
      )}
    </div>
  );
}

const ADMIN_PAGE_SIZE = 25;

// Module-level filter store — lives outside React, survives StrictMode double-mounts,
// tab switches, re-renders, and any other lifecycle event. Initialized once from localStorage.
const _FILTER_DEFAULTS = { clinic_id:'', status:'', warranty_type:'', date_from:'', date_to:'', search:'' };
let _adminFilters = (() => {
  try {
    const s = localStorage.getItem('sl_admin_filters_v2');
    const saved = s ? JSON.parse(s) : {};
    return { ..._FILTER_DEFAULTS, clinic_id: saved.clinic_id||'', status: saved.status||'', warranty_type: saved.warranty_type||'', date_from: saved.date_from||'', date_to: saved.date_to||'' };
  } catch { return { ..._FILTER_DEFAULTS }; }
})();

// Memoized so Shell re-renders (e.g. live indicator pulse) never re-render AdminDashboard
const AdminDashboard = React.memo(function AdminDashboard({ user, csrf, showToast, onReload }) {
  // Read initial state from module-level store (never from useState initializer alone)
  const [filters, _setFiltersState] = useState(() => ({ ..._adminFilters }));

  // setFilters always writes to module-level store + localStorage synchronously,
  // then updates React state. On any remount, useState() reads the module store.
  const setFilters = useCallback((updater) => {
    _setFiltersState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      Object.assign(_adminFilters, next);
      const { search: _, ...toSave } = next;
      try { localStorage.setItem('sl_admin_filters_v2', JSON.stringify(toSave)); } catch {}
      return next;
    });
  }, []);

  // Own ticket state — completely independent from sharedTickets
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchSeq = React.useRef(0);
  const silentFetchRef = React.useRef(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => { silentFetchRef.current = true; setReloadTick(t => t + 1); }, []);

  // Single effect: fires on mount, filter change, or manual reload
  useEffect(() => {
    const isSilent = silentFetchRef.current;
    silentFetchRef.current = false;
    const qs = Object.entries(filters).filter(([,v])=>v).map(([k,v])=>`${k}=${encodeURIComponent(v)}`).join('&');
    const id = ++fetchSeq.current;
    if (!isSilent) setLoading(true);
    api(`tickets.php${qs ? '?' + qs : ''}`)
      .then(d => { if (fetchSeq.current === id) setTickets(Array.isArray(d) ? d : []); })
      .catch(() => { if (fetchSeq.current === id) setTickets([]); })
      .finally(() => { if (fetchSeq.current === id && !isSilent) setLoading(false); });
  }, [filters, reloadTick]); // eslint-disable-line

  // Always keep a ref of current filters so the silent refresh never has a stale closure
  const filtersRef = React.useRef(filters);
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // Silent background refresh every 10 minutes — no loading spinner, data swaps quietly
  useEffect(() => {
    const interval = setInterval(() => {
      const f = filtersRef.current;
      const qs = Object.entries(f).filter(([,v])=>v).map(([k,v])=>`${k}=${encodeURIComponent(v)}`).join('&');
      const id = ++fetchSeq.current;
      api(`tickets.php${qs ? '?' + qs : ''}`)
        .then(d => { if (fetchSeq.current === id) setTickets(Array.isArray(d) ? d : []); })
        .catch(() => {});
    }, 600000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Client-side safety net: even if the server somehow returns wrong data,
  // this ensures what's DISPLAYED always matches the active filters.
  const displayTickets = useMemo(() => {
    let result = tickets;
    if (filters.status)       result = result.filter(t => t.status === filters.status);
    if (filters.warranty_type)result = result.filter(t => t.warranty_type === filters.warranty_type);
    if (filters.clinic_id)    result = result.filter(t => String(t.clinic_account_id) === String(filters.clinic_id));
    if (filters.date_from)    result = result.filter(t => t.repair_date >= filters.date_from);
    if (filters.date_to)      result = result.filter(t => t.repair_date <= filters.date_to);
    if (filters.search) {
      const s = filters.search.toLowerCase();
      result = result.filter(t =>
        (t.patient_name   ||'').toLowerCase().includes(s) ||
        (t.patient_mobile ||'').includes(s) ||
        (t.repair_no      ||'').toLowerCase().includes(s)
      );
    }
    return result;
  }, [tickets, filters]);

  const [actionModal, setActionModal] = useState(null);
  const [notesModal, setNotesModal] = useState(null);
  const [patientModal, setPatientModal] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const clearSelection = () => setSelectedIds(new Set());
  const [drawerTicket, setDrawerTicket] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [batchCourierModal, setBatchCourierModal] = useState(false);
  const [clinicList, setClinicList] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [page, setPage] = useState(1);
  const searchDebounce = React.useRef(null);
  const searchInputRef = React.useRef(null);
  const quote = QUOTES[Math.floor(Date.now()/86400000) % QUOTES.length];

  // Reset to page 1 whenever the filter changes so we never land on an out-of-range page
  useEffect(() => { setPage(1); }, [filters]);

  useEffect(()=>{
    api('accounts.php?action=list').then(setClinicList).catch(()=>{});
    api('tickets.php?action=admin_stats').then(setAdminStats).catch(()=>{});
  },[]);

  const setSearch = (val) => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(()=> setFilters(f=>({...f,search:val})), 350);
  };

  async function bulkAction(action, label) {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} ticket${ids.length>1?'s':''} as "${label}"?`)) return;
    try {
      await Promise.all(ids.map(id =>
        api(`tickets.php?id=${id}`, { method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ action, csrf_token:csrf }) })
      ));
      showToast(`${ids.length} ticket${ids.length>1?'s':''} updated`);
      clearSelection();
      reload();
    } catch(e) { showToast(e.message, 'error'); }
  }

  async function batchCourierSubmit({ courier_company, tracking_no }) {
    const ids = [...selectedIds];
    setBatchCourierModal(false);
    try {
      await Promise.all(ids.map(id =>
        api(`tickets.php?id=${id}`, { method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ action:'send_courier', courier_company, tracking_no, csrf_token:csrf }) })
      ));
      showToast(`📦 Courier saved for ${ids.length} ticket${ids.length>1?'s':''}`);
      clearSelection();
      reload();
    } catch(e) { showToast(e.message, 'error'); }
  }

  async function doAction(ticket, action, extra) {
    try {
      if (action === 'delete_ticket') {
        if (!window.confirm(`Delete ${ticket.repair_no}? This cannot be undone.`)) return;
        await api(`tickets.php?id=${ticket.id}`, { method:'DELETE', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ csrf_token:csrf }) });
        showToast(`${ticket.repair_no} deleted`); setActionModal(null);
        reload(); return;
      }
      const json = await api(`tickets.php?id=${ticket.id}`,{ method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({action,csrf_token:csrf,...extra}) });
      if (json?.error) { showToast(json.error,'error'); return; }
      showToast('Updated successfully'); setActionModal(null);
      reload();
    } catch(e) { showToast(e.message,'error'); }
  }

  const active          = displayTickets.filter(t=>!t.is_archived);
  const delayed         = displayTickets.filter(t=>t.delay_alert>0);
  const pending         = displayTickets.filter(t=>t.status==='Estimate Received'&&t.patient_decision==='Pending');
  const transit         = displayTickets.filter(t=>t.status==='Courier Sent');
  const pendingDecision = [...displayTickets]
    .filter(t => t.status==='Estimate Received' || t.status==='Patient Informed')
    .sort((a,b) => new Date(a.repair_date) - new Date(b.repair_date));

  const stageStats = useMemo(() => {
    const sums = {}, counts = {};
    displayTickets.forEach(t => {
      if (!t.milestone_dates) return;
      let ms; try { ms = JSON.parse(t.milestone_dates); } catch { return; }
      const rd = new Date(t.repair_date);
      Object.entries(ms).forEach(([rawStatus, dateStr]) => {
        if (!dateStr) return;
        // Normalize legacy "Work Order Sent" → "Under Repair"
        const status = rawStatus === 'Work Order Sent' ? 'Under Repair' : rawStatus;
        const diff = Math.round((new Date(dateStr) - rd) / 86400000);
        if (diff >= 0 && diff <= 120) { sums[status] = (sums[status]||0) + diff; counts[status] = (counts[status]||0) + 1; }
      });
    });
    return Object.entries(sums)
      .filter(([s]) => counts[s] >= 3)
      .map(([s,sum]) => ({ status:s, avg: Math.round(sum/counts[s]), count: counts[s] }))
      .sort((a,b) => (STATUS_ORDER.indexOf(a.status) ?? 99) - (STATUS_ORDER.indexOf(b.status) ?? 99));
  }, [displayTickets]);

  // Group tickets by patient — one row per patient, latest repair first
  const grouped = useMemo(() => {
    const map = new Map();
    displayTickets.forEach(t => {
      if (!map.has(t.patient_mobile)) map.set(t.patient_mobile, []);
      map.get(t.patient_mobile).push(t);
    });
    return Array.from(map.values()); // each entry = array of tickets for one patient
  }, [displayTickets]);

  const totalPages  = Math.max(1, Math.ceil(grouped.length / ADMIN_PAGE_SIZE));
  const pagedGroups = grouped.slice((page-1)*ADMIN_PAGE_SIZE, page*ADMIN_PAGE_SIZE);

  return (
    <>
    <div className="fadeUp">
      <div style={{ marginBottom:24 }}>
        <div style={S.pageTitle}>Repair Dashboard</div>
        <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>All clinics · real-time repair status</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16, marginBottom:20 }}>
        <StatCard icon="🔧" value={active.length}                        label="Active Repairs"    color="#7c3aed" />
        <StatCard icon="💰" value={pending.length}                       label="Pending Estimates" color="#d97706" />
        <StatCard icon="⚠️" value={delayed.length}                       label="Delayed Deliveries"color="#dc2626" />
        <StatCard icon="📦" value={transit.length}                       label="In Transit"        color="#2563eb" />
        <StatCard icon="👥" value={adminStats?.total_patients ?? 0}      label="Total Patients"    color="#059669" />
      </div>

      {stageStats.length > 0 && (
        <div style={{ ...S.card, padding:'12px 18px', marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>⏱ Avg Days to Reach Stage · based on {displayTickets.length} repairs</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
            {stageStats.map(s => (
              <div key={s.status} style={{ display:'flex', alignItems:'center', gap:5, background:`${STATUS_COLOR[s.status]||'#7c3aed'}12`, border:`1px solid ${STATUS_COLOR[s.status]||'#7c3aed'}28`, borderRadius:8, padding:'4px 10px' }}>
                <span style={{ fontSize:12, fontWeight:800, color:STATUS_COLOR[s.status]||'var(--accent)' }}>{s.avg}d</span>
                <span style={{ fontSize:10, color:'var(--muted)', fontWeight:600 }}>{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && pendingDecision.length > 0 && (
        <div style={{ ...S.card, padding:0, overflow:'hidden', marginBottom:18, border:'2px solid var(--amber)' }}>
          <div style={{ padding:'12px 20px', background:'var(--amberBg)', borderBottom:'1px solid var(--amber)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span style={{ fontSize:18 }}>⏳</span>
            <div style={{ fontWeight:800, fontSize:14, color:'var(--amber)' }}>Pending Patient Decision ({pendingDecision.length})</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginLeft:'auto' }}>Oldest first · follow up required</div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#d9770618' }}>{['Repair No.','Clinic','Patient','Mobile','Status','Days'].map(h=><th key={h} style={{ ...S.th, background:'transparent', color:'#92400e' }}>{h}</th>)}</tr></thead>
              <tbody>
                {pendingDecision.map((t,i) => (
                  <tr key={t.id} style={{ background:i%2===0?'var(--card)':'var(--amberBg)' }}>
                    <td style={S.td}><strong style={{ fontSize:12, color:'var(--accent)' }}>{t.repair_no}</strong></td>
                    <td style={S.td}>{t.clinic_label}</td>
                    <td style={S.td}>{t.patient_name}</td>
                    <td style={S.td}><code style={{ fontSize:12 }}>{t.patient_mobile}</code></td>
                    <td style={S.td}>
                      <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#d97706'), fontSize:11 }}>{STATUS_ICON[t.status]} {t.status}</span>
                      {(() => { try { const ms=t.milestone_dates?JSON.parse(t.milestone_dates):{}; const sd=ms[t.status]; const d=sd?Math.floor((Date.now()-new Date(sd))/86400000):null; return d>=7&&t.status!=='Delivered to Patient'?<span style={{ ...S.pill('#b45309'), fontSize:9, marginTop:3, display:'block' }}>⏰ {d}d here</span>:null; } catch{return null;} })()}
                    </td>
                    <td style={S.td}><strong style={{ color:daysSince(t.repair_date,t.delivered_to_patient||null)>14?'var(--danger)':daysSince(t.repair_date,t.delivered_to_patient||null)>7?'var(--amber)':'var(--text1)' }}>{daysSince(t.repair_date,t.delivered_to_patient||null)}d</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && delayed.length > 0 && (() => {
        const overdue = [...delayed].sort((a,b) => daysSince(b.repair_date) - daysSince(a.repair_date));
        return (
          <div style={{ ...S.card, padding:0, overflow:'hidden', marginBottom:18, border:'2px solid var(--danger)' }}>
            <div style={{ padding:'12px 20px', background:'#fee2e218', borderBottom:'1px solid var(--danger)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <span style={{ fontSize:18 }}>🚨</span>
              <div style={{ fontWeight:800, fontSize:14, color:'var(--danger)' }}>Overdue Repairs ({overdue.length})</div>
              <div style={{ fontSize:12, color:'var(--muted)', marginLeft:'auto' }}>Worst first · immediate action needed</div>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#dc262612' }}>
                    {['Repair No.','Clinic','Patient','Mobile','Status','Days'].map(h=>
                      <th key={h} style={{ ...S.th, background:'transparent', color:'#991b1b' }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {overdue.map((t,i) => {
                    const d = daysSince(t.repair_date, t.delivered_to_patient||null);
                    const isCritical = t.delay_alert === 2;
                    return (
                      <tr key={t.id} style={{ background:i%2===0?'var(--card)':'#fee2e20a' }}>
                        <td style={S.td}><strong style={{ fontSize:12, color:'var(--accent)' }}>{t.repair_no}</strong></td>
                        <td style={S.td}>{t.clinic_label}</td>
                        <td style={S.td}>{t.patient_name}</td>
                        <td style={S.td}><code style={{ fontSize:12 }}>{t.patient_mobile}</code></td>
                        <td style={S.td}>
                          <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#dc2626'), fontSize:11 }}>{STATUS_ICON[t.status]} {t.status}</span>
                        </td>
                        <td style={S.td}>
                          <strong style={{ color:isCritical?'var(--danger)':'var(--amber)', fontSize:13 }}>{d}d</strong>
                          {isCritical && <span style={{ display:'block', fontSize:9, color:'var(--danger)', fontWeight:700 }}>CRITICAL</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14, alignItems:'center' }}>
        {['','Form Submitted','Courier Sent','Received at HO','Sent to Company','Estimate Received','Patient Informed','Patient Approved','Under Repair','Dispatched to Branch','Received at Clinic'].map(s => {
          // counts from current server result — accurate for current clinic/date/search filters
          const cnt = displayTickets.filter(t => s ? t.status===s : true).length;
          const active = filters.status === s || (!s && !filters.status);
          return (
            <button key={s||'all'} onClick={()=>setFilters(f=>({...f,status:s}))}
              style={{ padding:'5px 13px', fontSize:11, fontWeight:700, borderRadius:20, border:`1.5px solid ${active?'var(--accent)':'var(--border)'}`, background:active?'var(--accent)':'var(--card)', color:active?'#fff':'var(--muted)', cursor:'pointer', transition:'all .12s', whiteSpace:'nowrap' }}>
              {s||'All'} <span style={{ opacity:.75 }}>{cnt}</span>
            </button>
          );
        })}
        <div style={{ marginLeft:'auto', display:'flex', gap:6, flexShrink:0 }}>
          <button onClick={()=>setViewMode('table')} style={{ padding:'5px 13px', fontSize:11, fontWeight:700, borderRadius:20, border:`1.5px solid ${viewMode==='table'?'var(--accent)':'var(--border)'}`, background:viewMode==='table'?'var(--accent)':'var(--card)', color:viewMode==='table'?'#fff':'var(--muted)', cursor:'pointer' }}>📋 Table</button>
          <button onClick={()=>setViewMode('kanban')} style={{ padding:'5px 13px', fontSize:11, fontWeight:700, borderRadius:20, border:`1.5px solid ${viewMode==='kanban'?'var(--accent)':'var(--border)'}`, background:viewMode==='kanban'?'var(--accent)':'var(--card)', color:viewMode==='kanban'?'#fff':'var(--muted)', cursor:'pointer' }}>🗂 Kanban</button>
        </div>
      </div>

      <div style={{ ...S.card, marginBottom:18 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:10 }}>
          <input ref={searchInputRef} style={S.input} placeholder="🔍 Search…" defaultValue={filters.search} onChange={e=>setSearch(e.target.value)} />
          <select style={S.input} value={filters.clinic_id} onChange={e=>setFilters(f=>({...f,clinic_id:e.target.value}))}>
            <option value="">All Clinics</option>
            {clinicList.map(c=><option key={c.id} value={c.id}>{c.clinic_label}</option>)}
          </select>
          <select style={S.input} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
            <option value="">All Statuses</option>
            {STATUS_ORDER.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <select style={S.input} value={filters.warranty_type} onChange={e=>setFilters(f=>({...f,warranty_type:e.target.value}))}>
            <option value="">All Types</option>
            <option>Under Warranty</option><option>Non Warranty</option><option>Re-Repair</option>
          </select>
          <input style={S.input} type="date" value={filters.date_from} onChange={e=>setFilters(f=>({...f,date_from:e.target.value}))} />
          <input style={S.input} type="date" value={filters.date_to}   onChange={e=>setFilters(f=>({...f,date_to:e.target.value}))} />
        </div>
        {(filters.clinic_id||filters.status||filters.warranty_type||filters.date_from||filters.date_to||filters.search) && (
          <div style={{ marginTop:10, display:'flex', justifyContent:'flex-end' }}>
            <button onClick={()=>{setFilters({..._FILTER_DEFAULTS});setSearch('');if(searchInputRef.current)searchInputRef.current.value='';}} style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fca5a5', borderRadius:8, padding:'5px 14px', fontSize:12, fontWeight:700, cursor:'pointer' }}>✕ Clear Filters</button>
          </div>
        )}
      </div>

      {loading ? <LoadingState /> : viewMode === 'kanban' ? (
        <KanbanView
          tickets={displayTickets}
          onDrawer={setDrawerTicket}
          onAction={(ticket, type) => setActionModal({ ticket, type })}
          onDirectAction={(ticket, action) => doAction(ticket, action, {})}
          user={user}
        />
      ) : (
        <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>
                  {user.role !== 'owner' && <th style={{ ...S.th, width:36, padding:'8px 10px' }}>
                    <input type="checkbox" style={{ cursor:'pointer' }}
                      checked={pagedGroups.length>0 && pagedGroups.every(g=>selectedIds.has(g[0].id))}
                      onChange={e => {
                        if (e.target.checked) setSelectedIds(new Set(pagedGroups.map(g=>g[0].id)));
                        else clearSelection();
                      }} />
                  </th>}
                  {['Repair No.','Actions','Clinic','Patient','Mobile','Brand / Model','Status','Days'].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {grouped.length===0 && <tr><td colSpan={user.role!=='owner'?9:8} style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>No repairs found</td></tr>}
                {pagedGroups.map((group, i) => {
                  const t   = group[0]; // most recent ticket for this patient
                  const cnt = group.length;
                  const bg  = t.delay_alert===2?'#fee2e215':t.delay_alert===1?'#fef9c315':i%2===0?'var(--card)':'var(--bg)';
                  const openPatient = () => {
                    setPatientModal({loading:true});
                    api(`patients_repair.php?q=${encodeURIComponent(t.patient_mobile)}`)
                      .then(rows=>{ const p=rows.find(r=>r.mobile===t.patient_mobile)||rows[0]; setPatientModal(p||null); })
                      .catch(()=>setPatientModal(null));
                  };
                  return (
                    <tr key={t.patient_mobile} style={{ background:bg }}>
                      {user.role !== 'owner' && <td style={{ ...S.td, width:36, padding:'8px 10px' }} onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" style={{ cursor:'pointer' }} checked={selectedIds.has(t.id)} onChange={()=>toggleSelect(t.id)} />
                      </td>}
                      <td style={S.td}>
                        <span style={{ fontWeight:900, fontSize:12, color:'var(--accent)', cursor:'pointer', textDecoration:'underline', textUnderlineOffset:3 }} onClick={()=>setDrawerTicket(t)}>{t.repair_no}</span>
                        {t.is_re_repair&&<span style={{ ...S.pill('#dc2626'), fontSize:9, marginLeft:4 }}>RE</span>}
                        {cnt>1&&<div style={{ fontSize:9, color:'var(--muted)', marginTop:2 }}>{cnt} repairs</div>}
                      </td>
                      <td style={{ ...S.td, whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          {t.status==='Form Submitted' && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11, background:'linear-gradient(135deg,#059669,#16a34a)' }} onClick={()=>setActionModal({ticket:t,type:'confirm_received_ho'})}>{t.sending_to==='Direct Company' ? '🏭 To Co.' : '🏢 At HO'}</button>}
                          {t.status==='Form Submitted' && <button style={{ ...S.btnD, padding:'4px 10px', fontSize:11 }} onClick={()=>doAction(t,'delete_ticket')}>🗑️</button>}
                          {t.status==='Courier Sent' && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11 }} onClick={()=>setActionModal({ticket:t,type:'confirm_received_ho'})}>{t.sending_to==='Direct Company'?'✓ At Co.':'✓ At HO'}</button>}
                          {t.status==='Received at HO' && t.sending_to!=='Direct Company' && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11, background:'linear-gradient(135deg,#0891b2,#06b6d4)' }} onClick={()=>setActionModal({ticket:t,type:'sent_to_company'})}>🏭 To Co.</button>}
                          {(t.status==='Sent to Company' || (t.status==='Received at HO' && t.sending_to==='Direct Company')) && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11 }} onClick={()=>setActionModal({ticket:t,type:'submit_estimate_report'})}>💰 Estimate</button>}
                          {(t.status==='Sent to Company' || (t.status==='Received at HO' && t.sending_to==='Direct Company')) && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11, background:'linear-gradient(135deg,#059669,#16a34a)' }} onClick={()=>setActionModal({ticket:t,type:'submit_foc'})}>✅ FOC</button>}
                          {t.status==='Patient Approved' && <button style={{ ...S.btnP, padding:'4px 10px', fontSize:11, background:'linear-gradient(135deg,#0891b2,#06b6d4)' }} onClick={()=>doAction(t,'confirm_work_order')}>🔧 Under Repair</button>}
                          {t.status==='Under Repair' && <button style={{ ...S.btnS, padding:'4px 10px', fontSize:11 }} onClick={()=>setActionModal({ticket:t,type:'dispatch_back'})}>🚚 Dispatch</button>}
                          {t.status==='Patient Rejected' && <button style={{ ...S.btnS, padding:'4px 10px', fontSize:11, borderColor:'#dc2626', color:'#dc2626' }} onClick={()=>setActionModal({ticket:t,type:'dispatch_back'})}>📦 Return</button>}
                          <button title={t.admin_note ? 'Edit note' : 'Add note'} style={{ ...S.btnS, padding:'4px 8px', fontSize:12, background:t.admin_note?'#7c3aed':'', borderColor:t.admin_note?'#5b21b6':'', color:t.admin_note?'#fff':'' }} onClick={()=>setNotesModal({ticket:t, note:t.admin_note||''})}>{t.admin_note ? '✏️' : '📄'}</button>
                          <button title="Share on WhatsApp" style={{ background:'#25D366', color:'#fff', border:'none', borderRadius:6, padding:'4px 8px', fontSize:12, cursor:'pointer' }}
                            onClick={()=>{ const msg=buildWATrackMsg(t,t.patient_name); window.open(`https://wa.me/91${t.patient_mobile}?text=${encodeURIComponent(msg)}`,'_blank'); }}>
                            WA
                          </button>
                        </div>
                      </td>
                      <td style={S.td}><span style={{ fontSize:12 }}>{t.clinic_label}</span></td>
                      <td style={S.td}><span style={{ cursor:'pointer', color:'var(--accent)', fontWeight:700, textDecoration:'underline', textUnderlineOffset:3 }} onClick={openPatient}>{t.patient_name}</span></td>
                      <td style={S.td}><code style={{ fontSize:12 }}>{t.patient_mobile}</code></td>
                      <td style={S.td}>
                        <div style={{ fontSize:12, fontWeight:600 }}>{t.ha_brand}</div>
                        {t.ha_model && <div style={{ fontSize:10, color:'var(--muted)', marginTop:1 }}>{t.ha_model}</div>}
                        {(() => { try { const acc = typeof t.accessories==='string' ? JSON.parse(t.accessories||'[]') : (t.accessories||[]); return acc.length>0 ? acc.map((a,i)=><div key={i} style={{ fontSize:10, color:'#0891b2', marginTop:2 }}>🔌 {a.name}{a.serial?` · ${a.serial}`:''}</div>) : null; } catch { return null; } })()}
                      </td>
                      <td style={S.td}>
                        <span style={{ ...S.pill(STATUS_COLOR[t.status]||'#666'), fontSize:11 }}>{STATUS_ICON[t.status]} {t.status}</span>
                        {t.out_company && <CourierBadge out_company={t.out_company} out_tracking={t.out_tracking} ret_company={t.ret_company} ret_tracking={t.ret_tracking} />}
                        {(() => { try { const ms=t.milestone_dates?JSON.parse(t.milestone_dates):{}; const sd=ms[t.status]; const d=sd?Math.floor((Date.now()-new Date(sd))/86400000):null; return d>=7&&t.status!=='Delivered to Patient'?<span style={{ ...S.pill('#b45309'), fontSize:9, marginTop:3, display:'block' }}>⏰ {d}d here</span>:null; } catch{return null;} })()}
                        {t.holding_note && t.status==='Received at Clinic' && (
                          <div style={{ marginTop:4, padding:'3px 8px', background:'#fef3c7', border:'1px solid #fcd34d', borderRadius:6, fontSize:10, color:'#92400e', fontWeight:600, maxWidth:200 }}>
                            🗒 {t.holding_note}
                          </div>
                        )}
                      </td>
                      <td style={S.td}><strong style={{ color:daysSince(t.repair_date,t.delivered_to_patient||null)>=SLA_DAYS?'var(--danger)':daysSince(t.repair_date,t.delivered_to_patient||null)>SLA_DAYS*0.7?'var(--amber)':'var(--text1)' }}>{daysSince(t.repair_date,t.delivered_to_patient||null)}d</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Bulk action bar */}
      {user.role !== 'owner' && selectedIds.size > 0 && (() => {
        const selTickets = tickets.filter(t => selectedIds.has(t.id));
        const statuses   = [...new Set(selTickets.map(t => t.status))];
        const allCourierSent  = statuses.length===1 && statuses[0]==='Courier Sent';
        const allWorkOrderSent= statuses.length===1 && statuses[0]==='Under Repair';
        return (
          <div style={{ position:'sticky', bottom:16, background:'#1e1b4b', color:'#fff', borderRadius:12, padding:'12px 20px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', boxShadow:'0 8px 32px rgba(0,0,0,0.35)', marginTop:12, zIndex:100 }}>
            <span style={{ fontWeight:700, fontSize:13 }}>{selectedIds.size} selected</span>
            {allCourierSent   && <button onClick={()=>bulkAction('confirm_received_ho','Received at HO')}   style={{ background:'#059669', color:'#fff', border:'none', borderRadius:8, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer' }}>✓ Mark as Received at HO</button>}
            {allWorkOrderSent && <button onClick={()=>bulkAction('confirm_work_order','Under Repair')} style={{ background:'#0891b2', color:'#fff', border:'none', borderRadius:8, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer' }}>🔧 Mark Under Repair</button>}
            {statuses.length===1 && statuses[0]==='Form Submitted' && <button onClick={()=>setBatchCourierModal(true)} style={{ background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, padding:'7px 16px', fontWeight:700, fontSize:12, cursor:'pointer' }}>📦 Batch Courier Entry</button>}
            {!allCourierSent && !allWorkOrderSent && !(statuses.length===1&&statuses[0]==='Form Submitted') && <span style={{ fontSize:12, color:'#a78bfa' }}>Select tickets with the same status to apply bulk action</span>}
            <button onClick={clearSelection} style={{ marginLeft:'auto', background:'transparent', color:'#a78bfa', border:'1px solid #4c3d8a', borderRadius:8, padding:'7px 14px', fontSize:12, cursor:'pointer' }}>✕ Clear</button>
          </div>
        );
      })()}

      {/* Pagination */}
      {tickets.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16, flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:13, color:'var(--muted)' }}>
            Showing <strong>{(page-1)*ADMIN_PAGE_SIZE+1}–{Math.min(page*ADMIN_PAGE_SIZE, grouped.length)}</strong> of <strong>{grouped.length}</strong> patients · <strong>{tickets.length}</strong> total repairs
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ ...S.btnS, padding:'6px 14px', fontSize:12, opacity:page===1?.4:1 }}>← Prev</button>
            {Array.from({length:totalPages},(_,i)=>i+1).filter(p=> p===1||p===totalPages||Math.abs(p-page)<=1).reduce((acc,p,idx,arr)=>{
              if(idx>0&&arr[idx-1]!==p-1) acc.push('…');
              acc.push(p); return acc;
            },[]).map((p,i)=> p==='…'
              ? <span key={`e${i}`} style={{ padding:'0 4px', color:'var(--muted)', fontSize:13 }}>…</span>
              : <button key={p} onClick={()=>setPage(p)} style={{ ...S.btnS, padding:'6px 12px', fontSize:12, minWidth:34, background:page===p?'var(--accent)':'', color:page===p?'#fff':'', borderColor:page===p?'var(--accent)':'' }}>{p}</button>
            )}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ ...S.btnS, padding:'6px 14px', fontSize:12, opacity:page===totalPages?.4:1 }}>Next →</button>
          </div>
        </div>
      )}

      {actionModal && <ActionModal ticket={actionModal.ticket} type={actionModal.type} csrf={csrf} onClose={()=>setActionModal(null)} onDone={(a,e)=>doAction(actionModal.ticket,a,e)} />}

      {notesModal && (
        <Modal title="📝 Internal Note" onClose={()=>setNotesModal(null)} width={480}>
          <div style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>{notesModal.ticket.repair_no} · {notesModal.ticket.patient_name} · {notesModal.ticket.clinic_label}</div>
          <textarea
            style={{ ...S.input, width:'100%', minHeight:120, resize:'vertical' }}
            value={notesModal.note}
            onChange={e => setNotesModal(m => ({ ...m, note: e.target.value }))}
            placeholder="Visible only to admin &amp; owner…"
            autoFocus
          />
          <div style={{ display:'flex', gap:10, marginTop:14, justifyContent:'flex-end' }}>
            <button style={S.btnS} onClick={()=>setNotesModal(null)}>Cancel</button>
            <button style={S.btnP} onClick={async () => {
              try {
                await api(`tickets.php?id=${notesModal.ticket.id}`, { method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({ action:'save_note', admin_note:notesModal.note, csrf_token:csrf }) });
                showToast('Note saved'); setNotesModal(null);
                reload();
              } catch(e) { showToast(e.message,'error'); }
            }}>Save</button>
          </div>
        </Modal>
      )}

      <div style={{ marginTop:32, padding:'18px 24px', background:'linear-gradient(135deg,#7c3aed10,#7c3aed04)', border:'1px solid #7c3aed18', borderRadius:14, display:'flex', alignItems:'center', gap:18 }}>
        <div style={{ fontSize:22, flexShrink:0 }}>💬</div>
        <div>
          <div style={{ fontSize:10, color:'#7c3aed', fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', marginBottom:6 }}>Today's Thought</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:15, color:'var(--text1)', fontStyle:'italic', lineHeight:1.5 }}>"{quote.q}"</div>
          <div style={{ fontSize:11, color:'var(--muted)', marginTop:5 }}>{quote.a}</div>
        </div>
      </div>
    </div>
    {patientModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:1100, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px 16px', overflowY:'auto' }} onClick={()=>setPatientModal(null)}>
        <div style={{ ...S.card, maxWidth:860, width:'100%', padding:0, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
          {patientModal.loading ? (
            <div style={{ padding:48, textAlign:'center' }}><LoadingState /></div>
          ) : (
            <div style={{ padding:24, maxHeight:'85vh', overflowY:'auto' }}>
              <PatientProfile patient={patientModal} onBack={()=>setPatientModal(null)} backLabel="✕ Close" />
            </div>
          )}
        </div>
      </div>
    )}
    {drawerTicket && <RepairDrawer ticket={drawerTicket} user={user} csrf={csrf} onClose={()=>setDrawerTicket(null)} onAction={(a)=>{ setDrawerTicket(null); setActionModal(a); }} />}
    {batchCourierModal && <BatchCourierModal count={selectedIds.size} onClose={()=>setBatchCourierModal(false)} onDone={batchCourierSubmit} />}
    </>
  );
}); // React.memo

// ═════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═════════════════════════════════════════════════════════════
function NotificationsPanel({ user, csrf, notifications = [], onRefresh }) {
  const [localNotes, setLocalNotes] = useState(notifications);
  const [clinicFilter, setClinicFilter] = useState('all');
  useEffect(()=>{ setLocalNotes(notifications); }, [notifications]);

  // Build clinic list from notifications that have a clinic_label (admin/owner only)
  const clinics = useMemo(() => {
    const map = {};
    localNotes.forEach(n => {
      if (n.clinic_label && n.clinic_account_id) {
        if (!map[n.clinic_account_id]) map[n.clinic_account_id] = { id: n.clinic_account_id, label: n.clinic_label, unread: 0 };
        if (!n.is_read) map[n.clinic_account_id].unread++;
      }
    });
    return Object.values(map).sort((a,b) => b.unread - a.unread || a.label.localeCompare(b.label));
  }, [localNotes]);

  const visible = useMemo(() => {
    if (clinicFilter === 'all') return localNotes;
    if (clinicFilter === 'other') return localNotes.filter(n => !n.clinic_account_id);
    return localNotes.filter(n => String(n.clinic_account_id) === String(clinicFilter));
  }, [localNotes, clinicFilter]);

  const unread      = localNotes.filter(n=>!n.is_read).length;
  const visibleUnread = visible.filter(n=>!n.is_read).length;

  async function markAll() {
    const body = clinicFilter === 'all' || clinicFilter === 'other'
      ? { action:'mark_all_read' }
      : { action:'mark_all_read', clinic_id: clinicFilter };
    await api('notifications.php',{ method:'POST', body:JSON.stringify(body) }).catch(()=>{});
    setLocalNotes(n => n.map(x => {
      if (clinicFilter === 'all') return {...x, is_read:1};
      if (clinicFilter === 'other') return !x.clinic_account_id ? {...x, is_read:1} : x;
      return String(x.clinic_account_id) === String(clinicFilter) ? {...x, is_read:1} : x;
    }));
    if (onRefresh) onRefresh();
  }

  const showClinicFilter = user.role !== 'clinic' && clinics.length > 0;

  return (
    <div className="fadeUp">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={S.pageTitle}>Notifications</div>
          <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>{unread} unread · {localNotes.length} total</div>
        </div>
        {visibleUnread > 0 && <button onClick={markAll} style={S.btnS}>✓ Mark {clinicFilter==='all'?'all':'visible'} read</button>}
      </div>

      {showClinicFilter && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
          <button
            onClick={()=>setClinicFilter('all')}
            style={{ ...S.btnS, fontSize:12, padding:'6px 14px', background:clinicFilter==='all'?'var(--accent)':'', color:clinicFilter==='all'?'#fff':'', borderColor:clinicFilter==='all'?'var(--accent)':'' }}
          >
            All
            {unread > 0 && <span style={{ marginLeft:6, background:clinicFilter==='all'?'rgba(255,255,255,.3)':'var(--accent)', color:'#fff', borderRadius:20, fontSize:10, padding:'1px 7px', fontWeight:800 }}>{unread}</span>}
          </button>
          {clinics.map(c => (
            <button
              key={c.id}
              onClick={()=>setClinicFilter(String(c.id))}
              style={{ ...S.btnS, fontSize:12, padding:'6px 14px', background:String(clinicFilter)===String(c.id)?'var(--accent)':'', color:String(clinicFilter)===String(c.id)?'#fff':'', borderColor:String(clinicFilter)===String(c.id)?'var(--accent)':'' }}
            >
              {c.label}
              {c.unread > 0 && <span style={{ marginLeft:6, background:String(clinicFilter)===String(c.id)?'rgba(255,255,255,.3)':'var(--danger)', color:'#fff', borderRadius:20, fontSize:10, padding:'1px 7px', fontWeight:800 }}>{c.unread}</span>}
            </button>
          ))}
        </div>
      )}

      {visible.length===0 ? <EmptyState icon="🔔" title="No notifications" sub={clinicFilter==='all'?"You're all caught up!":"No notifications from this clinic."} /> : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {visible.map(n=>(
            <div key={n.id} className="card-hover" style={{ ...S.card, borderLeft:`4px solid ${n.is_read?'var(--border)':'var(--accent)'}`, padding:'14px 20px', opacity:n.is_read?.7:1 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                <div>
                  {n.clinic_label && clinicFilter === 'all' && (
                    <div style={{ fontSize:10, fontWeight:800, color:'var(--accent)', letterSpacing:.5, textTransform:'uppercase', marginBottom:4 }}>{n.clinic_label}</div>
                  )}
                  <div style={{ fontWeight:n.is_read?400:700, fontSize:14, color:'var(--text1)' }}>{n.message}</div>
                </div>
                {!n.is_read && <span style={{ ...S.pill('#7c3aed'), fontSize:10, flexShrink:0 }}>New</span>}
              </div>
              <div style={{ fontSize:11, color:'var(--muted)', marginTop:5 }}>{fmtDate(n.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// CLINIC MANAGEMENT
// ═════════════════════════════════════════════════════════════
function ClinicManagement({ user, csrf, showToast }) {
  const [clinics, setClinics] = useState([]);
  const [slipmsClinicList, setSlipmsClinicList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(()=>{
    setLoading(true);
    Promise.all([api('accounts.php?action=list'),api('accounts.php?action=slipms_clinics')])
      .then(([cl,sl])=>{ setClinics(cl); setSlipmsClinicList(sl); }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);
  useEffect(()=>{ load(); },[load]);

  async function toggle(id, active) {
    await api(`accounts.php?id=${id}`,{ method:'PUT', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({is_active:active?1:0,csrf_token:csrf}) });
    load();
  }

  return (
    <div className="fadeUp">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={S.pageTitle}>Clinic Accounts</div>
          <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>{clinics.filter(c=>c.is_active).length} active · {clinics.length} total</div>
        </div>
        <button onClick={()=>setShowAdd(true)} style={S.btnP}>+ Add Clinic</button>
      </div>

      {loading ? <LoadingState /> : clinics.length===0 ? <EmptyState icon="🏥" title="No clinics yet" sub="Add a clinic to get started." /> : (
        <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>
                  {['Clinic ID','Clinic','City','Username','Email','Active Repairs','Status','Action'].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {clinics.map((c,i)=>(
                  <tr key={c.id} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                    <td style={S.td}><code style={{ fontSize:12, color:'var(--accent)' }}>{c.clinic_id}</code></td>
                    <td style={S.td}><div style={{ fontWeight:700 }}>{c.clinic_label}</div><div style={{ fontSize:11, color:'var(--muted)' }}>{c.name}</div></td>
                    <td style={S.td}>{c.city}</td>
                    <td style={S.td}><code style={{ fontSize:12 }}>{c.username}</code></td>
                    <td style={S.td}><span style={{ fontSize:12 }}>{c.email}</span></td>
                    <td style={{ ...S.td, textAlign:'center' }}>
                      <span style={{ ...S.pill(c.active_repairs===0?'#6b7280':c.active_repairs>10?'#dc2626':'#d97706'), fontSize:12, fontWeight:800 }}>
                        {c.active_repairs}
                      </span>
                    </td>
                    <td style={S.td}><span style={S.pill(c.is_active?'#059669':'#dc2626')}>{c.is_active?'Active':'Inactive'}</span></td>
                    <td style={S.td}><button onClick={()=>toggle(c.id,!c.is_active)} style={c.is_active?{...S.btnD,padding:'5px 14px',fontSize:12}:{...S.btnS,padding:'5px 14px',fontSize:12}}>{c.is_active?'Deactivate':'Activate'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && <AddClinicModal csrf={csrf} slipmsClinicList={slipmsClinicList} onClose={()=>setShowAdd(false)} onCreated={d=>{ setCreated(d); setShowAdd(false); load(); }} showToast={showToast} />}
      {created && <CreatedClinicModal data={created} onClose={()=>setCreated(null)} />}
    </div>
  );
}

function AddClinicModal({ csrf, slipmsClinicList, onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ name:'', clinic_label:'', clinic_code:'', city:'', phone:'', email:'' });
  const [submitting, setSubmitting] = useState(false);

  function importFromSlipms(e) {
    const clinic = slipmsClinicList.find(c=>c.clinic_id===e.target.value);
    if (!clinic) return;
    setForm(f=>({...f, clinic_label:clinic.clinic_label||'', name:clinic.name||'', clinic_code:(clinic.clinic_id||'').toUpperCase().slice(0,6)}));
  }

  async function submit(e) {
    e.preventDefault(); setSubmitting(true);
    try { const r = await api('accounts.php',{ method:'POST', headers:{'X-CSRF-Token':csrf}, body:JSON.stringify({...form,csrf_token:csrf}) }); onCreated(r); }
    catch(e) { showToast(e.message,'error'); }
    finally { setSubmitting(false); }
  }

  return (
    <Modal title="Add Clinic Account" onClose={onClose}>
      {slipmsClinicList.length>0 && (
        <div style={{ marginBottom:18, padding:'14px 16px', background:'var(--bg3)', borderRadius:12 }}>
          <label style={S.label}>Import from SoundLife PMS</label>
          <select style={S.input} defaultValue="" onChange={importFromSlipms}>
            <option value="">— Select clinic to auto-fill —</option>
            {slipmsClinicList.map(c=><option key={c.clinic_id} value={c.clinic_id}>{c.clinic_label} ({c.clinic_id})</option>)}
          </select>
        </div>
      )}
      <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div><label style={S.label}>Contact Name</label><input style={S.input} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required /></div>
        <div><label style={S.label}>Clinic Display Name</label><input style={S.input} value={form.clinic_label} onChange={e=>setForm(f=>({...f,clinic_label:e.target.value}))} required /></div>
        <div><label style={S.label}>Repair Code (3-6 chars)</label><input style={S.input} value={form.clinic_code} onChange={e=>setForm(f=>({...f,clinic_code:e.target.value.replace(/[^A-Za-z0-9]/g,'').toUpperCase().slice(0,6)}))} placeholder="MUM / BLR / JUH" required /></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div><label style={S.label}>City</label><input style={S.input} value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} /></div>
          <div><label style={S.label}>Phone</label><input style={S.input} value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} /></div>
        </div>
        <div><label style={S.label}>Email (for OTP)</label><input style={S.input} type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required /></div>
        <div style={{ display:'flex', gap:10, paddingTop:4 }}>
          <button type="submit" style={S.btnP} disabled={submitting}>{submitting?'Creating…':'Create Account'}</button>
          <button type="button" onClick={onClose} style={S.btnS}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function CreatedClinicModal({ data, onClose }) {
  return (
    <Modal title="✅ Clinic Account Created" onClose={onClose}>
      <div style={{ background:'var(--greenBg)', border:'1.5px solid #86efac', borderRadius:12, padding:20, marginBottom:18 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--green)', marginBottom:14 }}>⚠️ Save these credentials — shown only once!</div>
        {[['Clinic ID',data.clinic_id],['Username',data.username],['Password',data.password],['Display Name',data.clinic_label]].map(([k,v])=>(
          <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #bbf7d0' }}>
            <span style={{ fontSize:13, color:'var(--green)', fontWeight:600 }}>{k}</span>
            <code style={{ fontSize:14, fontWeight:900, background:'var(--greenBg)', color:'var(--green)', padding:'3px 10px', borderRadius:8 }}>{v}</code>
          </div>
        ))}
      </div>
      <button onClick={onClose} style={S.btnP}>I have saved these credentials →</button>
    </Modal>
  );
}

// ═════════════════════════════════════════════════════════════
// EXPORT TAB
// ═════════════════════════════════════════════════════════════
function ExportTab({ user }) {
  const _d = new Date();
  const today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
  const [from, setFrom] = useState(today.slice(0,8)+'01');
  const [to, setTo]     = useState(today);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinicFilter, setClinicFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    // Use same endpoint as AdminDashboard for clinic list
    api('accounts.php?action=list').then(d => setClinics(Array.isArray(d) ? d : [])).catch(()=>{});
  }, []);

  function load() {
    setLoading(true);
    let qs = `date_from=${from}&date_to=${to}`;
    if (clinicFilter) qs += `&clinic_id=${clinicFilter}`;
    if (statusFilter) qs += `&status=${encodeURIComponent(statusFilter)}`;
    api(`tickets.php?${qs}`)
      .then(d => setTickets(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function fmtD(v) {
    if (!v) return '—';
    try { return new Date(v).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}); } catch(e) { return v; }
  }
  function complaint(t) { return [t.complaint||'', t.complaint_other||''].filter(Boolean).join(', ') || '—'; }
  function serialNo(t) { return [t.serial_left, t.serial_right].filter(Boolean).join(' / ') || '—'; }
  function estimate(t) {
    if (t.company_report_type === 'FOC') return 'FOC';
    const parts = [];
    if (+t.amount_left > 0)  parts.push(`L Rs.${Number(t.amount_left).toLocaleString('en-IN')}`);
    if (+t.amount_right > 0) parts.push(`R Rs.${Number(t.amount_right).toLocaleString('en-IN')}`);
    if (parts.length) return parts.join(' + ');
    if (t.estimate_amount) return `Rs.${Number(t.estimate_amount).toLocaleString('en-IN')}`;
    return '—';
  }
  function appDecision(t) {
    if (t.company_report_type === 'FOC') return 'FOC';
    const dl = t.decision_left, dr = t.decision_right;
    if (dl || dr) { const p=[]; if(dl) p.push(`L:${dl}`); if(dr) p.push(`R:${dr}`); return p.join(' / '); }
    return t.patient_decision || '—';
  }
  function sendTo(t) {
    if (t.sending_to === 'Direct Company') return t.direct_company || 'Direct Co.';
    return 'Head Office';
  }

  const COLS = ['DATE','STATUS','RECEIVER NAME','PATIENT NAME','MOBILE','HA BRAND','HA MODEL','HA TYPE','SERIAL (L)','SERIAL (R)','WARRANTY','REMARKS','PROBLEM','SEND TO','SEND DATE','SEND POD','ESTIMATE','APP / NOT APP','APP DATE','DEL DATE'];

  // Group tickets by patient_mobile, latest repair first per patient
  const grouped = useMemo(() => {
    const map = {};
    tickets.forEach(t => {
      const key = t.patient_mobile || t.id;
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return Object.values(map).sort((a,b) => new Date(b[0].repair_date) - new Date(a[0].repair_date));
  }, [tickets]);

  function rowData(t) {
    const haType = Array.isArray(t.ha_type) ? t.ha_type.join(', ') : (t.ha_type || '—');
    return [
      fmtD(t.repair_date),
      t.status,
      t.clinic_label || '—',
      t.patient_name,
      t.patient_mobile || '—',
      t.ha_brand || '—',
      t.ha_model || '—',
      haType,
      t.serial_left || '—',
      t.serial_right || '—',
      t.warranty_type,
      t.remarks || '—',
      complaint(t),
      sendTo(t),
      fmtD(t.out_dispatched_at),
      t.out_tracking ? String(t.out_tracking) : '—',  // keep as string — prevents scientific notation in Excel
      estimate(t),
      appDecision(t),
      fmtD(t.decided_at),
      fmtD(t.delivered_to_patient),
    ];
  }

  function repairSummary(t) {
    return `${t.repair_no} | ${[t.ha_brand,t.ha_model].filter(Boolean).join(' ')} | ${t.status} | ${fmtD(t.repair_date)}`;
  }

  function exportExcel() {
    const periodLabel = `${fmtD(from+'T00:00:00')} — ${fmtD(to+'T00:00:00')}`;
    const nc = COLS.length;

    // Status colour map for cells
    const statusColor = { 'Delivered to Patient':'#dcfce7','Patient Approved':'#d1fae5','Under Repair':'#dbeafe','Patient Rejected':'#fee2e2','Dispatched to Branch':'#dbeafe','Received at Clinic':'#ede9fe','Form Submitted':'#fef9c3','Courier Sent':'#dbeafe','Received at HO':'#dcfce7','Estimate Received':'#ffedd5','Patient Informed':'#ede9fe' };

    const cell = (val, opts={}) => {
      const { bg='#ffffff', color='#1e1b4b', bold=false, align='left', size=9, wrap=false, border=true, asText=false } = opts;
      const borderStyle = border ? 'border:1px solid #c4b5fd;' : '';
      const textFormat = asText ? 'mso-number-format:"\\@";' : '';
      // x:str tells Excel to treat cell content as text, preventing scientific notation
      const xStr = asText ? ' x:str' : '';
      return `<td${xStr} style="background:${bg};color:${color};font-weight:${bold?'bold':'normal'};text-align:${align};font-size:${size}pt;padding:5px 7px;${borderStyle}${textFormat}${wrap?'white-space:pre-wrap;':'white-space:nowrap;'}vertical-align:top;">${val ?? ''}</td>`;
    };

    // Expand repair history into separate columns: Repair 1, Repair 2, ...
    const maxRepairs = Math.max(...grouped.map(g => g.length), 1);
    const repairHistoryCols = Array.from({ length: maxRepairs }, (_, i) =>
      i === 0 ? 'REPAIR 1 (LATEST)' : `REPAIR ${i + 1}`
    );
    const rows = grouped.map(group => {
      const base = rowData(group[0]);
      for (let i = 0; i < maxRepairs; i++) {
        base.push(group[i] ? repairSummary(group[i]) : '—');
      }
      return base;
    });

    const headerRow = COLS.concat(repairHistoryCols).map(c =>
      cell(c, { bg:'#5b21b6', color:'#ffffff', bold:true, align:'center', size:8 })
    ).join('');

    const dataRows = rows.map((row, ri) => {
      const altBg = ri%2===0 ? '#ffffff' : '#f5f3ff';
      const status = row[1];
      const statusBg = statusColor[status] || altBg;
      return '<tr>' + row.map((val, ci) => {
        const bg = ci===1 ? statusBg : altBg;
        const isHistory = ci >= COLS.length; // history columns start after base columns
        return cell(val, { bg, size:8, wrap:isHistory, border:true, asText: ci === 15 });
      }).join('') + '</tr>';
    }).join('');

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"/>
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Repairs</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head><body>
<table style="border-collapse:collapse;font-family:Arial,sans-serif;">
  <tr><td colspan="${nc}" style="background:#1e1b4b;color:#ffffff;font-size:14pt;font-weight:bold;padding:12px 14px;border:none;">🔊 SoundLife Service — Repair Export Report</td></tr>
  <tr><td colspan="${nc}" style="background:#4c1d95;color:#c4b5fd;font-size:9pt;padding:6px 14px;border:none;">Period: ${periodLabel} &nbsp;·&nbsp; ${tickets.length} Records &nbsp;·&nbsp; Generated: ${new Date().toLocaleString('en-IN')}</td></tr>
  <tr><td colspan="${nc}" style="background:#7c3aed;height:3px;border:none;"></td></tr>
  <tr>${headerRow}</tr>
  ${dataRows}
</table>
</body></html>`;

    const blob = new Blob(['\ufeff' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `SoundLife-Repairs-${from}-to-${to}.xls`; a.click();
    URL.revokeObjectURL(url);
  }

  function exportPDF() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.setFillColor(30, 27, 75);
    doc.rect(0, 0, pw, 24, 'F');
    doc.setFillColor(124, 58, 237);
    doc.rect(0, 20, pw, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(17);
    doc.setFont('helvetica', 'bold');
    doc.text('SoundLife Service', 12, 11);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Repair Export Report', 12, 18);
    doc.text(`Period: ${fmtD(from+'T00:00:00')} — ${fmtD(to+'T00:00:00')}   ·   ${grouped.length} patients · ${tickets.length} repairs`, pw - 12, 18, { align: 'right' });

    autoTable(doc, {
      startY: 28,
      head: [COLS],
      body: tickets.map(rowData),
      styles: { fontSize: 6, cellPadding: 2, overflow: 'linebreak', font: 'helvetica', textColor: [20,20,40] },
      headStyles: { fillColor: [91, 33, 182], textColor: [255,255,255], fontStyle: 'bold', fontSize: 6.5, halign: 'center', cellPadding: 2.5 },
      alternateRowStyles: { fillColor: [245, 243, 255] },
      rowStyles: { lineColor: [220,220,235], lineWidth: 0.1 },
      columnStyles: {
        0:{cellWidth:15}, 1:{cellWidth:22}, 2:{cellWidth:22}, 3:{cellWidth:22},
        4:{cellWidth:18}, 5:{cellWidth:17}, 6:{cellWidth:17}, 7:{cellWidth:15},
        8:{cellWidth:18}, 9:{cellWidth:18}, 10:{cellWidth:14}, 11:{cellWidth:20},
        12:{cellWidth:20}, 13:{cellWidth:15}, 14:{cellWidth:14}, 15:{cellWidth:18},
        16:{cellWidth:14}, 17:{cellWidth:14}, 18:{cellWidth:13}, 19:{cellWidth:13},
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const statusColors = { 'Delivered to Patient':[220,252,231],'Patient Approved':[209,250,229],'Under Repair':[219,234,254],'Patient Rejected':[254,226,226],'Dispatched to Branch':[219,234,254],'Received at Clinic':[237,233,254],'Form Submitted':[254,249,195],'Estimate Received':[255,237,213],'Patient Informed':[237,233,254] };
          const col = statusColors[data.cell.text[0]];
          if (col) { doc.setFillColor(...col); doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F'); doc.setTextColor(20,20,40); doc.setFontSize(6); doc.text(data.cell.text[0]||'', data.cell.x+2, data.cell.y+data.cell.height/2+2); }
        }
      },
      margin: { left: 7, right: 7 },
      didDrawPage: (data) => {
        doc.setFontSize(7);
        doc.setTextColor(160,160,180);
        doc.text(`Page ${data.pageNumber}`, pw/2, ph-5, { align:'center' });
        doc.text('SoundLife Service Portal — Confidential', 10, ph-5);
        doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, pw-10, ph-5, { align:'right' });
      },
    });
    doc.save(`SoundLife-Repairs-${from}-to-${to}.pdf`);
  }

  return (
    <div className="fadeUp">
      <div style={{ background:'linear-gradient(135deg,#1e1b4b,#4c1d95,#7c3aed)', borderRadius:20, padding:'28px 32px', marginBottom:24, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-40, right:-40, width:180, height:180, borderRadius:'50%', background:'rgba(255,255,255,.04)', pointerEvents:'none' }}/>
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:11, color:'#a78bfa', fontWeight:700, letterSpacing:2, textTransform:'uppercase', marginBottom:6 }}>Data Export</div>
          <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:24, color:'#fff', fontWeight:700, marginBottom:4 }}>Export Repairs</div>
          <div style={{ fontSize:13, color:'#c4b5fd' }}>Download repair data as Excel or PDF · {COLS.length} columns · one row per repair entry</div>
        </div>
      </div>

      <div style={{ ...S.card, marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:14, color:'var(--text1)' }}>🔍 Filter Records</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:16 }}>
          <div><label style={S.label}>From Date</label><input style={S.input} type="date" value={from} onChange={e=>setFrom(e.target.value)} /></div>
          <div><label style={S.label}>To Date</label><input style={S.input} type="date" value={to} onChange={e=>setTo(e.target.value)} /></div>
          <div>
            <label style={S.label}>Clinic</label>
            <select style={S.input} value={clinicFilter} onChange={e=>setClinicFilter(e.target.value)}>
              <option value="">All Clinics</option>
              {clinics.map(c=><option key={c.id} value={c.id}>{c.clinic_label}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Status</label>
            <select style={S.input} value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
              <option value="">All Statuses</option>
              {STATUS_ORDER.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button style={{ ...S.btnP, padding:'10px 28px', fontSize:14 }} onClick={load} disabled={loading}>
          {loading ? '⏳ Loading…' : '🔍 Fetch Records'}
        </button>
      </div>

      {loading && <LoadingState />}

      {!loading && tickets.length > 0 && (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--text1)' }}>
              ✓ {tickets.length} records · {from} → {to}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button style={{ ...S.btnP, padding:'10px 22px', background:'linear-gradient(135deg,#059669,#16a34a)', display:'flex', alignItems:'center', gap:8 }} onClick={exportExcel}>
                <span style={{ fontSize:16 }}>📊</span> Export Excel
              </button>
              <button style={{ ...S.btnP, padding:'10px 22px', background:'linear-gradient(135deg,#dc2626,#b91c1c)', display:'flex', alignItems:'center', gap:8 }} onClick={exportPDF}>
                <span style={{ fontSize:16 }}>📄</span> Export PDF
              </button>
            </div>
          </div>

          <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'12px 20px', background:'linear-gradient(90deg,#1e1b4b,#7c3aed)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>Preview — first 10 records</span>
              <span style={{ fontSize:11, color:'#c4b5fd' }}>{tickets.length} total records in export</span>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead>
                  <tr style={{ background:'#7c3aed18' }}>
                    {COLS.map(c=><th key={c} style={{ ...S.th, background:'transparent', color:'var(--accent)', fontSize:10, padding:'8px 10px', whiteSpace:'nowrap' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {tickets.slice(0,10).map((t,i)=>(
                    <tr key={t.id} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                      {rowData(t).map((v,j)=>(
                        <td key={j} style={{ ...S.td, fontSize:11, whiteSpace:'nowrap', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis' }} title={String(v)}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tickets.length > 10 && <div style={{ padding:'10px 20px', fontSize:12, color:'var(--muted)', borderTop:'1px solid var(--border)' }}>… and {tickets.length-10} more records in the full export</div>}
          </div>
        </>
      )}
      {!loading && tickets.length === 0 && (
        <EmptyState icon="📤" title="No data yet" sub="Set your date range and click Fetch Records to preview and export" />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// REPORTS
// ═════════════════════════════════════════════════════════════
function exportReportXLSX(data, from, to) {
  const XLSX = window.XLSX || require('xlsx');

  // ── Colour palette — matches app UI ────────────────────────
  const C = {
    brandPurple:  '7C3AED', brandLight:  'EDE9FE',
    headerBg:     '7C3AED', headerFg:    'FFFFFF',   // vivid purple title
    sectionBg:    '8B5CF6', sectionFg:   'FFFFFF',   // medium purple sections
    subHeaderBg:  'C4B5FD', subHeaderFg: '4C1D95',   // lavender col headers
    altRow:       'F5F3FF', white:        'FFFFFF',
    green:        '059669', greenBg:     'D1FAE5',
    amber:        'D97706', amberBg:     'FEF3C7',
    red:          'DC2626', redBg:       'FEE2E2',
    blue:         '2563EB', blueBg:      'DBEAFE',
    teal:         '0891B2', tealBg:      'CFFAFE',
    muted:        '6B7280',
  };

  const bold      = (sz=11) => ({ font:{ bold:true, sz, color:{rgb:C.headerFg} } });
  const hdrCell   = (bg, fg='FFFFFF', sz=11) => ({
    font:{ bold:true, sz, color:{rgb:fg} },
    fill:{ fgColor:{rgb:bg} },
    alignment:{ horizontal:'center', vertical:'center', wrapText:true },
    border:{ bottom:{style:'thin',color:{rgb:'CCCCCC'}} },
  });
  const bodyCell  = (bg=C.white) => ({
    fill:{ fgColor:{rgb:bg} },
    alignment:{ vertical:'center' },
    border:{ bottom:{style:'hair',color:{rgb:'E5E7EB'}} },
  });
  const numCell   = (bg=C.white, color=C.brandPurple) => ({
    font:{ bold:true, color:{rgb:color} },
    fill:{ fgColor:{rgb:bg} },
    alignment:{ horizontal:'center', vertical:'center' },
    border:{ bottom:{style:'hair',color:{rgb:'E5E7EB'}} },
  });

  const ws = {};
  let r = 0; // current row (0-indexed)

  function setCell(col, row, value, style={}) {
    const addr = XLSX.utils.encode_cell({c:col, r:row});
    ws[addr] = { v:value, t: typeof value==='number'?'n':'s', s:style };
  }
  function merge(c1,r1,c2,r2) {
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({s:{c:c1,r:r1},e:{c:c2,r:r2}});
  }
  function sectionTitle(title, cols=7) {
    setCell(0, r, title, {
      font:{bold:true, sz:11, color:{rgb:C.sectionFg}},
      fill:{fgColor:{rgb:C.sectionBg}},
      alignment:{horizontal:'left', vertical:'center'},
    });
    merge(0,r, cols,r);
    r++;
  }
  function blankRow() { r++; }

  // ── Title banner ────────────────────────────────────────────
  // Row 0 — brand title
  setCell(0, r, '🎧  SoundLife Speech & Hearing Clinic', {
    font:{bold:true, sz:18, color:{rgb:'FFFFFF'}},
    fill:{fgColor:{rgb:'7C3AED'}},
    alignment:{horizontal:'center', vertical:'center'},
  });
  merge(0,r, 9,r); r++;

  // Row 1 — report subtitle (lighter purple)
  setCell(0, r, `Service Report   ·   ${from}   →   ${to}`, {
    font:{bold:false, sz:12, color:{rgb:'EDE9FE'}},
    fill:{fgColor:{rgb:'6D28D9'}},
    alignment:{horizontal:'center', vertical:'center'},
  });
  merge(0,r, 9,r); r++;

  // Row 2 — thin accent stripe
  for (let c=0; c<=9; c++) {
    setCell(c, r, '', { fill:{fgColor:{rgb:'C4B5FD'}} });
  }
  r++;
  blankRow();

  // ── Summary KPI row ─────────────────────────────────────────
  const kpis = [
    ['🔧 Total Repairs', data.summary.total_repairs||0, C.brandPurple, C.brandLight],
    ['⚡ Still Active',  data.summary.active_repairs||0, C.blue,        C.blueBg],
    ['💰 Non-Warranty',  data.summary.non_warranty||0,   C.amber,       C.amberBg],
    ['🔁 Re-Repairs',    data.summary.re_repairs||0,     C.red,         C.redBg],
  ];
  kpis.forEach(([label,,fg,bg], i) => {
    setCell(i*2, r, label, { font:{bold:true,sz:9,color:{rgb:fg}}, fill:{fgColor:{rgb:bg}}, alignment:{horizontal:'center'} });
    merge(i*2,r, i*2+1,r);
  });
  r++;
  kpis.forEach(([,val,fg,bg], i) => {
    setCell(i*2, r, val, { font:{bold:true,sz:20,color:{rgb:fg}}, fill:{fgColor:{rgb:bg}}, alignment:{horizontal:'center'} });
    merge(i*2,r, i*2+1,r);
  });
  r++;
  blankRow();

  // ── Repairs by Clinic & Month ────────────────────────────────
  sectionTitle('REPAIRS BY CLINIC & MONTH', 2);
  ['Clinic','Month','Count'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.clinic_monthly||[]).forEach((row,i) => {
    const bg = i%2===0 ? C.white : C.altRow;
    setCell(0,r,row.clinic_label, bodyCell(bg));
    setCell(1,r,row.month,        bodyCell(bg));
    setCell(2,r,row.cnt,          numCell(bg));
    r++;
  });
  blankRow();

  // ── Average Turnaround ───────────────────────────────────────
  sectionTitle('AVERAGE TURNAROUND TIME', 2);
  ['Clinic','Avg Days','Delivered'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.turnaround||[]).forEach((row,i) => {
    const bg = i%2===0 ? C.white : C.altRow;
    setCell(0,r,row.clinic_label,     bodyCell(bg));
    setCell(1,r,Number(row.avg_days), numCell(bg, row.avg_days>5?C.red:C.green));
    setCell(2,r,row.total,            numCell(bg));
    r++;
  });
  blankRow();

  // ── Re-Repair Rate ───────────────────────────────────────────
  sectionTitle('RE-REPAIR RATE BY BRAND', 3);
  ['Brand','Total','Re-Repairs','Rate %'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.re_repair||[]).forEach((row,i) => {
    const bg = i%2===0 ? C.white : C.altRow;
    const rate = Number(row.re_rate);
    setCell(0,r,row.ha_brand,  bodyCell(bg));
    setCell(1,r,row.total,     numCell(bg));
    setCell(2,r,row.re_repairs,numCell(bg));
    setCell(3,r,`${rate}%`,    numCell(bg, rate>5?C.red:rate>0?C.amber:C.green));
    r++;
  });
  blankRow();

  // ── Delivery Delays ──────────────────────────────────────────
  sectionTitle('DELIVERY DELAYS', 3);
  ['Clinic','Avg Delay Days','Delivered','Red Alerts'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.delay_leaders||[]).forEach((row,i) => {
    const bg = i%2===0 ? C.white : C.altRow;
    setCell(0,r,row.clinic_label,      bodyCell(bg));
    setCell(1,r,Number(row.avg_delay), numCell(bg, row.avg_delay>3?C.red:C.green));
    setCell(2,r,row.delivered,         numCell(bg));
    setCell(3,r,row.red_alerts,        numCell(bg, row.red_alerts>0?C.red:C.green));
    r++;
  });
  blankRow();

  // ── Stale Repairs ────────────────────────────────────────────
  sectionTitle('STALE REPAIRS  (> 10 DAYS)', 5);
  ['Repair No','Clinic','Patient','Status','Warranty','Days Open'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.stale||[]).forEach((row,i) => {
    const days = Number(row.days_open);
    const bg   = days>=15 ? C.redBg : days>=10 ? C.amberBg : (i%2===0?C.white:C.altRow);
    setCell(0,r,row.repair_no,             bodyCell(bg));
    setCell(1,r,row.clinic_label,          bodyCell(bg));
    setCell(2,r,row.patient_name,          bodyCell(bg));
    setCell(3,r,row.status,                bodyCell(bg));
    setCell(4,r,row.warranty_type||'—',    bodyCell(bg));
    setCell(5,r,days,                      numCell(bg, days>=15?C.red:C.amber));
    r++;
  });
  blankRow();

  // ── Company-wise Breakdown ───────────────────────────────────
  sectionTitle('COMPANY-WISE BREAKDOWN', 7);
  ['Brand','Total','Re-Repairs','Re-Rate %','Non-Warranty','FOC','Estimate','Avg Days'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.brand_breakdown||[]).forEach((row,i) => {
    const bg = i%2===0 ? C.white : C.altRow;
    setCell(0,r,row.ha_brand,      bodyCell(bg));
    setCell(1,r,row.total,         numCell(bg));
    setCell(2,r,row.re_repairs,    numCell(bg));
    setCell(3,r,`${row.re_rate}%`, numCell(bg, row.re_rate>5?C.red:C.green));
    setCell(4,r,row.non_warranty,  numCell(bg));
    setCell(5,r,row.foc_count,     numCell(bg));
    setCell(6,r,row.estimate_count,numCell(bg));
    setCell(7,r,row.avg_days||'—', numCell(bg));
    r++;
  });
  blankRow();

  // ── Warranty Expiry Tracker ──────────────────────────────────
  sectionTitle('WARRANTY EXPIRY TRACKER', 9);
  ['Repair No','Clinic','Patient','Mobile','Brand','Model','Warranty (Mo.)','Delivered','Expiry Date','Days Left'].forEach((h,i)=>setCell(i,r,h,hdrCell(C.subHeaderBg, C.subHeaderFg)));
  r++;
  (data.warranty_tracker||[]).forEach((row,i) => {
    const days = Number(row.days_remaining);
    const bg   = days<=30 ? C.redBg : days<=90 ? C.amberBg : (i%2===0?C.white:C.altRow);
    setCell(0,r,row.repair_no,        bodyCell(bg));
    setCell(1,r,row.clinic_label,     bodyCell(bg));
    setCell(2,r,row.patient_name,     bodyCell(bg));
    setCell(3,r,row.patient_mobile,   bodyCell(bg));
    setCell(4,r,row.ha_brand,         bodyCell(bg));
    setCell(5,r,row.ha_model||'',     bodyCell(bg));
    setCell(6,r,row.warranty_months,  numCell(bg));
    setCell(7,r,row.delivered_date,   bodyCell(bg));
    setCell(8,r,row.expiry_date,      bodyCell(bg));
    setCell(9,r,days,                 numCell(bg, days<=30?C.red:days<=90?C.amber:C.green));
    r++;
  });

  // ── Sheet metadata ───────────────────────────────────────────
  ws['!ref'] = XLSX.utils.encode_range({s:{c:0,r:0}, e:{c:9,r:r}});
  ws['!cols'] = [
    {wch:22},{wch:18},{wch:22},{wch:20},{wch:16},{wch:22},{wch:14},{wch:13},{wch:14},{wch:11}
  ];
  ws['!rows'] = [{hpt:28},{hpt:22}]; // title rows taller

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Service Report');
  XLSX.writeFile(wb, `SoundLife-Service-Report-${from}-to-${to}.xlsx`);
}

function ReportsTab({ user, csrf, onOpenTicket }) {
  const _d = new Date(); // local date — not UTC, avoids off-by-one in IST before 5:30 AM
  const today = `${_d.getFullYear()}-${String(_d.getMonth()+1).padStart(2,'0')}-${String(_d.getDate()).padStart(2,'0')}`;
  const [from, setFrom] = useState(today.slice(0,8)+'01');
  const [to, setTo]     = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [rowLoading, setRowLoading] = useState(null); // id of row being fetched
  const [subTab, setSubTab] = useState('log'); // 'log' | 'brand'

  const openTicket = (id) => {
    setRowLoading(id);
    api(`tickets.php?action=fetch_one&id=${id}`)
      .then(t => { if (t && t.id) onOpenTicket && onOpenTicket(t); })
      .catch(() => {})
      .finally(() => setRowLoading(null));
  };

  function load() {
    setLoading(true);
    const reqs = [api(`reports.php?from=${from}&to=${to}`)];
    if (user.role === 'owner') reqs.push(api(`tickets.php?action=audit_log&from=${from}&to=${to}`));
    Promise.all(reqs)
      .then(([d, al]) => { setData(d); if (al) setAuditLog(al); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }
  useEffect(()=>{ load(); },[]); // eslint-disable-line

  if (user.role === 'clinic') {
    return (
      <div className="fadeUp">
        <div style={{ marginBottom:24 }}>
          <div style={S.pageTitle}>🛡️ Warranty Expiry Tracker</div>
          <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>Warranties expiring within the next 12 months or expired in last 90 days</div>
        </div>
        <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
          {loading ? <div style={{ padding:32, textAlign:'center', color:'var(--muted)' }}>Loading…</div> :
          (data?.warranty_tracker||[]).length === 0 ? <div style={{ padding:32, textAlign:'center', color:'var(--muted)' }}>No active warranties found</div> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Repair No.','Patient','Brand / Model','Warranty','Delivered','Expiry','Days Left'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>{(data.warranty_tracker||[]).map((r,i)=>{
                  const d = +r.days_remaining;
                  const col = d < 0 ? '#6b7280' : d <= 30 ? '#dc2626' : d <= 90 ? '#d97706' : '#059669';
                  return (
                    <tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                      <td style={S.td}><strong style={{ color:'var(--accent)', fontSize:12 }}>{r.repair_no}</strong></td>
                      <td style={S.td}><div style={{ fontSize:13, fontWeight:600 }}>{r.patient_name}</div><code style={{ fontSize:11, color:'var(--muted)' }}>{r.patient_mobile}</code></td>
                      <td style={S.td}><div style={{ fontSize:13, fontWeight:600 }}>{r.ha_brand}</div>{r.ha_model&&<div style={{ fontSize:11, color:'var(--muted)' }}>{r.ha_model}</div>}</td>
                      <td style={S.td}><span style={{ ...S.pill('#2563eb'), fontSize:11 }}>{r.warranty_months}m</span></td>
                      <td style={S.td}><span style={{ fontSize:12 }}>{r.delivered_date}</span></td>
                      <td style={S.td}><span style={{ fontSize:12, fontWeight:600 }}>{r.expiry_date}</span></td>
                      <td style={S.td}><span style={{ ...S.pill(col), fontSize:11 }}>{d<0?`Exp ${Math.abs(d)}d ago`:`${d}d left`}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fadeUp">
      <div style={{ marginBottom:20, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={S.pageTitle}>Reports & Analytics</div>
          <div style={{ fontSize:13, color:'var(--muted)', marginTop:4 }}>Performance metrics across all clinics</div>
        </div>
        <div style={{ display:'flex', gap:6, background:'var(--bg3)', borderRadius:12, padding:4 }}>
          {[['log','📋 Repair Log'],['brand','🏷️ Brand Intelligence']].map(([id,label])=>(
            <button key={id} onClick={()=>setSubTab(id)} style={{ border:'none', borderRadius:9, padding:'7px 16px', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all .15s', background:subTab===id?'var(--accent)':'transparent', color:subTab===id?'#fff':'var(--muted)' }}>{label}</button>
          ))}
        </div>
      </div>

      <div style={{ ...S.card, display:'flex', gap:14, alignItems:'flex-end', marginBottom:24, flexWrap:'wrap' }}>
        <div><label style={S.label}>From</label><input style={S.input} type="date" value={from} onChange={e=>setFrom(e.target.value)} /></div>
        <div><label style={S.label}>To</label><input style={S.input} type="date" value={to} onChange={e=>setTo(e.target.value)} /></div>
        <button onClick={load} style={S.btnP}>Load Report</button>
        {data && <button onClick={()=>exportReportXLSX(data, from, to)} style={S.btnS}>⬇️ Export Excel</button>}
      </div>

      {loading && <LoadingState />}
      {!loading && data && subTab === 'brand' && (() => {
        const brands = data.brand_breakdown || [];
        if (!brands.length) return <EmptyState icon="🏷️" title="No brand data" sub="No repairs found for this date range" />;
        const fastest = [...brands].filter(b=>b.avg_days!=null).sort((a,b)=>a.avg_days-b.avg_days)[0];
        const mostFOC = [...brands].filter(b=>b.foc_pct!=null).sort((a,b)=>b.foc_pct-a.foc_pct)[0];
        const mostVol = brands[0];
        const brandColors = { Phonak:'#0057B8', Signia:'#E30613', Widex:'#00A651', Oticon:'#F47920', ReSound:'#662D91', Starkey:'#003087', Unitron:'#0072CE', Interton:'#555', Coselgi:'#c97a00' };
        return (
          <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
            {/* Top summary strip */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:14 }}>
              {[
                { icon:'🏆', label:'Most Repaired', value:mostVol?.ha_brand||'—', sub:`${mostVol?.total||0} repairs`, col:'#7c3aed' },
                { icon:'⚡', label:'Fastest TAT', value:fastest?.ha_brand||'—', sub:`${fastest?.avg_days||'—'}d avg`, col:'#059669' },
                { icon:'🎁', label:'Most FOC-Friendly', value:mostFOC?.ha_brand||'—', sub:`${mostFOC?.foc_pct||0}% FOC`, col:'#2563eb' },
                { icon:'🔧', label:'Total Brands', value:brands.length, sub:'brands tracked', col:'#d97706' },
              ].map(s=>(
                <div key={s.label} style={{ ...S.card, display:'flex', alignItems:'center', gap:14, padding:'16px 20px', borderLeft:`4px solid ${s.col}` }}>
                  <div style={{ width:42, height:42, borderRadius:12, background:`${s.col}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.6 }}>{s.label}</div>
                    <div style={{ fontSize:20, fontWeight:900, color:s.col, lineHeight:1.1 }}>{s.value}</div>
                    <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Brand cards grid */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:16 }}>
              {brands.map((b,i)=>{
                const col = brandColors[b.ha_brand] || '#7c3aed';
                const tatCol = !b.avg_days ? '#6b7280' : b.avg_days > 20 ? '#dc2626' : b.avg_days > 12 ? '#d97706' : '#059669';
                const focPct = b.foc_pct != null ? +b.foc_pct : null;
                const focCol = focPct == null ? '#6b7280' : focPct >= 70 ? '#059669' : focPct >= 40 ? '#d97706' : '#dc2626';
                const reCol  = +b.re_rate > 20 ? '#dc2626' : +b.re_rate > 10 ? '#d97706' : '#059669';
                return (
                  <div key={b.ha_brand} style={{ ...S.card, padding:0, overflow:'hidden', border:`1.5px solid ${col}22` }}>
                    {/* Card header */}
                    <div style={{ background:`linear-gradient(135deg,${col}18,${col}08)`, padding:'16px 20px', borderBottom:`1.5px solid ${col}20`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:38, height:38, borderRadius:10, background:col, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, color:'#fff', fontWeight:900 }}>{b.ha_brand?.slice(0,2).toUpperCase()}</div>
                        <div>
                          <div style={{ fontSize:16, fontWeight:900, color:'var(--text1)' }}>{b.ha_brand}</div>
                          <div style={{ fontSize:11, color:'var(--muted)' }}>{b.total} repair{b.total!==1?'s':''} in period</div>
                        </div>
                      </div>
                      <span style={{ fontSize:24, fontWeight:900, color:`${col}60` }}>#{i+1}</span>
                    </div>
                    {/* Metrics grid */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
                      {[
                        { label:'Avg TAT', value: b.avg_days != null ? `${b.avg_days}d` : '—', sub:'repair turnaround', col:tatCol },
                        { label:'FOC Rate', value: focPct != null ? `${focPct}%` : '—', sub:`${b.foc_count||0} of ${(+b.foc_count||0)+(+b.estimate_count||0)} reported`, col:focCol },
                        { label:'Re-Repair', value:`${b.re_rate||0}%`, sub:`${b.re_repairs||0} re-repairs`, col:reCol },
                        { label:'Avg Estimate', value: b.avg_estimate ? `₹${Number(b.avg_estimate).toLocaleString('en-IN')}` : '—', sub:`${b.estimate_count||0} paid repairs`, col:'#7c3aed' },
                      ].map((m,mi)=>(
                        <div key={m.label} style={{ padding:'14px 18px', borderRight:mi%2===0?'1px solid var(--border)':'none', borderBottom:mi<2?'1px solid var(--border)':'none' }}>
                          <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, textTransform:'uppercase', letterSpacing:.6, marginBottom:4 }}>{m.label}</div>
                          <div style={{ fontSize:22, fontWeight:900, color:m.col, lineHeight:1 }}>{m.value}</div>
                          <div style={{ fontSize:10, color:'var(--muted)', marginTop:4 }}>{m.sub}</div>
                        </div>
                      ))}
                    </div>
                    {/* FOC bar */}
                    {focPct != null && (
                      <div style={{ padding:'10px 18px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, width:28, flexShrink:0 }}>FOC</div>
                        <div style={{ flex:1, height:6, borderRadius:3, background:'var(--border)', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${focPct}%`, background:`linear-gradient(90deg,${focCol},${focCol}bb)`, borderRadius:3, transition:'width .6s ease' }} />
                        </div>
                        <div style={{ fontSize:10, color:'var(--muted)', fontWeight:700, width:32, textAlign:'right' }}>{focPct}%</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Comparison table */}
            <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
              <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>📊 Side-by-Side Comparison</div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Brand','Total','Avg TAT','FOC%','Avg Estimate','Re-Repair%','Non-Warranty'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{brands.map((r,i)=>{
                    const tatCol = !r.avg_days?'var(--muted)':r.avg_days>20?'var(--danger)':r.avg_days>12?'var(--amber)':'var(--green)';
                    const focPctVal = r.foc_pct != null ? +r.foc_pct : null;
                    return (
                      <tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                        <td style={S.td}><strong style={{ color:brandColors[r.ha_brand]||'var(--accent)' }}>{r.ha_brand}</strong></td>
                        <td style={S.td}><strong>{r.total}</strong></td>
                        <td style={S.td}><span style={{ fontWeight:700, color:tatCol }}>{r.avg_days!=null?`${r.avg_days}d`:'—'}</span></td>
                        <td style={S.td}>{focPctVal!=null?<span style={{ ...S.pill(focPctVal>=70?'#059669':focPctVal>=40?'#d97706':'#dc2626'), fontSize:11 }}>{focPctVal}%</span>:'—'}</td>
                        <td style={S.td}>{r.avg_estimate?`₹${Number(r.avg_estimate).toLocaleString('en-IN')}`:'—'}</td>
                        <td style={S.td}><span style={S.pill(+r.re_rate>20?'#dc2626':+r.re_rate>10?'#d97706':'#059669')}>{r.re_rate||0}%</span></td>
                        <td style={S.td}>{r.non_warranty||0}</td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}
      {!loading && data && subTab === 'log' && (
        <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:16 }}>
            <StatCard icon="🔧" value={data.summary.total_repairs||0} label="Total Repairs"  color="#7c3aed" />
            <StatCard icon="📋" value={data.summary.active_repairs||0} label="Still Active"  color="#2563eb" />
            <StatCard icon="💰" value={data.summary.non_warranty||0}   label="Non-Warranty"  color="#d97706" />
            <StatCard icon="🔁" value={data.summary.re_repairs||0}     label="Re-Repairs"    color="#dc2626" />
          </div>

          <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>📅 Repairs by Clinic & Month</div>
            {(data.clinic_monthly||[]).length===0 ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No data for this period</div> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Clinic','Month','Count'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{(data.clinic_monthly||[]).map((r,i)=>(<tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}><td style={S.td}>{r.clinic_label}</td><td style={S.td}>{r.month}</td><td style={S.td}><strong>{r.cnt}</strong></td></tr>))}</tbody>
                </table>
              </div>
            )}
          </div>

          {[
            { title:'⏱️ Average Turnaround Per Clinic', rows:data.turnaround, cols:['Clinic','Avg Days','Delivered'], render:(r,i)=>(<tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}><td style={S.td}>{r.clinic_label}</td><td style={S.td}><strong style={{ color:r.avg_days>14?'var(--danger)':r.avg_days>7?'var(--amber)':'var(--green)' }}>{r.avg_days}d</strong></td><td style={S.td}>{r.total}</td></tr>) },
            { title:'🔁 Re-Repair Rate by Brand', rows:data.re_repair, cols:['Brand','Total','Re-Repairs','Rate'], render:(r,i)=>(<tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}><td style={S.td}><strong>{r.ha_brand}</strong></td><td style={S.td}>{r.total}</td><td style={S.td}>{r.re_repairs}</td><td style={S.td}><span style={S.pill(r.re_rate>30?'#dc2626':r.re_rate>15?'#d97706':'#059669')}>{r.re_rate}%</span></td></tr>) },
            { title:'⚠️ Delivery Delay Leaderboard', rows:data.delay_leaders, cols:['Clinic','Avg Delay','Delivered','Red Alerts'], render:(r,i)=>(<tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}><td style={S.td}>{r.clinic_label}</td><td style={S.td}><strong style={{ color:r.avg_delay>3?'var(--danger)':r.avg_delay>1?'var(--amber)':'var(--green)' }}>{r.avg_delay}d</strong></td><td style={S.td}>{r.delivered}</td><td style={S.td}>{r.red_alerts>0?<span style={S.pill('#dc2626')}>{r.red_alerts}</span>:'—'}</td></tr>) },
            { title:'🕰️ Pending Repairs Older Than 10 Days', rows:data.stale, cols:['Repair No.','Clinic','Patient','Brand','Status','Warranty','Days Open'], render:(r,i)=>(<tr key={i} onClick={()=>openTicket(r.id)} style={{ background:rowLoading===r.id?'var(--accentbg)':i%2===0?'var(--card)':'var(--bg)', cursor:'pointer', opacity:rowLoading&&rowLoading!==r.id?0.5:1 }} className="row-hover"><td style={S.td}><strong style={{ color:'var(--accent)' }}>{rowLoading===r.id?'⏳':''}{r.repair_no}</strong></td><td style={S.td}>{r.clinic_label}</td><td style={S.td}>{r.patient_name}</td><td style={S.td}><span style={{ fontSize:11, color:'var(--muted)', fontWeight:600 }}>{r.ha_brand||'—'}</span></td><td style={S.td}><span style={{ ...S.pill(STATUS_COLOR[r.status]||'#666'), fontSize:11 }}>{r.status}</span></td><td style={S.td}><span style={{ fontSize:11, fontWeight:700, color:r.warranty_type==='Under Warranty'?'#059669':'#d97706' }}>{r.warranty_type==='Under Warranty'?'W':'Non-W'}</span></td><td style={S.td}><strong style={{ color:r.days_open>20?'var(--danger)':'var(--amber)' }}>{r.days_open}d</strong></td></tr>) },
          ].map(({ title, rows, cols, render }) => (
            <div key={title} style={{ ...S.card, padding:0, overflow:'hidden' }}>
              <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>{title}</div>
              {rows.length===0 ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No data for this period</div> : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{cols.map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>{rows.map(render)}</tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>🏭 Company-wise Breakdown</div>
            {(data.brand_breakdown||[]).length===0 ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No data for this period</div> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Brand','Total','Re-Repairs','Re-Rate','Non-Warranty','FOC','Estimate','Avg Days'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{(data.brand_breakdown||[]).map((r,i)=>(
                    <tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                      <td style={S.td}><strong>{r.ha_brand}</strong></td>
                      <td style={S.td}><strong>{r.total}</strong></td>
                      <td style={S.td}>{r.re_repairs}</td>
                      <td style={S.td}><span style={S.pill(r.re_rate>30?'#dc2626':r.re_rate>15?'#d97706':'#059669')}>{r.re_rate}%</span></td>
                      <td style={S.td}>{r.non_warranty}</td>
                      <td style={S.td}>{r.foc_count||0}</td>
                      <td style={S.td}>{r.estimate_count||0}</td>
                      <td style={S.td}>{r.avg_days!=null?<span style={{ color:r.avg_days>14?'var(--danger)':r.avg_days>7?'var(--amber)':'var(--green)' }}>{r.avg_days}d</span>:'—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>🛡️ Warranty Expiry Tracker</div>
            {(data.warranty_tracker||[]).length===0 ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No warranties found (expiring within ±90–365 days)</div> : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Repair No.','Clinic','Patient','Mobile','Brand / Model','Warranty','Delivered','Expiry','Days Left'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>{(data.warranty_tracker||[]).map((r,i)=>{
                    const d = +r.days_remaining;
                    const col = d < 0 ? '#6b7280' : d <= 30 ? '#dc2626' : d <= 90 ? '#d97706' : '#059669';
                    return (
                      <tr key={i} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}>
                        <td style={S.td}><strong style={{ color:'var(--accent)', fontSize:12 }}>{r.repair_no}</strong></td>
                        <td style={S.td}>{r.clinic_label}</td>
                        <td style={S.td}>{r.patient_name}</td>
                        <td style={S.td}><code style={{ fontSize:12 }}>{r.patient_mobile}</code></td>
                        <td style={S.td}><div style={{ fontSize:13, fontWeight:600 }}>{r.ha_brand}</div>{r.ha_model&&<div style={{ fontSize:11, color:'var(--muted)' }}>{r.ha_model}</div>}</td>
                        <td style={S.td}><span style={{ ...S.pill('#2563eb'), fontSize:11 }}>{r.warranty_months}m</span></td>
                        <td style={S.td}><span style={{ fontSize:12 }}>{r.delivered_date}</span></td>
                        <td style={S.td}><span style={{ fontSize:12, fontWeight:600 }}>{r.expiry_date}</span></td>
                        <td style={S.td}><span style={{ ...S.pill(col), fontSize:11 }}>{d<0?`Exp ${Math.abs(d)}d ago`:`${d}d`}</span></td>
                      </tr>
                    );
                  })}</tbody>
                </table>
              </div>
            )}
          </div>

          {user.role === 'owner' && (
            <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
              <div style={{ padding:'18px 24px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:15, color:'var(--text1)' }}>📋 Status Audit Trail</div>
              {auditLog.length===0 ? <div style={{ padding:24, textAlign:'center', color:'var(--muted)' }}>No audit entries for this period</div> : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr style={{ background:'linear-gradient(90deg,#1e1b4b,#7c3aed)' }}>{['Time','Repair No.','Clinic','From Status','To Status','Changed By','Role'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>{auditLog.map((r,i)=>(<tr key={r.id} style={{ background:i%2===0?'var(--card)':'var(--bg)' }}><td style={S.td}><span style={{ fontSize:12, fontFamily:'monospace' }}>{r.created_at.slice(0,16)}</span></td><td style={S.td}><strong style={{ color:'var(--accent)', fontSize:12 }}>{r.repair_no}</strong></td><td style={S.td}>{r.clinic_label}</td><td style={S.td}><span style={{ ...S.pill(STATUS_COLOR[r.from_status]||'#666'), fontSize:10 }}>{r.from_status}</span></td><td style={S.td}><span style={{ ...S.pill(STATUS_COLOR[r.to_status]||'#666'), fontSize:10 }}>{r.to_status}</span></td><td style={S.td}>{r.changed_by}</td><td style={S.td}><span style={{ fontSize:11, color:'var(--muted)', fontWeight:600 }}>{r.changed_role}</span></td></tr>))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
function LoadingState() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, flexDirection:'column', gap:14 }}>
      <div style={{ width:36, height:36, border:'3px solid var(--border)', borderTop:'3px solid var(--accent)', borderRadius:'50%', animation:'spin .8s linear infinite' }} />
      <div style={{ fontSize:13, color:'var(--muted)' }}>Loading…</div>
    </div>
  );
}
function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ ...S.card, textAlign:'center', padding:48 }}>
      <div style={{ fontSize:40, marginBottom:14 }}>{icon}</div>
      <div style={{ fontSize:17, fontWeight:800, color:'var(--text1)', marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:13, color:'var(--muted)' }}>{sub}</div>
    </div>
  );
}
